import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Settings, Mail, Activity, LogOut, Bell, Save, Shield, Globe,
  Clock, KeyRound, Eye, EyeOff, CheckCircle, Tags, ChevronRight,
  Plus, Pencil, X, Check, Trash2, Users, AlertTriangle, RefreshCw,
  Monitor, MapPin, Wifi, WifiOff, BarChart2, ExternalLink, Lock, List,
  ChevronUp, ChevronDown, ShoppingCart, Package, Percent, Palette, UploadCloud, Image as ImageIcon,
  Database, Search, ChevronLeft, Table2, SlidersHorizontal,
  Globe2, BookOpen, HelpCircle, Wrench
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import AdminSidebar from '../components/AdminSidebar';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from '../components/ui/sonner';
import { apiClient } from '../lib/apiClient';
import UnfcccEmailList from './UnfcccEmailList';
import EmailLibrarySection from '../components/EmailLibrarySection';
import DangerZoneSection from '../components/DangerZoneSection';
import OrderMaintenanceSection from '../components/OrderMaintenanceSection';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getEmailFromToken = () => {
  try { const p = JSON.parse(atob(localStorage.getItem('cop31_ultra_token').split('.')[1])); return p.sub; } catch { return null; }
};

// ─── Rich Text Editor Toolbar Button ────────────────
const ToolBtn = ({ children, className = '', onMouseDown, title }) => (
  <button
    type="button"
    title={title}
    onMouseDown={onMouseDown}
    className={`px-2 py-1 rounded text-sm hover:bg-white hover:shadow-sm transition-all text-gray-700 select-none ${className}`}
  >
    {children}
  </button>
);

// ─── Rich Text Editor ────────────────────────────────
const RichTextEditor = ({ value, onChange, insertRef, tr }) => {
  const editorRef = useRef(null);

  // Initialize editor content on mount only
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || '';
    }
  }, []); // only on mount

  // Expose insert-at-cursor function via ref
  useEffect(() => {
    if (insertRef) {
      insertRef.current = (text) => {
        editorRef.current?.focus();
        document.execCommand('insertText', false, text);
        if (editorRef.current) onChange(editorRef.current.innerHTML);
      };
    }
  }, [onChange, insertRef]);

  const execCmd = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    setTimeout(() => { if (editorRef.current) onChange(editorRef.current.innerHTML); }, 0);
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-gray-200">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-100 bg-gray-50 flex-wrap">
        <ToolBtn onMouseDown={e => { e.preventDefault(); execCmd('bold'); }} title="Kalın (Ctrl+B)">
          <span className="font-bold text-gray-800">B</span>
        </ToolBtn>
        <ToolBtn onMouseDown={e => { e.preventDefault(); execCmd('italic'); }} title="İtalik (Ctrl+I)">
          <span className="italic text-gray-700">I</span>
        </ToolBtn>
        <ToolBtn onMouseDown={e => { e.preventDefault(); execCmd('underline'); }} title="Altı Çizili (Ctrl+U)">
          <span className="underline text-gray-700">U</span>
        </ToolBtn>
        <div className="w-px h-4 bg-gray-300 mx-1" />
        <select
          defaultValue=""
          onChange={e => { execCmd('fontSize', e.target.value); e.target.value = ''; }}
          className="text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white cursor-pointer focus:outline-none"
          title="Yazı boyutu"
        >
          <option value="" disabled>{tr ? 'Boyut' : 'Size'}</option>
          <option value="2">{tr ? 'Küçük' : 'Small'}</option>
          <option value="3">{tr ? 'Normal' : 'Normal'}</option>
          <option value="4">{tr ? 'Büyük' : 'Large'}</option>
          <option value="5">{tr ? 'X-Büyük' : 'X-Large'}</option>
        </select>
        <select
          defaultValue=""
          onChange={e => { execCmd('fontName', e.target.value); e.target.value = ''; }}
          className="text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white cursor-pointer focus:outline-none ml-1"
          title="Yazı tipi"
        >
          <option value="" disabled>{tr ? 'Yazı Tipi' : 'Font'}</option>
          <option value="Arial">Arial</option>
          <option value="Georgia">Georgia</option>
          <option value="Times New Roman">Times New Roman</option>
          <option value="Helvetica Neue">Helvetica</option>
        </select>
        <div className="w-px h-4 bg-gray-300 mx-1" />
        <ToolBtn onMouseDown={e => { e.preventDefault(); execCmd('insertUnorderedList'); }} title="Madde Listesi">
          <List className="h-3.5 w-3.5" />
        </ToolBtn>
        <ToolBtn onMouseDown={e => { e.preventDefault(); execCmd('removeFormat'); }} title={tr ? 'Biçimi Sil' : 'Clear Format'} className="text-gray-400">
          <span className="text-xs">{tr ? 'Temizle' : 'Clear'}</span>
        </ToolBtn>
      </div>
      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => { if (editorRef.current) onChange(editorRef.current.innerHTML); }}
        className="p-3 min-h-[180px] max-h-[240px] overflow-y-auto text-sm focus:outline-none"
        style={{ fontFamily: 'Arial, sans-serif', lineHeight: '1.75' }}
      />
    </div>
  );
};

// ─── Zone helpers ────────────────────────────────────
const ZONE_BADGE = {
  blue_zone: 'bg-blue-100 text-blue-700',
  green_zone: 'bg-purple-100 text-purple-700',
  supplier: 'bg-amber-100 text-amber-700',
};
const zoneLabel = (zone, tr) => ({
  blue_zone: 'Blue Zone',
  green_zone: 'Green Zone',
  supplier: tr ? 'Tedarikçi' : 'Supplier',
}[zone] || zone);

// ─── Email Template Modal ───────────────────────────
const TemplateModal = ({ template, onClose, onSave, tr }) => {
  const [form, setForm] = useState(template || {
    zone: 'blue_zone',
    name_tr: '', name_en: '', subject_tr: '', subject_en: '',
    body_tr: '', body_en: '', status: 'draft', variables: ['{{name}}', '{{email}}']
  });
  const [lang, setLang] = useState('tr');
  const [saving, setSaving] = useState(false);
  const isNew = !template?.id;
  const insertRef = useRef(null);

  const insertVar = (v) => {
    if (insertRef.current) insertRef.current(v);
  };

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(form); onClose(); } catch {} finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl mx-4 max-h-[94vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{isNew ? (tr ? 'Yeni Şablon' : 'New Template') : (tr ? 'Şablonu Düzenle' : 'Edit Template')}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{tr ? 'TR ve EN içerikleri ayrı düzenleyin' : 'Edit TR and EN contents separately'}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
        </div>
        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Name fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>İsim (TR)</Label>
              <Input value={form.name_tr} onChange={e => setForm(f => ({...f, name_tr: e.target.value}))} className="rounded-xl" placeholder="Örn: Onay Maili" />
            </div>
            <div className="space-y-1">
              <Label>Name (EN)</Label>
              <Input value={form.name_en} onChange={e => setForm(f => ({...f, name_en: e.target.value}))} className="rounded-xl" placeholder="e.g. Approval Email" />
            </div>
          </div>
          {/* Zone + Status row */}
          <div className="flex gap-3 items-end">
            <div className="space-y-1 flex-1">
              <Label>Zone</Label>
              <select
                value={form.zone || 'blue_zone'}
                onChange={e => setForm(f => ({...f, zone: e.target.value}))}
                className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white"
              >
                <option value="blue_zone">Blue Zone</option>
                <option value="green_zone">Green Zone</option>
                <option value="supplier">{tr ? 'Tedarikçi' : 'Supplier'}</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>{tr ? 'Durum' : 'Status'}</Label>
              <select
                value={form.status}
                onChange={e => setForm(f => ({...f, status: e.target.value}))}
                className="text-sm border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white"
              >
                <option value="active">{tr ? 'Aktif' : 'Active'}</option>
                <option value="draft">{tr ? 'Taslak' : 'Draft'}</option>
              </select>
            </div>
          </div>
          {/* Lang tab toggle */}
          <div className="flex gap-2">
            {['tr', 'en'].map(l => (
              <button key={l} onClick={() => setLang(l)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${lang === l ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {l === 'tr' ? '🇹🇷 Türkçe' : '🇬🇧 English'}
              </button>
            ))}
          </div>
          {/* Subject */}
          <div className="space-y-1">
            <Label>{tr ? `Konu (${lang.toUpperCase()})` : `Subject (${lang.toUpperCase()})`}</Label>
            <Input
              value={lang === 'tr' ? form.subject_tr : form.subject_en}
              onChange={e => setForm(f => ({...f, [lang === 'tr' ? 'subject_tr' : 'subject_en']: e.target.value}))}
              className="rounded-xl" placeholder={lang === 'tr' ? 'E-posta konusu...' : 'Email subject...'}
            />
          </div>
          {/* Rich Text Body — key forces remount on lang change */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5">
              {tr ? `İçerik (${lang.toUpperCase()})` : `Body (${lang.toUpperCase()})`}
              <span className="text-xs text-gray-400 font-normal">— HTML</span>
            </Label>
            <RichTextEditor
              key={`editor-${lang}`}
              value={lang === 'tr' ? form.body_tr : form.body_en}
              onChange={v => setForm(f => ({...f, [lang === 'tr' ? 'body_tr' : 'body_en']: v}))}
              insertRef={insertRef}
              tr={tr}
            />
          </div>
          {/* Variables */}
          <div>
            <Label className="text-xs text-gray-400 mb-1.5 block">{tr ? 'Değişkenler (tıkla ekle)' : 'Variables (click to insert)'}</Label>
            <div className="flex flex-wrap gap-1.5">
              {['{{name}}', '{{email}}', '{{login_url}}', '{{company}}', '{{country}}', '{{event_date}}', '{{admin_email}}'].map(v => (
                <button key={v} onClick={() => insertVar(v)} className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-mono hover:bg-blue-100 transition-colors">{v}</button>
              ))}
              <button
                onClick={() => insertVar('{{sector}}')}
                title={tr ? 'Tedarikçi kategorisi — onay anında otomatik dolar (örn: Stand Constructor, Catering)' : 'Supplier category — auto-filled at approval time (e.g. Stand Constructor, Catering)'}
                className="px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-xs font-mono hover:bg-amber-100 transition-colors flex items-center gap-1"
              >
                {'{{sector}}'}
                <span className="text-amber-400 font-bold text-xs leading-none">★</span>
              </button>
              <button
                onClick={() => insertVar('{{sub_sector}}')}
                title={tr ? 'Tedarikçi alt kategorisi — onay anında otomatik dolar (örn: Konteyner Lojistik)' : 'Supplier sub-category — auto-filled at approval time (e.g. Container Logistics)'}
                className="px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-xs font-mono hover:bg-amber-100 transition-colors flex items-center gap-1"
              >
                {'{{sub_sector}}'}
                <span className="text-amber-400 font-bold text-xs leading-none">★</span>
              </button>
            </div>
            <p className="text-xs text-amber-600 mt-1.5">
              <span className="font-bold">★</span> {tr ? '"{{sector}}" ve "{{sub_sector}}" tedarikçi onaylandığında kategorisine göre otomatik değişir.' : '"{{sector}}" and "{{sub_sector}}" auto-fill with the supplier\'s category when approved.'}
            </p>
          </div>
        </div>
        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-100">
          <Button variant="outline" className="rounded-full" onClick={onClose}>{tr ? 'İptal' : 'Cancel'}</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-gray-900 hover:bg-gray-800 text-white rounded-full gap-2">
            {saving ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Save className="h-4 w-4" />}
            {tr ? 'Kaydet' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Settings Component ─────────────────────────
const UltraAdminSettings = () => {
  const navigate = useNavigate();
  const { section = 'general', subsection } = useParams();
  const { language } = useLanguage();
  const tr = language === 'tr';
  const isMainUser = true; // Ultra admin her zaman şifre değiştirebilir

  // Bilingual category display helpers — picks the value for the active
  // language with graceful fallback to the other language.
  const pickName = (item) => {
    if (!item) return '';
    const ntr = (item.name_tr || '').trim();
    const nen = (item.name_en || '').trim();
    const lg  = (item.name    || '').trim();
    return tr ? (ntr || nen || lg) : (nen || ntr || lg);
  };
  const isMissingActiveLang = (item) => {
    if (!item) return false;
    const target = tr ? (item.name_tr || '').trim() : (item.name_en || '').trim();
    return !target;
  };
  const MissingLangBadge = ({ item }) => (
    isMissingActiveLang(item) ? (
      <span className="text-[10px] font-medium bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded ml-1.5">
        {tr ? 'TR yok' : 'EN missing'}
      </span>
    ) : null
  );

  // Primary super admin (is_main_user) gate — controls whether the
  // "Super Admins" management shortcut appears in the Settings menu.
  // Mirrors the previous sidebar gate so regular ultra/super admins
  // never see the entry.
  const [isPrimary, setIsPrimary] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem('cop31_ultra_token');
    if (!token) return;
    (async () => {
      try {
        const r = await apiClient.get('/auth/me');
        setIsPrimary(!!r.data?.is_main_user);
      } catch { setIsPrimary(false); }
    })();
  }, []);

  // URL'den aktif bölüm türetiliyor — useState yerine URL param kullanılıyor
  const activeSection = section;
  const catTab = activeSection === 'categories' ? (subsection || 'user') : 'user';

  // Bölüm navigasyonu
  const goSection = (key) => navigate(`/ultra-admin/settings/${key}`);
  const goCatTab  = (tab) => navigate(`/ultra-admin/settings/categories/${tab}`);

  // /ultra-admin/settings → /ultra-admin/settings/general redirect
  useEffect(() => {
    if (!section) navigate('/ultra-admin/settings/general', { replace: true });
  }, [section]);

  // General
  const [saved, setSaved] = useState(false);
  const [settings, setSettings] = useState({
    site_name: 'COP 31 Antalya Platform',
    admin_email: 'admin@cop31.org',
    max_registrations: '500',
    event_start: '2026-11-09',
    event_end: '2026-11-20',
  });

  // Security Center
  const [upStep, setUpStep] = useState('idle');
  const [upCode, setUpCode] = useState(''); const [upNewPass, setUpNewPass] = useState(''); const [upConfirmPass, setUpConfirmPass] = useState('');
  const [upShowPass, setUpShowPass] = useState(false); const [upError, setUpError] = useState(''); const [upLoading, setUpLoading] = useState(false);
  const [loginPwForm, setLoginPwForm] = useState({ current: '', newp: '', confirm: '' });
  const [loginPwShowConfirm, setLoginPwShowConfirm] = useState(false);
  const [loginPwError, setLoginPwError] = useState(''); const [loginPwSuccess, setLoginPwSuccess] = useState(false); const [loginPwLoading, setLoginPwLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false); const [showNew, setShowNew] = useState(false);
  // Reset Lock (Production Safety)
  const [allowResetTestData, setAllowResetTestData] = useState(false);
  const [resetLockLoading, setResetLockLoading] = useState(false);

  // Categories tab artık URL'den türetiliyor (aşağıdaki catTab sabit değerini kullan)
  const [categories, setCategories] = useState([]); const [catLoading, setCatLoading] = useState(false);
  const [catForm, setCatForm] = useState({ name_tr: '', name_en: '' }); const [editingCat, setEditingCat] = useState(null); const [catError, setCatError] = useState('');
  // Supplier Categories
  const [supplierCats, setSupplierCats] = useState([]); const [supplierCatLoading, setSupplierCatLoading] = useState(false);
  const [supplierCatForm, setSupplierCatForm] = useState({ name_tr: '', name_en: '' }); const [editingSupplierCat, setEditingSupplierCat] = useState(null); const [supplierCatError, setSupplierCatError] = useState('');
  const [contractorCats, setContractorCats] = useState([]); const [contractorCatForm, setContractorCatForm] = useState({ name_tr: '', name_en: '' }); const [editingContractorCat, setEditingContractorCat] = useState(null); const [contractorCatError, setContractorCatError] = useState('');
  // Subcategories
  const [expandedCats, setExpandedCats] = useState({});
  const [newSubForms, setNewSubForms] = useState({});       // { [catId]: '' }
  const [editingSub, setEditingSub] = useState(null);       // { id, name, parent_id }
  const [subErrors, setSubErrors] = useState({});           // { [catId]: errorMsg }
  // Product templates per sub-category
  const [expandedSubs, setExpandedSubs] = useState({});    // { [subId]: true/false }
  const [newTplForms, setNewTplForms] = useState({});      // { [subId]: '' }
  const [tplErrors, setTplErrors] = useState({});          // { [subId]: errorMsg }
  // Product Profiles
  const [productProfiles, setProductProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [newProfileForm, setNewProfileForm] = useState({ name: '', formula: '', calculation_type: 'flat', pricing_mode: '' });
  const [profileError, setProfileError] = useState('');

  // Email Templates
  const [templates, setTemplates] = useState([]); const [tmplLoading, setTmplLoading] = useState(false);
  const [editTmpl, setEditTmpl] = useState(null); const [showTmplModal, setShowTmplModal] = useState(false);
  const [isNewTmpl, setIsNewTmpl] = useState(false);

  // Visitor Stats
  const [visitorStats, setVisitorStats] = useState(null); const [visitorLoading, setVisitorLoading] = useState(false);
  const [statsDays, setStatsDays] = useState(7);

  // Registration Settings
  const [regSettings, setRegSettings] = useState(null); const [regLoading, setRegLoading] = useState(false);
  const [regSaved, setRegSaved] = useState(false);

  // Units
  const [units, setUnits] = useState([]); const [unitLoading, setUnitLoading] = useState(false);
  const [unitForm, setUnitForm] = useState({ name: '', code: '' }); const [editingUnit, setEditingUnit] = useState(null); const [unitError, setUnitError] = useState('');

  // Shopping Settings
  const [shoppingSettings, setShoppingSettings] = useState({
    cart_reservation_timeout_hours: 24,
    cart_expiry_warning_hours_before: 1,
    stock_low_threshold_percent: 50,
  });
  const [shoppingLoading, setShoppingLoading] = useState(false);
  const [shoppingSaved, setShoppingSaved] = useState(false);
  const [shoppingError, setShoppingError] = useState('');

  // Phase 86N — Stand Change Request Quota Settings
  const [scrSettings, setScrSettings] = useState({ max_change_requests_per_tenant: 1 });
  const [scrLoading, setScrLoading] = useState(false);
  const [scrSaved, setScrSaved] = useState(false);
  const [scrError, setScrError] = useState('');

  // Session Settings
  const [sessionSettings, setSessionSettings] = useState({
    blue_zone:   { timeout_minutes: 480, enabled: true },
    green_zone:  { timeout_minutes: 480, enabled: true },
    supplier:    { timeout_minutes: 480, enabled: true },
    ultra_admin: { timeout_minutes: 120, enabled: true },
  });
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionSaved, setSessionSaved]     = useState(false);
  const [sessionError, setSessionError]     = useState('');


  // Patterns & Colors
  const [patternGroups, setPatternGroups] = useState([]);
  const [patternLoading, setPatternLoading] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [patternGroupError, setPatternGroupError] = useState('');
  const [editingGroup, setEditingGroup] = useState(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  // 12 fixed slots: null = empty, or {file, preview, name, status, error}
  const [patternSlots, setPatternSlots] = useState(Array(12).fill(null));
  const [isDraggingSlot, setIsDraggingSlot] = useState(null); // index being dragged over
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const patternFileInputRef = useRef(null);
  const patternBrowseRef = useRef(null);
  const [newItemError, setNewItemError] = useState('');
  const [savingItem, setSavingItem] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // URL Scraper state
  const [showScrapeModal, setShowScrapeModal] = useState(false);
  const [scrapeUrls, setScrapeUrls] = useState(['']); // multi-URL list
  const [scrapeUrl, setScrapeUrl] = useState(''); // legacy single (kept for compat)
  const [scrapeLoading, setScrapeLoading] = useState(false);
  const [scrapeResults, setScrapeResults] = useState(null);
  const [scrapeSelected, setScrapeSelected] = useState(new Set());
  const [scrapeGroupName, setScrapeGroupName] = useState('');
  const [scrapeImporting, setScrapeImporting] = useState(false);
  const [scrapeError, setScrapeError] = useState('');
  const [scrapeSuccess, setScrapeSuccess] = useState('');
  const [scrapeProgress, setScrapeProgress] = useState({ done: 0, total: 0 });

  const handleScrapeUrl = async () => {
    // scrapeUrls (multi) veya scrapeUrl (single/legacy) hangisi doluysa onu kullan
    let validUrls = scrapeUrls.filter(u => u.trim());
    if (validUrls.length === 0 && scrapeUrl.trim()) {
      validUrls = [scrapeUrl.trim()];
    }
    if (!validUrls.length) return;
    setScrapeLoading(true);
    setScrapeError('');
    setScrapeResults(null);
    setScrapeSelected(new Set());
    setScrapeProgress({ done: 0, total: validUrls.length });

    const allImages = [];
    const seen = new Set();

    for (let i = 0; i < validUrls.length; i++) {
      setScrapeProgress({ done: i, total: validUrls.length });
      try {
        const r = await apiClient.post(`/ultra-admin/patterns/scrape-url`, { url: validUrls[i] });
        for (const img of (r.data.images || [])) {
          if (!seen.has(img.url)) {
            seen.add(img.url);
            allImages.push(img);
          }
        }
      } catch (e) {
        // devam et, diğer URL'lere geç
      }
    }

    setScrapeProgress({ done: validUrls.length, total: validUrls.length });
    if (allImages.length === 0) {
      setScrapeError(tr ? 'Hiçbir URL\'den uygun görsel bulunamadı.' : 'No suitable images found from any URL.');
    } else {
      setScrapeResults({ images: allImages, count: allImages.length });
    }
    setScrapeLoading(false);
  };

  const handleScrapeImport = async () => {
    if (!scrapeGroupName.trim()) { setScrapeError(tr ? 'Grup adı girin.' : 'Enter group name.'); return; }
    if (scrapeSelected.size === 0) { setScrapeError(tr ? 'En az bir görsel seçin.' : 'Select at least one image.'); return; }
    setScrapeImporting(true);
    setScrapeError('');
    try {
      const selectedImages = scrapeResults.images.filter(img => scrapeSelected.has(img.url));
      const r = await apiClient.post(`/ultra-admin/patterns/import-scraped`, {
        group_name: scrapeGroupName,
        images: selectedImages
      });
      setScrapeSuccess(tr ? `${r.data.imported} görsel "${r.data.group_name}" grubuna eklendi!` : `${r.data.imported} images added to "${r.data.group_name}"!`);
      await fetchPatternGroups();
      setSelectedGroupId(r.data.group_id);
      setTimeout(() => { setShowScrapeModal(false); setScrapeResults(null); setScrapeSelected(new Set()); setScrapeGroupName(''); setScrapeSuccess(''); setScrapeUrls(['']); }, 2000);
    } catch (e) {
      setScrapeError(e.response?.data?.detail || (tr ? 'İçe aktarma hatası.' : 'Import error.'));
    }
    setScrapeImporting(false);
  };

  // Pattern picker (for template assignment in Categories tab)
  const [patternPicker, setPatternPicker] = useState(null); // {catId, subId, tplName, currentItemIds}
  const [pickerSelected, setPickerSelected] = useState(new Set()); // selected ITEM IDs (not group IDs)
  const [savingPatterns, setSavingPatterns] = useState(false);

  // Collections viewer state
  const [colList, setColList] = useState([]);           // [{name, count}]
  const [selectedCol, setSelectedCol] = useState(null); // collection name
  const [colDocs, setColDocs] = useState([]);
  const [colTotal, setColTotal] = useState(0);
  const [colPage, setColPage] = useState(1);
  const [colPages, setColPages] = useState(1);
  const [colSearch, setColSearch] = useState('');
  const [colListSearch, setColListSearch] = useState(''); // filter collection names
  const [colLoading, setColLoading] = useState(false);
  const [expandedDoc, setExpandedDoc] = useState(null);

  // ─── Stand Options state ──────────────────────────────────────────────────
  const [modularAreas, setModularAreas] = useState([25, 50, 100, 150]);
  const [customAreas, setCustomAreas] = useState([50, 100, 150, 200, 300]);
  const [newArea, setNewArea] = useState('');
  const [newCustomArea, setNewCustomArea] = useState('');
  const [savingStandOpts, setSavingStandOpts] = useState(false);

  const fetchStandOptions = async () => {
    try {
      const r = await apiClient.get(`/ultra-admin/stand-options`);
      setModularAreas(r.data?.modular_areas || [25, 50, 100, 150]);
      setCustomAreas(r.data?.custom_areas  || [50, 100, 150, 200, 300]);
    } catch (_) {}
  };

  const saveStandOptions = async (patch) => {
    setSavingStandOpts(true);
    try {
      const r = await apiClient.put(`/ultra-admin/stand-options`, patch);
      setModularAreas(r.data.modular_areas);
      setCustomAreas(r.data.custom_areas);
      toast.success(tr ? 'Stand seçenekleri kaydedildi.' : 'Stand options saved.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kayıt hatası');
    }
    setSavingStandOpts(false);
  };

  const handleAddArea = () => {
    const v = parseInt(newArea, 10);
    if (!v || v <= 0) { toast.error(tr ? 'Geçerli bir m² değeri girin.' : 'Enter a valid m² value.'); return; }
    if (modularAreas.includes(v)) { toast.error(tr ? 'Bu değer zaten var.' : 'This value already exists.'); return; }
    const updated = [...modularAreas, v].sort((a, b) => a - b);
    setNewArea('');
    saveStandOptions({ modular_areas: updated });
  };

  const handleRemoveArea = (area) => {
    const updated = modularAreas.filter(a => a !== area);
    saveStandOptions({ modular_areas: updated });
  };

  const handleAddCustomArea = () => {
    const v = parseInt(newCustomArea, 10);
    if (!v || v <= 0) { toast.error(tr ? 'Geçerli bir m² değeri girin.' : 'Enter a valid m² value.'); return; }
    if (customAreas.includes(v)) { toast.error(tr ? 'Bu değer zaten var.' : 'This value already exists.'); return; }
    const updated = [...customAreas, v].sort((a, b) => a - b);
    setNewCustomArea('');
    saveStandOptions({ custom_areas: updated });
  };

  const handleRemoveCustomArea = (area) => {
    const updated = customAreas.filter(a => a !== area);
    saveStandOptions({ custom_areas: updated });
  };

  const fetchCollections = async () => {
    try {
      const r = await apiClient.get(`/ultra-admin/collections`);
      setColList(r.data);
    } catch (_) {}
  };

  const fetchColDocs = async (name, page = 1, q = '') => {
    setColLoading(true);
    try {
      const r = await apiClient.get(`/ultra-admin/collections/${name}`, {
        params: { page, limit: 50, q }
      });
      setColDocs(r.data.docs);
      setColTotal(r.data.total);
      setColPage(r.data.page);
      setColPages(r.data.pages);
    } catch (_) {}
    setColLoading(false);
  };

  const handleSelectCol = (name) => {
    setSelectedCol(name);
    setColPage(1);
    setColSearch('');
    setExpandedDoc(null);
    fetchColDocs(name, 1, '');
  };

  const handleDeleteColDoc = async (docId) => {
    if (!window.confirm(tr ? 'Bu kaydı silmek istediğinizden emin misiniz?' : 'Delete this record?')) return;
    try {
      await apiClient.delete(`/ultra-admin/collections/${selectedCol}/${docId}`);
      fetchColDocs(selectedCol, colPage, colSearch);
      fetchCollections();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // Load data based on active section
  useEffect(() => {
    fetchPlatformSettings(); // always load on mount
    if (activeSection === 'categories') { fetchCategories(); fetchSupplierCategories(); fetchContractorCategories(); fetchProductProfiles(); fetchPatternGroups(); }
    if (activeSection === 'emails') fetchTemplates();
    if (activeSection === 'visitors') fetchVisitorStats();
    if (activeSection === 'registration') fetchRegSettings();
    if (activeSection === 'units') fetchUnitsList();
    if (activeSection === 'shopping') fetchShoppingSettings();
    if (activeSection === 'stand-change-quota') fetchScrSettings();
    if (activeSection === 'session') fetchSessionSettings();
    if (activeSection === 'patterns') fetchPatternGroups();
    if (activeSection === 'collections') fetchCollections();
    if (activeSection === 'stand-options') fetchStandOptions();
    if (activeSection === 'security') fetchSecuritySettings();
  }, [activeSection]);

  useEffect(() => { if (activeSection === 'visitors') fetchVisitorStats(); }, [statsDays]);

  const fetchPlatformSettings = async () => {
    try {
      const r = await apiClient.get(`/ultra-admin/platform-settings`);
      setSettings({
        site_name: r.data.site_name || 'COP 31 Antalya Platform',
        admin_email: r.data.admin_email || 'mbucak@gmail.com',
        max_registrations: String(r.data.max_registrations || 500),
        event_start: r.data.event_start || '2026-11-09',
        event_end: r.data.event_end || '2026-11-20',
      });
    } catch { /* use defaults */ }
  };

  const fetchShoppingSettings = async () => {
    setShoppingLoading(true);
    try {
      const r = await apiClient.get(`/ultra-admin/shopping-settings`);
      setShoppingSettings({
        cart_reservation_timeout_hours: r.data.cart_reservation_timeout_hours ?? 24,
        cart_expiry_warning_hours_before: r.data.cart_expiry_warning_hours_before ?? 1,
        stock_low_threshold_percent: r.data.stock_low_threshold_percent ?? 50,
      });
    } catch { setShoppingError(tr ? 'Ayarlar yüklenemedi.' : 'Failed to load settings.'); }
    finally { setShoppingLoading(false); }
  };

  const fetchSecuritySettings = async () => {
    try {
      const r = await apiClient.get(`/ultra-admin/system-settings`);
      setAllowResetTestData(r.data.allow_reset_test_data || false);
    } catch { /* use defaults */ }
  };

  const handleToggleResetLock = async () => {
    setResetLockLoading(true);
    try {
      const newValue = !allowResetTestData;
      await apiClient.put(`/ultra-admin/system-settings/reset-lock`, {
        allow_reset_test_data: newValue
      });
      setAllowResetTestData(newValue);
      toast.success(newValue 
        ? (tr ? 'Test verisi sıfırlama özelliği etkinleştirildi.' : 'Test data reset feature enabled.')
        : (tr ? 'Test verisi sıfırlama özelliği devre dışı bırakıldı.' : 'Test data reset feature disabled.'));
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Ayar güncellenemedi.' : 'Failed to update setting.'));
    } finally {
      setResetLockLoading(false);
    }
  };

  const handleSaveShoppingSettings = async () => {
    setShoppingError('');
    const timeout = parseInt(shoppingSettings.cart_reservation_timeout_hours) || 24;
    const warning = parseInt(shoppingSettings.cart_expiry_warning_hours_before) || 1;
    const threshold = parseInt(shoppingSettings.stock_low_threshold_percent) || 50;
    if (warning >= timeout) {
      setShoppingError(tr ? 'Uyarı saati, rezervasyon süresinden küçük olmalıdır.' : 'Warning hours must be less than reservation timeout.');
      return;
    }
    if (threshold < 1 || threshold > 100) {
      setShoppingError(tr ? 'Düşük stok eşiği 1-100 arasında olmalıdır.' : 'Low stock threshold must be between 1 and 100.');
      return;
    }
    try {
      await apiClient.put(`/ultra-admin/shopping-settings`, {
        cart_reservation_timeout_hours: timeout,
        cart_expiry_warning_hours_before: warning,
        stock_low_threshold_percent: threshold,
      });
      setShoppingSaved(true);
      setTimeout(() => setShoppingSaved(false), 2500);
    } catch (e) { setShoppingError(e.response?.data?.detail || (tr ? 'Kaydetme hatası.' : 'Save error.')); }
  };

  // ── Phase 86N — Stand Change Request Quota ────────────────────────────────
  const fetchScrSettings = async () => {
    setScrLoading(true);
    try {
      const r = await apiClient.get(`/ultra-admin/stand-change-settings`);
      setScrSettings({
        max_change_requests_per_tenant: r.data.max_change_requests_per_tenant ?? 1,
      });
    } catch { setScrError(tr ? 'Ayar yüklenemedi.' : 'Failed to load.'); }
    finally { setScrLoading(false); }
  };

  const handleSaveScrSettings = async () => {
    setScrError('');
    const n = Math.max(1, Math.min(20, parseInt(scrSettings.max_change_requests_per_tenant) || 1));
    try {
      await apiClient.put(`/ultra-admin/stand-change-settings`, {
        max_change_requests_per_tenant: n,
      });
      setScrSettings({ max_change_requests_per_tenant: n });
      setScrSaved(true);
      setTimeout(() => setScrSaved(false), 2500);
    } catch (e) { setScrError(e.response?.data?.detail || (tr ? 'Kaydetme hatası.' : 'Save error.')); }
  };

  // ── Session Settings ────────────────────────────────────────────────────────
  const fetchSessionSettings = async () => {
    setSessionLoading(true);
    try {
      const r = await apiClient.get(`/ultra-admin/session-settings`);
      setSessionSettings(prev => ({ ...prev, ...r.data }));
    } catch { setSessionError(tr ? 'Ayarlar yüklenemedi.' : 'Failed to load.'); }
    finally { setSessionLoading(false); }
  };

  const handleSaveSessionSettings = async () => {
    setSessionError('');
    try {
      await apiClient.put(`/ultra-admin/session-settings`, sessionSettings);
      setSessionSaved(true);
      setTimeout(() => setSessionSaved(false), 2500);
      toast.success(tr ? 'Oturum ayarları kaydedildi.' : 'Session settings saved.');
    } catch (e) { setSessionError(e.response?.data?.detail || (tr ? 'Kaydetme hatası.' : 'Save error.')); }
  };

  const updateSessionRole = (role, field, value) =>
    setSessionSettings(s => ({ ...s, [role]: { ...s[role], [field]: value } }));

  // ── Pattern Groups CRUD ──────────────────────────────────────────────────────
  const fetchPatternGroups = async () => {
    setPatternLoading(true);
    try {
      const r = await apiClient.get(`/ultra-admin/pattern-groups`);
      setPatternGroups(r.data);
      if (!selectedGroupId && r.data.length > 0) setSelectedGroupId(r.data[0].id);
    } catch { setPatternGroupError(tr ? 'Yüklenemedi.' : 'Failed to load.'); }
    finally { setPatternLoading(false); }
  };

  const handleAddPatternGroup = async () => {
    const name = newGroupName.trim();
    if (!name) { setPatternGroupError(tr ? 'Grup adı boş olamaz.' : 'Group name required.'); return; }
    setPatternGroupError('');
    try {
      await apiClient.post(`/ultra-admin/pattern-groups`, { name });
      setNewGroupName('');
      const r = await apiClient.get(`/ultra-admin/pattern-groups`);
      setPatternGroups(r.data);
      const added = r.data.find(g => g.name === name);
      if (added) setSelectedGroupId(added.id);
    } catch (e) { setPatternGroupError(e.response?.data?.detail || 'Hata'); }
  };

  const handleUpdatePatternGroup = async (id) => {
    if (!editingGroup?.name?.trim()) return;
    try {
      await apiClient.put(`/ultra-admin/pattern-groups/${id}`, { name: editingGroup.name });
      setEditingGroup(null);
      const r = await apiClient.get(`/ultra-admin/pattern-groups`);
      setPatternGroups(r.data);
    } catch (e) { setPatternGroupError(e.response?.data?.detail || 'Hata'); }
  };

  const handleDeletePatternGroup = async (id) => {
    if (!window.confirm(tr ? 'Bu grubu ve tüm öğelerini silmek istediğinize emin misiniz?' : 'Delete this group and all its items?')) return;
    try {
      await apiClient.delete(`/ultra-admin/pattern-groups/${id}`);
      const r = await apiClient.get(`/ultra-admin/pattern-groups`);
      setPatternGroups(r.data);
      setSelectedGroupId(r.data.length > 0 ? r.data[0].id : null);
    } catch (e) { setPatternGroupError(e.response?.data?.detail || 'Hata'); }
  };

  const handleAddPatternItem = async () => { return; };

  // Fill slots from a FileList — starting from first empty slot
  const fillSlotsFromFiles = useCallback((files) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    const valid = Array.from(files).filter(f => validTypes.includes(f.type));
    setPatternSlots(prev => {
      const next = [...prev];
      let fi = 0;
      for (let i = 0; i < 12 && fi < valid.length; i++) {
        if (!next[i]) {
          const file = valid[fi++];
          next[i] = {
            file,
            preview: URL.createObjectURL(file),
            name: file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' '),
            status: 'pending',
            error: null,
          };
        }
      }
      return next;
    });
  }, []);

  // Drop on the whole grid (fill empty slots)
  const handleGridDragOver = useCallback((e) => { e.preventDefault(); }, []);
  const handleGridDrop = useCallback((e) => {
    e.preventDefault();
    setIsDraggingSlot(null);
    fillSlotsFromFiles(e.dataTransfer.files);
  }, [fillSlotsFromFiles]);

  // Drop on a specific slot
  const handleSlotDrop = useCallback((e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingSlot(null);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) return;
    setPatternSlots(prev => {
      const next = [...prev];
      next[idx] = { file, preview: URL.createObjectURL(file), name: file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' '), status: 'pending', error: null };
      return next;
    });
  }, []);

  const handleBulkUpload = async () => {
    const filled = patternSlots.map((s, i) => ({ slot: s, idx: i })).filter(x => x.slot && x.slot.status === 'pending');
    if (!filled.length || !selectedGroupId) return;
    setBulkUploading(true);
    setBulkProgress({ done: 0, total: filled.length });
    let done = 0;
    for (const { slot, idx } of filled) {
      setPatternSlots(prev => { const n = [...prev]; if (n[idx]) n[idx] = { ...n[idx], status: 'uploading' }; return n; });
      try {
        const fd = new FormData();
        fd.append('name', slot.name.trim() || slot.file.name);
        fd.append('image', await compressImage(slot.file));
        await apiClient.post(`/ultra-admin/pattern-groups/${selectedGroupId}/items`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setPatternSlots(prev => { const n = [...prev]; if (n[idx]) n[idx] = { ...n[idx], status: 'done' }; return n; });
      } catch (e) {
        setPatternSlots(prev => { const n = [...prev]; if (n[idx]) n[idx] = { ...n[idx], status: 'error', error: e.response?.data?.detail || 'Hata' }; return n; });
      }
      done++;
      setBulkProgress({ done, total: filled.length });
    }
    const r = await apiClient.get(`/ultra-admin/pattern-groups`);
    setPatternGroups(r.data);
    setBulkUploading(false);
    const hasErrors = patternSlots.some(s => s?.status === 'error');
    if (!hasErrors) { setShowAddItemModal(false); setPatternSlots(Array(12).fill(null)); }
  };

  const handleUpdatePatternItem = async () => {
    if (!editingItem?.name?.trim()) { setNewItemError(tr ? 'İsim boş olamaz.' : 'Name required.'); return; }
    setSavingItem(true); setNewItemError('');
    try {
      const fd = new FormData();
      fd.append('name', editingItem.name.trim());
      if (editingItem.newImage) fd.append('image', await compressImage(editingItem.newImage));
      await apiClient.put(`/ultra-admin/pattern-items/${editingItem.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setEditingItem(null);
      setShowAddItemModal(false);
      const r = await apiClient.get(`/ultra-admin/pattern-groups`);
      setPatternGroups(r.data);
    } catch (e) { setNewItemError(e.response?.data?.detail || 'Hata'); }
    finally { setSavingItem(false); }
  };

  const handleDeletePatternItem = async (itemId) => {
    if (!window.confirm(tr ? 'Bu öğeyi silmek istediğinize emin misiniz?' : 'Delete this item?')) return;
    try {
      await apiClient.delete(`/ultra-admin/pattern-items/${itemId}`);
      const r = await apiClient.get(`/ultra-admin/pattern-groups`);
      setPatternGroups(r.data);
    } catch {}
  };

  const fetchUnitsList = async () => {    setUnitLoading(true);
    try { const r = await apiClient.get(`/ultra-admin/units`); setUnits(r.data); }
    catch { setUnitError('Yüklenemedi.'); }
    finally { setUnitLoading(false); }
  };
  const handleAddUnit = async () => {
    const name = unitForm.name.trim(); const code = unitForm.code.trim();
    if (!name || !code) { setUnitError(tr ? 'Ad ve kod zorunludur.' : 'Name and code required.'); return; }
    setUnitError('');
    try { await apiClient.post(`/ultra-admin/units`, { name, code }); setUnitForm({ name: '', code: '' }); fetchUnitsList(); }
    catch (e) { setUnitError(e.response?.data?.detail || 'Hata'); }
  };
  const handleUpdateUnit = async (id) => {
    if (!editingUnit?.name?.trim()) return;
    try { await apiClient.put(`/ultra-admin/units/${id}`, { name: editingUnit.name, code: editingUnit.code, is_active: editingUnit.is_active }); setEditingUnit(null); fetchUnitsList(); }
    catch (e) { setUnitError(e.response?.data?.detail || 'Hata'); }
  };
  const handleToggleUnit = async (u) => {
    try { await apiClient.put(`/ultra-admin/units/${u.id}`, { is_active: !u.is_active }); fetchUnitsList(); }
    catch {}
  };
  const handleDeleteUnit = async (id) => {
    if (!window.confirm(tr ? 'Bu birimi silmek istiyor musunuz?' : 'Delete this unit?')) return;
    try { await apiClient.delete(`/ultra-admin/units/${id}`); fetchUnitsList(); }
    catch (e) { setUnitError(e.response?.data?.detail || 'Hata'); }
  };

  const fetchCategories = async () => {    setCatLoading(true);
    try { const r = await apiClient.get(`/ultra-admin/user-categories`); setCategories(r.data); }
    catch { setCatError(tr ? 'Yüklenemedi.' : 'Failed to load.'); }
    finally { setCatLoading(false); }
  };
  const handleAddCategory = async () => {
    const name_tr = (catForm.name_tr || '').trim();
    const name_en = (catForm.name_en || '').trim();
    if (!name_tr && !name_en) { setCatError(tr ? 'En az bir dilde ad girin (TR veya EN).' : 'Enter a name in at least one language (TR or EN).'); return; }
    setCatError('');
    try { await apiClient.post(`/ultra-admin/user-categories`, { name_tr, name_en }); setCatForm({ name_tr: '', name_en: '' }); fetchCategories(); }
    catch (e) { setCatError(e.response?.data?.detail || 'Hata'); }
  };
  const handleUpdateCategory = async (id) => {
    const name_tr = (editingCat?.name_tr || '').trim();
    const name_en = (editingCat?.name_en || '').trim();
    if (!name_tr && !name_en) return;
    try { await apiClient.put(`/ultra-admin/user-categories/${id}`, { name_tr, name_en }); setEditingCat(null); fetchCategories(); }
    catch (e) { setCatError(e.response?.data?.detail || 'Hata'); }
  };
  const handleDeleteCategory = async (id) => {
    if (!window.confirm(tr ? 'Silmek istediğinize emin misiniz?' : 'Delete this category?')) return;
    try { await apiClient.delete(`/ultra-admin/user-categories/${id}`); fetchCategories(); }
    catch (e) { setCatError(e.response?.data?.detail || 'Hata'); }
  };

  // Supplier categories
  const fetchSupplierCategories = async () => {
    setSupplierCatLoading(true);
    try { const r = await apiClient.get(`/ultra-admin/supplier-categories`); setSupplierCats(r.data); }
    catch { setSupplierCatError(tr ? 'Yüklenemedi.' : 'Failed to load.'); }
    finally { setSupplierCatLoading(false); }
  };

  // Contractor categories
  const fetchContractorCategories = async () => {
    try { const r = await apiClient.get(`/stand-designs/admin/contractor-categories`); setContractorCats(r.data || []); }
    catch {}
  };
  const handleAddContractorCategory = async () => {
    const name_tr = (contractorCatForm.name_tr || '').trim();
    const name_en = (contractorCatForm.name_en || '').trim();
    if (!name_tr && !name_en) { setContractorCatError(tr ? 'En az bir dilde ad girin.' : 'Enter a name in at least one language.'); return; }
    setContractorCatError('');
    try { await apiClient.post(`/stand-designs/admin/contractor-categories`, { name_tr, name_en }); setContractorCatForm({ name_tr: '', name_en: '' }); fetchContractorCategories(); }
    catch (e) { setContractorCatError(e.response?.data?.detail || 'Hata'); }
  };
  const handleUpdateContractorCategory = async (id) => {
    const name_tr = (editingContractorCat?.name_tr || '').trim();
    const name_en = (editingContractorCat?.name_en || '').trim();
    if (!name_tr && !name_en) return;
    try { await apiClient.put(`/stand-designs/admin/contractor-categories/${id}`, { name_tr, name_en }); setEditingContractorCat(null); fetchContractorCategories(); }
    catch (e) { setContractorCatError(e.response?.data?.detail || 'Hata'); }
  };
  const handleDeleteContractorCategory = async (id) => {
    if (!window.confirm(tr ? 'Silmek istediğinize emin misiniz?' : 'Delete this category?')) return;
    try { await apiClient.delete(`/stand-designs/admin/contractor-categories/${id}`); fetchContractorCategories(); }
    catch (e) { setContractorCatError(e.response?.data?.detail || 'Hata'); }
  };

  // Scroll pozisyonunu bozmadan sessiz güncelleme (loading spinner göstermez)
  const silentRefreshSupplierCategories = async () => {
    try { const r = await apiClient.get(`/ultra-admin/supplier-categories`); setSupplierCats(r.data); }
    catch {}
  };
  const handleAddSupplierCategory = async () => {
    const name_tr = (supplierCatForm.name_tr || '').trim();
    const name_en = (supplierCatForm.name_en || '').trim();
    if (!name_tr && !name_en) { setSupplierCatError(tr ? 'En az bir dilde ad girin.' : 'Enter a name in at least one language.'); return; }
    setSupplierCatError('');
    try { await apiClient.post(`/ultra-admin/supplier-categories`, { name_tr, name_en }); setSupplierCatForm({ name_tr: '', name_en: '' }); fetchSupplierCategories(); }
    catch (e) { setSupplierCatError(e.response?.data?.detail || 'Hata'); }
  };
  const handleUpdateSupplierCategory = async (id) => {
    const name_tr = (editingSupplierCat?.name_tr || '').trim();
    const name_en = (editingSupplierCat?.name_en || '').trim();
    if (!name_tr && !name_en) return;
    try { await apiClient.put(`/ultra-admin/supplier-categories/${id}`, { name_tr, name_en }); setEditingSupplierCat(null); fetchSupplierCategories(); }
    catch (e) { setSupplierCatError(e.response?.data?.detail || 'Hata'); }
  };
  const handleDeleteSupplierCategory = async (id) => {
    if (!window.confirm(tr ? 'Silmek istediğinize emin misiniz?' : 'Delete this category?')) return;
    try { await apiClient.delete(`/ultra-admin/supplier-categories/${id}`); fetchSupplierCategories(); }
    catch (e) { setSupplierCatError(e.response?.data?.detail || 'Hata'); }
  };

  // Sub-category handlers
  const toggleCatExpand = (catId) => setExpandedCats(p => ({ ...p, [catId]: !p[catId] }));
  const setSubForm = (catId, val) => setNewSubForms(p => ({ ...p, [catId]: val }));
  const clearSubError = (catId) => setSubErrors(p => ({ ...p, [catId]: '' }));

  const handleAddSubCategory = async (catId) => {
    const raw = newSubForms[catId];
    // Accept both legacy string and new {name_tr,name_en} object form
    const name_tr = (typeof raw === 'object' ? raw?.name_tr : raw || '').trim();
    const name_en = (typeof raw === 'object' ? raw?.name_en : raw || '').trim();
    if (!name_tr && !name_en) { setSubErrors(p => ({ ...p, [catId]: tr ? 'En az bir dilde ad girin.' : 'Enter a name in at least one language.' })); return; }
    clearSubError(catId);
    try {
      await apiClient.post(`/ultra-admin/supplier-categories/${catId}/subcategories`, { name_tr, name_en });
      setSubForm(catId, { name_tr: '', name_en: '' });
      fetchSupplierCategories();
    } catch (e) { setSubErrors(p => ({ ...p, [catId]: e.response?.data?.detail || 'Hata' })); }
  };

  const handleUpdateSubCategory = async (catId, subId) => {
    const name_tr = (editingSub?.name_tr || '').trim();
    const name_en = (editingSub?.name_en || '').trim();
    if (!name_tr && !name_en) return;
    try {
      await apiClient.put(`/ultra-admin/supplier-categories/${catId}/subcategories/${subId}`, { name_tr, name_en });
      setEditingSub(null);
      fetchSupplierCategories();
    } catch (e) { setSubErrors(p => ({ ...p, [catId]: e.response?.data?.detail || 'Hata' })); }
  };

  const handleDeleteSubCategory = async (catId, subId) => {
    if (!window.confirm(tr ? 'Alt kategoriyi silmek istediğinize emin misiniz?' : 'Delete this subcategory?')) return;
    try { await apiClient.delete(`/ultra-admin/supplier-categories/${catId}/subcategories/${subId}`); fetchSupplierCategories(); }
    catch (e) { setSubErrors(p => ({ ...p, [catId]: e.response?.data?.detail || 'Hata' })); }
  };

  // ── Product Templates ────────────────────────────────────────────────────
  const [editingTpl, setEditingTpl] = useState(null); // {catId, subId, oldName, newName}

  // Subcategory state'ini refetch yapmadan günceller (scroll sıfırlamaz)
  const updateSubLocally = (catId, subId, updater) => {
    setSupplierCats(prev => prev.map(cat => {
      if (cat.id !== catId) return cat;
      return {
        ...cat,
        subcategories: (cat.subcategories || []).map(sub =>
          sub.id === subId ? updater(sub) : sub
        ),
      };
    }));
  };

  const handleAddTemplate = async (catId, subId) => {
    const name = (newTplForms[subId] || '').trim();
    if (!name) return;
    try {
      const r = await apiClient.post(`/ultra-admin/supplier-categories/${catId}/subcategories/${subId}/templates`, { template: name });
      setNewTplForms(p => ({ ...p, [subId]: '' }));
      setTplErrors(p => ({ ...p, [subId]: '' }));
      // Optimistic update + arka planda sessiz sync
      updateSubLocally(catId, subId, sub => ({
        ...sub,
        product_templates: r.data.product_templates,
        template_codes: r.data.template_codes,
      }));
      silentRefreshSupplierCategories();
    } catch (e) {
      setTplErrors(p => ({ ...p, [subId]: e.response?.data?.detail || 'Hata' }));
      // Hata durumunda da sync yap — local state ile DB uyumunu garantile
      silentRefreshSupplierCategories();
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTpl) return;
    const { catId, subId, oldName, newName } = editingTpl;
    const trimmed = newName.trim();
    if (!trimmed) return;
    try {
      const r = await apiClient.put(
        `/ultra-admin/supplier-categories/${catId}/subcategories/${subId}/templates`,
        { old_name: oldName, new_name: trimmed }
      );
      setEditingTpl(null);
      setTplErrors(p => ({ ...p, [subId]: '' }));
      updateSubLocally(catId, subId, sub => ({
        ...sub,
        product_templates: r.data.product_templates,
        template_codes: r.data.template_codes,
      }));
      silentRefreshSupplierCategories();
    } catch (e) {
      setTplErrors(p => ({ ...p, [subId]: e.response?.data?.detail || 'Hata' }));
      silentRefreshSupplierCategories();
    }
  };

  const handleRemoveTemplate = async (catId, subId, tpl) => {
    // Önce local state'den kaldır (anlık geri bildirim)
    updateSubLocally(catId, subId, sub => ({
      ...sub,
      product_templates: (sub.product_templates || []).filter(t => t !== tpl),
    }));
    try {
      await apiClient.delete(`/ultra-admin/supplier-categories/${catId}/subcategories/${subId}/templates`, { data: { template: tpl } });
      silentRefreshSupplierCategories();
    } catch (e) {
      setTplErrors(p => ({ ...p, [subId]: e.response?.data?.detail || 'Hata' }));
      silentRefreshSupplierCategories(); // hata durumunda geri al
    }
  };
  // ── Product Profiles ─────────────────────────────────────────────────────
  const fetchProductProfiles = async () => {
    setProfilesLoading(true);
    try { const r = await apiClient.get(`/ultra-admin/product-profiles`); setProductProfiles(r.data); }
    catch { } finally { setProfilesLoading(false); }
  };

  const handleAddProfile = async () => {
    const { name, formula, calculation_type, pricing_mode } = newProfileForm;
    if (!name.trim()) { setProfileError(tr ? 'Profil adı gerekli.' : 'Profile name required.'); return; }
    try {
      await apiClient.post(`/ultra-admin/product-profiles`, { name: name.trim(), formula: formula.trim(), calculation_type, pricing_mode: pricing_mode || null });
      setNewProfileForm({ name: '', formula: '', calculation_type: 'flat', pricing_mode: '' });
      setProfileError('');
      fetchProductProfiles();
    } catch (e) { setProfileError(e.response?.data?.detail || 'Hata'); }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm(tr ? 'Profili silmek istiyor musunuz?' : 'Delete this profile?')) return;
    try { await apiClient.delete(`/ultra-admin/product-profiles/${profileId}`); fetchProductProfiles(); }
    catch (e) { setProfileError(e.response?.data?.detail || 'Silinemedi.'); }
  };

  const handleMoveTemplate = async (catId, subId, tplIdx, dir) => {
    const sub = supplierCats.find(c => c.id === catId)?.subcategories?.find(s => s.id === subId);
    if (!sub) return;
    const templates = [...(sub.product_templates || [])];
    const next = tplIdx + dir;
    if (next < 0 || next >= templates.length) return;
    [templates[tplIdx], templates[next]] = [templates[next], templates[tplIdx]];
    // Optimistic local update
    updateSubLocally(catId, subId, s => ({ ...s, product_templates: templates }));
    // Persist to server
    try {
      await apiClient.post(
        `/ultra-admin/supplier-categories/${catId}/subcategories/${subId}/templates/reorder`,
        { templates }
      );
    } catch { silentRefreshSupplierCategories(); }
  };

  const handleSaveTemplatePatterns = async () => {
    if (!patternPicker) return;
    const { catId, subId, tplName } = patternPicker;
    setSavingPatterns(true);
    try {
      const r = await apiClient.put(
        `/ultra-admin/supplier-categories/${catId}/subcategories/${subId}/template-patterns`,
        { template_name: tplName, item_ids: [...pickerSelected] },   // individual item IDs
      );
      updateSubLocally(catId, subId, s => ({ ...s, template_pattern_map: r.data.template_pattern_map }));
      setPatternPicker(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    finally { setSavingPatterns(false); }
  };

  const handleSetTemplateProfile = async (catId, subId, templateName, profileId) => {    try {
      await apiClient.put(`/ultra-admin/supplier-categories/${catId}/subcategories/${subId}/template-profiles`,
        { template_name: templateName, profile_id: profileId });
      // Scroll pozisyonunu bozmamak için local state güncelle
      updateSubLocally(catId, subId, sub => ({
        ...sub,
        template_profile_map: {
          ...(sub.template_profile_map || {}),
          [templateName]: profileId,
        },
      }));
      silentRefreshSupplierCategories();
    } catch (e) { setTplErrors(p => ({ ...p, [subId]: e.response?.data?.detail || 'Hata' })); }
  };

  // ── Reorder helpers ─────────────────────────────────────────────────────
  const moveItem = (arr, idx, dir) => {
    const next = idx + dir;
    if (next < 0 || next >= arr.length) return arr;
    const copy = [...arr];
    [copy[idx], copy[next]] = [copy[next], copy[idx]];
    return copy;
  };

  const handleMoveCategory = async (idx, dir) => {
    const next = moveItem(categories, idx, dir);
    setCategories(next);
    try { await apiClient.post(`/ultra-admin/user-categories/reorder`, { ids: next.map(c => c.id) }); }
    catch {}
  };

  const handleMoveSupplierCat = async (idx, dir) => {
    const next = moveItem(supplierCats, idx, dir);
    setSupplierCats(next);
    try { await apiClient.post(`/ultra-admin/supplier-categories/reorder`, { ids: next.map(c => c.id) }); }
    catch {}
  };

  const handleMoveSubCat = async (catId, subIdx, dir) => {
    setSupplierCats(prev => prev.map(cat => {
      if (cat.id !== catId) return cat;
      const newSubs = moveItem(cat.subcategories || [], subIdx, dir);
      return { ...cat, subcategories: newSubs };
    }));
    // persist
    const cat = supplierCats.find(c => c.id === catId);
    if (!cat) return;
    const newSubs = moveItem(cat.subcategories || [], subIdx, dir);
    try { await apiClient.post(`/ultra-admin/supplier-categories/${catId}/subcategories/reorder`, { ids: newSubs.map(s => s.id) }); }
    catch {}
  };

  const fetchTemplates = async () => {
    setTmplLoading(true);
    try { const r = await apiClient.get(`/ultra-admin/email-templates`); setTemplates(r.data); }
    catch {} finally { setTmplLoading(false); }
  };
  const handleSaveTemplate = async (form) => {
    if (isNewTmpl) {
      await apiClient.post(`/ultra-admin/email-templates`, form);
    } else {
      await apiClient.put(`/ultra-admin/email-templates/${form.id}`, form);
    }
    fetchTemplates();
  };
  const handleDeleteTemplate = async (id) => {
    if (!window.confirm(tr ? 'Bu şablonu silmek istediğinize emin misiniz?' : 'Delete this template?')) return;
    await apiClient.delete(`/ultra-admin/email-templates/${id}`);
    fetchTemplates();
  };

  const fetchVisitorStats = async () => {
    setVisitorLoading(true);
    try { const r = await apiClient.get(`/ultra-admin/visitor-stats?days=${statsDays}`); setVisitorStats(r.data); }
    catch {} finally { setVisitorLoading(false); }
  };

  const fetchRegSettings = async () => {
    setRegLoading(true);
    try { const r = await apiClient.get(`/ultra-admin/registration-settings`); setRegSettings(r.data); }
    catch {} finally { setRegLoading(false); }
  };
  const handleSaveRegSettings = async () => {
    try {
      await apiClient.put(`/ultra-admin/registration-settings`, {
        blue_zone_open: regSettings.blue_zone_open,
        green_zone_open: regSettings.green_zone_open,
        supplier_open: regSettings.supplier_open,
      });
      setRegSaved(true); setTimeout(() => setRegSaved(false), 2000);
    } catch {}
  };

  const handleChangeLoginPassword = async () => {
    if (!loginPwForm.newp) { setLoginPwError(tr ? 'Yeni şifreyi girin.' : 'Enter new password.'); return; }
    if (loginPwForm.newp !== loginPwForm.confirm) { setLoginPwError(tr ? 'Şifreler eşleşmiyor.' : 'Passwords do not match.'); return; }
    if (loginPwForm.newp.length < 6) { setLoginPwError(tr ? 'Şifre en az 6 karakter.' : 'Min 6 characters.'); return; }
    setLoginPwLoading(true); setLoginPwError('');
    try {
      await apiClient.post(`/ultra-admin/security/change-login-password`, { new_password: loginPwForm.newp });
      setLoginPwSuccess(true); setLoginPwForm({ current: '', newp: '', confirm: '' });
    } catch (e) { setLoginPwError(e.response?.data?.detail || 'Hata'); }
    finally { setLoginPwLoading(false); }
  };


  const handleLogout = () => { localStorage.removeItem('cop31_ultra_token'); localStorage.removeItem('cop31_ultra_user'); navigate('/giris'); };

  const menuItems = [
    { key: 'general',        icon: Globe,            label: tr ? 'Genel'                  : 'General'            },
    { key: 'registration',   icon: Users,            label: tr ? 'Kayıt'                  : 'Registration'       },
    { key: 'unfccc',         icon: Mail,             label: tr ? 'UNFCCC E-posta Listesi' : 'UNFCCC Email List'   },
    { key: 'stand-options',  icon: SlidersHorizontal, label: tr ? 'Stand Seçenekleri'    : 'Stand Options'       },
    { key: 'categories',     icon: Tags,             label: tr ? 'Kategoriler'             : 'Categories'         },
    { key: 'units',          icon: Settings,         label: tr ? 'Birimler'               : 'Units'              },
    { key: 'patterns',       icon: Palette,          label: tr ? 'Desenler & Renkler'     : 'Patterns & Colors'  },
    { key: 'shopping',       icon: ShoppingCart,     label: tr ? 'Alışveriş'              : 'Shopping'           },
    { key: 'stand-change-quota', icon: SlidersHorizontal, label: tr ? 'Stand Değişiklik Limiti' : 'Stand Change Limit' },
    { key: 'session',        icon: Clock,            label: tr ? 'Oturum Yönetimi'        : 'Session Management' },
    { key: 'security',       icon: Shield,           label: 'Security Center'                                    },
    { key: 'emails',         icon: Mail,             label: tr ? 'E-posta Şablonları'     : 'Email Templates'    },
    { key: 'visitors',       icon: Monitor,          label: tr ? 'Ziyaretçi Takip'        : 'Visitor Tracking'   },
    { key: 'logs',           icon: Activity,         label: tr ? 'Sistem Günlükleri'      : 'System Logs'        },
    { key: 'collections',    icon: Database,         label: tr ? 'Koleksiyonlar'          : 'Collections'        },
    { key: 'grid',           icon: SlidersHorizontal, label: tr ? 'Grid Plan Ayarları'   : 'Grid Settings'       },
    { key: 'audit',          icon: Activity,         label: tr ? 'Denetim Kayıtları'      : 'Audit Logs'         },
    { key: 'email-library',  icon: Mail,             label: tr ? 'E-posta Kütüphanesi'    : 'Email Library'      },
    { key: 'order-maintenance', icon: Wrench,        label: tr ? 'Sipariş Bakımı'         : 'Order Maintenance'  },
    // ── Moved from sidebar (Phase 80) — these are config/maintenance items
    //    admins don't need at the top level. They live here under Settings
    //    and simply deep-link to their dedicated pages (routes unchanged).
    { key: 'delegate-map',   icon: Globe2,           label: tr ? 'Delege Haritası'        : 'Delegate Map'       },
    { key: 'platform-rules', icon: BookOpen,         label: tr ? 'Platform Kuralları'     : 'Platform Rules'     },
    { key: 'data-health',    icon: Activity,         label: tr ? 'Veri Sağlığı'           : 'Data Health'        },
    { key: 'media-health',   icon: ImageIcon,        label: tr ? 'Medya Sağlığı'          : 'Media Health'       },
    { key: 'system-guide',   icon: HelpCircle,       label: tr ? 'Sistem Rehberi'         : 'System Guide'       },
    // Phase 86I — CDN (CloudFront) configuration. Primary super admin only.
    ...(isPrimary ? [{ key: 'cdn-config', icon: Globe, label: tr ? 'CDN (CloudFront) Ayarları' : 'CDN (CloudFront) Settings' }] : []),
    // ── Phase 82: Dashboard'dan taşınanlar — Business Hours ve Admin Activity
    //    artık Sistem Ayarları altında shortcut olarak yaşıyor.
    { key: 'business-hours', icon: Clock,            label: tr ? 'Çalışma Saatleri'       : 'Business Hours'     },
    { key: 'admin-activity', icon: Activity,         label: tr ? 'Yönetici Aktiviteleri'  : 'Admin Activity'     },
    // Only primary super admin (is_main_user=true) sees the Super Admins
    // management shortcut — mirrors the previous top-level sidebar gate.
    ...(isPrimary ? [{ key: 'super-admins',   icon: Shield,           label: tr ? 'Süper Adminler'         : 'Super Admins'       }] : []),
    { key: 'danger-zone',    icon: AlertTriangle,    label: tr ? 'Tehlikeli İşlemler'     : 'Dangerous Operations' },
  ];

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString(tr ? 'tr-TR' : 'en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      <div className="flex-1 flex flex-col">
        {/* Top Nav */}
        <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="px-6 flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-gray-100 flex items-center justify-center"><Settings className="h-4 w-4 text-gray-600" /></div>
              <div>
                <h1 className="text-base font-bold text-gray-900">{tr ? 'Ayarlar' : 'Settings'}</h1>
                <p className="text-xs text-gray-400">COP 31 {tr ? 'Platform Yönetimi' : 'Platform Management'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" className="rounded-full"><Bell className="h-5 w-5" /></Button>
              <LanguageToggle variant="admin" />
              <Button onClick={handleLogout} variant="outline" size="sm" className="rounded-full gap-2"><LogOut className="h-4 w-4" />{tr ? 'Çıkış' : 'Logout'}</Button>
            </div>
          </div>
        </nav>

        <div className="flex flex-1 overflow-hidden">
          {/* Left menu */}
          <aside className="w-56 bg-white border-r border-gray-100 flex-shrink-0 py-4">
            <p className="px-4 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">{tr ? 'Ayar Başlıkları' : 'Settings'}</p>
            <nav className="space-y-0.5 px-2">
              {menuItems.map(({ key, icon: Icon, label }) => {
                const isDanger = key === 'danger-zone';
                const active   = activeSection === key;
                return (
                  <button key={key} onClick={() => goSection(key)}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                      active
                        ? (isDanger ? 'bg-red-600 text-white font-semibold' : 'bg-gray-900 text-white font-semibold')
                        : (isDanger ? 'text-red-600 hover:bg-red-50 font-medium' : 'text-gray-600 hover:bg-gray-50')
                    }`}>
                    <div className="flex items-center gap-2.5"><Icon className="h-4 w-4 flex-shrink-0" /><span>{label}</span></div>
                    {active && <ChevronRight className="h-3.5 w-3.5 opacity-60" />}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Right content */}
          <main className="flex-1 overflow-y-auto p-8">

            {/* ── General ── */}
            {activeSection === 'general' && (
              <div className="max-w-2xl space-y-6">
                <div><h2 className="text-xl font-bold text-gray-900">{tr ? 'Genel Ayarlar' : 'General Settings'}</h2><p className="text-sm text-gray-400 mt-0.5">{tr ? 'Platform temel yapılandırması' : 'Core platform configuration'}</p></div>
                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Globe className="h-4 w-4 text-purple-700" />{tr ? 'Platform Ayarları' : 'Platform Settings'}</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-1"><Label>{tr ? 'Site Adı' : 'Site Name'}</Label><Input value={settings.site_name} onChange={e => setSettings({...settings, site_name: e.target.value})} className="rounded-xl" /></div>
                    <div className="space-y-1"><Label>{tr ? 'Admin E-posta' : 'Admin Email'}</Label><Input value={settings.admin_email} onChange={e => setSettings({...settings, admin_email: e.target.value})} className="rounded-xl" /></div>
                    <div className="space-y-1"><Label>{tr ? 'Maksimum Kayıt' : 'Max Registrations'}</Label><Input type="number" value={settings.max_registrations} onChange={e => setSettings({...settings, max_registrations: e.target.value})} className="rounded-xl" /></div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Clock className="h-4 w-4 text-sky-600" />{tr ? 'Etkinlik Tarihleri' : 'Event Dates'}</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1"><Label>{tr ? 'Başlangıç' : 'Start'}</Label><Input type="date" value={settings.event_start} onChange={e => setSettings({...settings, event_start: e.target.value})} className="rounded-xl" /></div>
                      <div className="space-y-1"><Label>{tr ? 'Bitiş' : 'End'}</Label><Input type="date" value={settings.event_end} onChange={e => setSettings({...settings, event_end: e.target.value})} className="rounded-xl" /></div>
                    </div>
                    <div className="mt-4 bg-purple-50 rounded-xl p-3"><p className="text-sm font-semibold text-emerald-800">COP 31 • {settings.event_start?.split('-').reverse().join('.')} — {settings.event_end?.split('-').reverse().join('.')} • Antalya, Türkiye</p></div>
                  </CardContent>
                </Card>
                <div className="flex justify-end">
                  <Button onClick={async () => {
                    setSaved(true);
                    try {
                      await apiClient.put(`/ultra-admin/platform-settings`, {
                        site_name: settings.site_name,
                        admin_email: settings.admin_email,
                        max_registrations: parseInt(settings.max_registrations) || 500,
                        event_start: settings.event_start,
                        event_end: settings.event_end,
                      });
                    } catch {}
                    setTimeout(() => setSaved(false), 2000);
                  }} className="bg-purple-700 hover:bg-purple-800 text-white rounded-full px-8 gap-2">
                    <Save className="h-4 w-4" />{saved ? (tr ? 'Kaydedildi!' : 'Saved!') : (tr ? 'Kaydet' : 'Save')}
                  </Button>
                </div>
              </div>
            )}

            {/* ── Registration ── */}
            {activeSection === 'registration' && (
              <div className="max-w-2xl space-y-6">
                <div><h2 className="text-xl font-bold text-gray-900">{tr ? 'Kayıt Ayarları' : 'Registration Settings'}</h2><p className="text-sm text-gray-400 mt-0.5">{tr ? 'Zone bazlı kayıt akışlarını yönetin' : 'Manage zone-based registration flows'}</p></div>
                {regLoading ? <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-gray-900 border-t-transparent rounded-full" /></div>
                : regSettings && (
                  <div className="space-y-4">
                    {[
                      { zone: 'blue_zone', label: 'Blue Zone', color: 'blue', icon: '🌊', desc: tr ? 'Ülke temsilcileri (ülke başına 1)' : 'Country representatives (1 per country)' },
                      { zone: 'green_zone', label: 'Green Zone', color: 'emerald', icon: '🌿', desc: tr ? 'Şirket kayıtları (sınırsız)' : 'Company registrations (unlimited)' },
                      { zone: 'supplier', label: tr ? 'Tedarikçi' : 'Supplier', color: 'orange', icon: '📦', desc: tr ? 'Tedarikçi başvuruları' : 'Supplier applications' },
                    ].map(({ zone, label, color, icon, desc }) => (
                      <Card key={zone} className="border-0 shadow-sm rounded-2xl">
                        <CardContent className="pt-5 pb-5">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{icon}</span>
                              <div><p className="font-bold text-gray-900">{label}</p><p className="text-xs text-gray-400">{desc}</p></div>
                            </div>
                            <button
                              onClick={async () => {
                                const updated = {...regSettings, [`${zone}_open`]: !regSettings[`${zone}_open`]};
                                setRegSettings(updated);
                                try {
                                  await apiClient.put(`/ultra-admin/registration-settings`, {
                                    blue_zone_open: updated.blue_zone_open,
                                    green_zone_open: updated.green_zone_open,
                                    supplier_open: updated.supplier_open,
                                  });
                                } catch { setRegSettings(regSettings); } // revert on error
                              }}
                              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                              style={{ backgroundColor: regSettings[`${zone}_open`] ? (color === 'blue' ? '#3b82f6' : color === 'emerald' ? '#10b981' : '#f97316') : '#d1d5db' }}>
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${regSettings[`${zone}_open`] ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              { key: 'pending', label: tr ? 'Bekliyor' : 'Pending', bg: 'bg-yellow-50', text: 'text-yellow-700' },
                              { key: 'approved', label: tr ? 'Onaylı' : 'Approved', bg: 'bg-green-50', text: 'text-green-700' },
                              { key: 'rejected', label: tr ? 'Reddedildi' : 'Rejected', bg: 'bg-red-50', text: 'text-red-700' },
                            ].map(({ key, label: lbl, bg, text }) => (
                              <div key={key} className={`${bg} rounded-xl p-3 text-center`}>
                                <p className={`text-xl font-bold ${text}`}>{regSettings[`${zone}_${key}`] ?? 0}</p>
                                <p className={`text-xs ${text} opacity-70`}>{lbl}</p>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── UNFCCC Email List ── */}
            {activeSection === 'unfccc' && <UnfcccEmailList tr={tr} />}

            {/* ── Phase 86I — CDN (CloudFront) Configuration ── */}
            {activeSection === 'cdn-config' && <CdnConfigSection tr={tr} />}

            {/* ── Stand Seçenekleri ── */}
            {activeSection === 'stand-options' && (
              <div className="max-w-xl space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{tr ? 'Stand Seçenekleri' : 'Stand Options'}</h2>
                  <p className="text-sm text-gray-400 mt-0.5">{tr ? 'Modüler hazır stand için alan (m²) seçeneklerini yönetin.' : 'Manage area (m²) options for modular ready stands.'}</p>
                </div>

                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <SlidersHorizontal className="h-4 w-4 text-blue-600" />
                      {tr ? 'Modüler Stand Alan Seçenekleri' : 'Modular Stand Area Options'}
                    </CardTitle>
                    <p className="text-xs text-gray-400">
                      {tr
                        ? 'Onboarding wizard\'ında "Modüler Hazır Stand" seçildiğinde bu m² seçenekleri gösterilir.'
                        : 'These m² options are shown when "Modular Ready Stand" is selected in the onboarding wizard.'}
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Mevcut seçenekler */}
                    <div className="flex flex-wrap gap-2">
                      {modularAreas.map(area => (
                        <span key={area} className="flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-xl px-3.5 py-2 text-sm font-semibold">
                          {area} m²
                          <button
                            onClick={() => handleRemoveArea(area)}
                            disabled={savingStandOpts}
                            className="text-blue-400 hover:text-red-500 transition-colors ml-0.5 disabled:opacity-40"
                            title={tr ? 'Kaldır' : 'Remove'}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ))}
                      {modularAreas.length === 0 && (
                        <p className="text-sm text-gray-400 italic">{tr ? 'Henüz seçenek yok.' : 'No options yet.'}</p>
                      )}
                    </div>

                    {/* Yeni seçenek ekle */}
                    <div className="flex gap-2 pt-2 border-t border-gray-100">
                      <div className="flex gap-0 items-stretch flex-1">
                        <input
                          type="number"
                          min="1"
                          max="9999"
                          value={newArea}
                          onChange={e => setNewArea(e.target.value)}
                          placeholder={tr ? 'Yeni m² değeri (örn: 200)' : 'New m² value (e.g. 200)'}
                          onKeyDown={e => e.key === 'Enter' && handleAddArea()}
                          className="flex-1 border border-r-0 border-gray-200 rounded-l-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                        />
                        <span className="bg-gray-50 border border-gray-200 rounded-r-xl px-4 flex items-center text-sm font-semibold text-gray-500">m²</span>
                      </div>
                      <Button
                        onClick={handleAddArea}
                        disabled={!newArea || savingStandOpts}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl gap-2 shrink-0"
                      >
                        {savingStandOpts
                          ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          : <Plus className="h-4 w-4" />}
                        {tr ? 'Ekle' : 'Add'}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400">
                      {tr
                        ? 'Enter\'a basarak veya "Ekle" butonuna tıklayarak ekleyebilirsiniz. Seçenekler otomatik sıralanır.'
                        : 'Press Enter or click "Add" to add options. Options are automatically sorted.'}
                    </p>
                  </CardContent>
                </Card>

                {/* Custom Stand Area Options */}
                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <SlidersHorizontal className="h-4 w-4 text-purple-600" />
                      {tr ? 'Özel Tasarım Alan Seçenekleri' : 'Custom Design Area Options'}
                    </CardTitle>
                    <p className="text-xs text-gray-400">
                      {tr
                        ? 'Katılımcı "Özel Tasarım" seçince bu m² seçenekleri gösterilir.'
                        : 'These m² options appear when the participant selects "Custom Design".'}
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      {customAreas.map(area => (
                        <span key={area} className="flex items-center gap-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-xl px-3.5 py-2 text-sm font-semibold">
                          {area} m²
                          <button
                            onClick={() => handleRemoveCustomArea(area)}
                            disabled={savingStandOpts}
                            className="text-purple-400 hover:text-red-500 transition-colors ml-0.5 disabled:opacity-40"
                            title={tr ? 'Kaldır' : 'Remove'}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ))}
                      {customAreas.length === 0 && (
                        <p className="text-sm text-gray-400 italic">{tr ? 'Henüz seçenek yok.' : 'No options yet.'}</p>
                      )}
                    </div>

                    <div className="flex gap-2 pt-2 border-t border-gray-100">
                      <div className="flex gap-0 items-stretch flex-1">
                        <input
                          type="number"
                          min="1"
                          max="9999"
                          value={newCustomArea}
                          onChange={e => setNewCustomArea(e.target.value)}
                          placeholder={tr ? 'Yeni m² değeri (örn: 500)' : 'New m² value (e.g. 500)'}
                          onKeyDown={e => e.key === 'Enter' && handleAddCustomArea()}
                          className="flex-1 border border-r-0 border-gray-200 rounded-l-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                        />
                        <span className="bg-gray-50 border border-gray-200 rounded-r-xl px-4 flex items-center text-sm font-semibold text-gray-500">m²</span>
                      </div>
                      <Button
                        onClick={handleAddCustomArea}
                        disabled={!newCustomArea || savingStandOpts}
                        className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl gap-2 shrink-0"
                      >
                        {savingStandOpts
                          ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          : <Plus className="h-4 w-4" />}
                        {tr ? 'Ekle' : 'Add'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* ── Categories ── */}
            {activeSection === 'categories' && (
              <div className="max-w-xl space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{tr ? 'Kategoriler' : 'Categories'}</h2>
                  <p className="text-sm text-gray-400 mt-0.5">{tr ? 'Kullanıcı ve tedarikçi kategorilerini yönetin' : 'Manage user and supplier categories'}</p>
                </div>
                {/* Tab toggle */}
                <div className="flex gap-2 flex-wrap">
                  {[
                    { key: 'user',       label: tr ? 'Kullanıcı Kategorileri' : 'User Categories' },
                    { key: 'supplier',   label: tr ? 'Tedarikçi Kategorileri' : 'Supplier Categories' },
                    { key: 'contractor', label: tr ? 'Yüklenici Kategorileri' : 'Contractor Categories' },
                    { key: 'profiles',   label: tr ? 'Ürün Profilleri' : 'Product Profiles' },
                  ].map(t => (
                    <button key={t.key} onClick={() => goCatTab(t.key)}
                      className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${catTab === t.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* ── User Categories tab ── */}
                {catTab === 'user' && (
                  <>
                    <Card className="border-0 shadow-sm rounded-2xl">
                      <CardContent className="pt-5 pb-5 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                              <span className="text-[10px] font-mono bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">TR</span>
                              {tr ? 'Türkçe Adı' : 'Turkish name'}
                            </Label>
                            <Input data-testid="cat-name-tr-input" placeholder={tr ? 'Örn. Devlet Kurumu' : 'e.g. Devlet Kurumu'} value={catForm.name_tr} onChange={e => { setCatForm(p => ({ ...p, name_tr: e.target.value })); setCatError(''); }} onKeyDown={e => e.key === 'Enter' && handleAddCategory()} className="rounded-xl" />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                              <span className="text-[10px] font-mono bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">EN</span>
                              {tr ? 'İngilizce Adı' : 'English name'}
                            </Label>
                            <Input data-testid="cat-name-en-input" placeholder={tr ? 'Örn. Government' : 'e.g. Government'} value={catForm.name_en} onChange={e => { setCatForm(p => ({ ...p, name_en: e.target.value })); setCatError(''); }} onKeyDown={e => e.key === 'Enter' && handleAddCategory()} className="rounded-xl" />
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <Button data-testid="cat-add-btn" onClick={handleAddCategory} className="bg-gray-900 hover:bg-gray-800 text-white rounded-xl gap-2"><Plus className="h-4 w-4" />{tr ? 'Ekle' : 'Add'}</Button>
                        </div>
                        {catError && <p className="text-red-500 text-xs">{catError}</p>}
                      </CardContent>
                    </Card>
                    <Card className="border-0 shadow-sm rounded-2xl">
                      <CardContent className="pt-4 pb-4">
                        {catLoading ? <div className="text-center py-8"><div className="animate-spin h-7 w-7 border-4 border-gray-900 border-t-transparent rounded-full mx-auto" /></div>
                        : categories.length === 0 ? <div className="text-center py-10 text-gray-400 text-sm">{tr ? 'Kategori bulunamadı.' : 'No categories found.'}</div>
                        : <div className="space-y-2">{categories.map((cat, catIdx) => (
                            <div key={cat.id} className="flex items-center justify-between px-4 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                              {editingCat?.id === cat.id ? (
                                <div className="flex-1 grid grid-cols-2 gap-2 mr-2">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] font-mono bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">TR</span>
                                    <Input data-testid={`cat-edit-tr-${cat.id}`} value={editingCat.name_tr || ''} onChange={e => setEditingCat({...editingCat, name_tr: e.target.value})} className="h-8 rounded-lg text-sm flex-1" onKeyDown={e => e.key === 'Enter' && handleUpdateCategory(cat.id)} autoFocus />
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] font-mono bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">EN</span>
                                    <Input data-testid={`cat-edit-en-${cat.id}`} value={editingCat.name_en || ''} onChange={e => setEditingCat({...editingCat, name_en: e.target.value})} className="h-8 rounded-lg text-sm flex-1" onKeyDown={e => e.key === 'Enter' && handleUpdateCategory(cat.id)} />
                                    <Button size="sm" onClick={() => handleUpdateCategory(cat.id)} className="bg-purple-700 text-white rounded-lg h-8"><Check className="h-4 w-4" /></Button>
                                    <Button size="sm" variant="ghost" onClick={() => setEditingCat(null)} className="rounded-lg h-8"><X className="h-4 w-4" /></Button>
                                  </div>
                                </div>
                              ) : (
                                <span className="font-medium text-gray-800 text-sm flex-1 flex items-center">
                                  {pickName(cat) || <span className="italic text-gray-400">—</span>}
                                  <MissingLangBadge item={cat} />
                                </span>
                              )}
                              {editingCat?.id !== cat.id && <div className="flex gap-1 items-center">
                                {/* Up/Down */}
                                <Button size="sm" variant="ghost" onClick={() => handleMoveCategory(catIdx, -1)} disabled={catIdx === 0} className="text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg h-7 w-7 p-0 disabled:opacity-25"><ChevronUp className="h-3.5 w-3.5" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => handleMoveCategory(catIdx, 1)} disabled={catIdx === categories.length - 1} className="text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg h-7 w-7 p-0 disabled:opacity-25"><ChevronDown className="h-3.5 w-3.5" /></Button>
                                <div className="w-px h-4 bg-gray-200 mx-0.5" />
                                <Button size="sm" variant="ghost" onClick={() => setEditingCat({ id: cat.id, name_tr: cat.name_tr || cat.name || '', name_en: cat.name_en || cat.name || '' })} className="text-blue-500 hover:bg-blue-50 rounded-lg h-8 w-8 p-0"><Pencil className="h-3.5 w-3.5" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDeleteCategory(cat.id)} className="text-red-500 hover:bg-red-50 rounded-lg h-8 w-8 p-0"><Trash2 className="h-3.5 w-3.5" /></Button>
                              </div>}
                            </div>
                          ))}</div>}
                      </CardContent>
                    </Card>
                  </>
                )}

                {/* ── Supplier Categories tab ── */}
                {catTab === 'supplier' && (
                  <>
                    <Card className="border-0 shadow-sm rounded-2xl">
                      <CardContent className="pt-5 pb-5 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                              <span className="text-[10px] font-mono bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">TR</span>
                              {tr ? 'Türkçe Adı' : 'Turkish name'}
                            </Label>
                            <Input data-testid="supplier-cat-name-tr-input" placeholder={tr ? 'Örn. Mobilya Kiralama' : 'e.g. Mobilya Kiralama'} value={supplierCatForm.name_tr} onChange={e => { setSupplierCatForm(p => ({ ...p, name_tr: e.target.value })); setSupplierCatError(''); }} onKeyDown={e => e.key === 'Enter' && handleAddSupplierCategory()} className="rounded-xl" />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                              <span className="text-[10px] font-mono bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">EN</span>
                              {tr ? 'İngilizce Adı' : 'English name'}
                            </Label>
                            <Input data-testid="supplier-cat-name-en-input" placeholder={tr ? 'Örn. Furniture Rental' : 'e.g. Furniture Rental'} value={supplierCatForm.name_en} onChange={e => { setSupplierCatForm(p => ({ ...p, name_en: e.target.value })); setSupplierCatError(''); }} onKeyDown={e => e.key === 'Enter' && handleAddSupplierCategory()} className="rounded-xl" />
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <Button data-testid="supplier-cat-add-btn" onClick={handleAddSupplierCategory} className="bg-gray-900 hover:bg-gray-800 text-white rounded-xl gap-2"><Plus className="h-4 w-4" />{tr ? 'Ekle' : 'Add'}</Button>
                        </div>
                        {supplierCatError && <p className="text-red-500 text-xs">{supplierCatError}</p>}
                      </CardContent>
                    </Card>
                    <Card className="border-0 shadow-sm rounded-2xl">
                      <CardContent className="pt-4 pb-4">
                        {supplierCatLoading ? <div className="text-center py-8"><div className="animate-spin h-7 w-7 border-4 border-gray-900 border-t-transparent rounded-full mx-auto" /></div>
                        : supplierCats.length === 0 ? <div className="text-center py-10 text-gray-400 text-sm">{tr ? 'Kategori bulunamadı.' : 'No categories found.'}</div>
                        : <div className="space-y-2">{supplierCats.map((cat, catIdx) => {
                            const isExpanded = !!expandedCats[cat.id];
                            const subs = cat.subcategories || [];
                            return (
                              <div key={cat.id} className="rounded-xl border border-amber-100 overflow-hidden">
                                {/* Category row */}
                                <div className="flex items-center justify-between px-4 py-3 bg-amber-50 hover:bg-amber-100 transition-colors">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <button onClick={() => toggleCatExpand(cat.id)} className="text-amber-600 hover:text-amber-800 flex-shrink-0" title={isExpanded ? (tr ? 'Daralt' : 'Collapse') : (tr ? 'Genişlet / Alt Kategoriler' : 'Expand / Subcategories')}>
                                      <ChevronRight className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                    </button>
                                    {editingSupplierCat?.id === cat.id ? (
                                      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-[10px] font-mono bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">TR</span>
                                          <Input data-testid={`supplier-cat-edit-tr-${cat.id}`} value={editingSupplierCat.name_tr || ''} onChange={e => setEditingSupplierCat({...editingSupplierCat, name_tr: e.target.value})} className="h-8 rounded-lg text-sm flex-1" onKeyDown={e => e.key === 'Enter' && handleUpdateSupplierCategory(cat.id)} autoFocus />
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-[10px] font-mono bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">EN</span>
                                          <Input data-testid={`supplier-cat-edit-en-${cat.id}`} value={editingSupplierCat.name_en || ''} onChange={e => setEditingSupplierCat({...editingSupplierCat, name_en: e.target.value})} className="h-8 rounded-lg text-sm flex-1" onKeyDown={e => e.key === 'Enter' && handleUpdateSupplierCategory(cat.id)} />
                                          <Button size="sm" onClick={() => handleUpdateSupplierCategory(cat.id)} className="bg-purple-700 text-white rounded-lg h-8"><Check className="h-4 w-4" /></Button>
                                          <Button size="sm" variant="ghost" onClick={() => setEditingSupplierCat(null)} className="rounded-lg h-8"><X className="h-4 w-4" /></Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        <span className="font-medium text-amber-900 text-sm flex-1 flex items-center">
                                          {pickName(cat) || <span className="italic text-amber-300">—</span>}
                                          <MissingLangBadge item={cat} />
                                        </span>
                                        {cat.code && (
                                          <span className="text-[10px] font-mono font-bold text-amber-600 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded mr-1">{cat.code}</span>
                                        )}
                                        {subs.length > 0 && <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-medium">{subs.length} {tr ? 'alt' : 'sub'}</span>}
                                      </>
                                    )}
                                  </div>
                                  {editingSupplierCat?.id !== cat.id && <div className="flex gap-1 flex-shrink-0 ml-2 items-center">
                                    {/* Up/Down */}
                                    <Button size="sm" variant="ghost" onClick={() => handleMoveSupplierCat(catIdx, -1)} disabled={catIdx === 0} className="text-amber-400 hover:text-amber-700 hover:bg-amber-200 rounded-lg h-7 w-7 p-0 disabled:opacity-25"><ChevronUp className="h-3.5 w-3.5" /></Button>
                                    <Button size="sm" variant="ghost" onClick={() => handleMoveSupplierCat(catIdx, 1)} disabled={catIdx === supplierCats.length - 1} className="text-amber-400 hover:text-amber-700 hover:bg-amber-200 rounded-lg h-7 w-7 p-0 disabled:opacity-25"><ChevronDown className="h-3.5 w-3.5" /></Button>
                                    <div className="w-px h-4 bg-amber-200 mx-0.5" />
                                    <Button size="sm" variant="ghost" onClick={() => setEditingSupplierCat({ id: cat.id, name_tr: cat.name_tr || cat.name || '', name_en: cat.name_en || cat.name || '' })} className="text-blue-500 hover:bg-blue-50 rounded-lg h-8 w-8 p-0"><Pencil className="h-3.5 w-3.5" /></Button>
                                    <Button size="sm" variant="ghost" onClick={() => handleDeleteSupplierCategory(cat.id)} className="text-red-500 hover:bg-red-50 rounded-lg h-8 w-8 p-0"><Trash2 className="h-3.5 w-3.5" /></Button>
                                  </div>}
                                </div>

                                {/* Subcategories panel */}
                                {isExpanded && (
                                  <div className="bg-white border-t border-amber-100 px-4 py-3 space-y-2">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{tr ? 'Alt Kategoriler' : 'Subcategories'}</p>
                                    {subs.map((sub, subIdx) => (
                                      <React.Fragment key={sub.id}>
                                      <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-amber-50/60 hover:bg-amber-50 transition-colors">
                                        {editingSub?.id === sub.id ? (
                                          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                                            <div className="flex items-center gap-1">
                                              <span className="text-[10px] font-mono bg-rose-100 text-rose-700 px-1 py-0.5 rounded">TR</span>
                                              <Input value={editingSub.name_tr || ''} onChange={e => setEditingSub({...editingSub, name_tr: e.target.value})} className="h-7 rounded-lg text-xs flex-1" onKeyDown={e => e.key === 'Enter' && handleUpdateSubCategory(cat.id, sub.id)} autoFocus />
                                            </div>
                                            <div className="flex items-center gap-1">
                                              <span className="text-[10px] font-mono bg-sky-100 text-sky-700 px-1 py-0.5 rounded">EN</span>
                                              <Input value={editingSub.name_en || ''} onChange={e => setEditingSub({...editingSub, name_en: e.target.value})} className="h-7 rounded-lg text-xs flex-1" onKeyDown={e => e.key === 'Enter' && handleUpdateSubCategory(cat.id, sub.id)} />
                                              <Button size="sm" onClick={() => handleUpdateSubCategory(cat.id, sub.id)} className="bg-purple-700 text-white rounded-lg h-7 px-2"><Check className="h-3.5 w-3.5" /></Button>
                                              <Button size="sm" variant="ghost" onClick={() => setEditingSub(null)} className="rounded-lg h-7 px-2"><X className="h-3.5 w-3.5" /></Button>
                                            </div>
                                          </div>
                                        ) : (
                                          <>
                                            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                              {sub.code && (
                                                <span className="text-[10px] font-mono font-bold text-blue-600 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded shrink-0">{sub.code}</span>
                                              )}
                                              <span className="text-sm text-amber-800 flex items-center">
                                                ↳ {pickName(sub) || <span className="italic text-amber-300">—</span>}
                                                <MissingLangBadge item={sub} />
                                              </span>
                                            </div>
                                            <div className="flex gap-1 items-center">
                                              {/* Template expand */}
                                              <button
                                                onClick={() => setExpandedSubs(p => ({ ...p, [sub.id]: !p[sub.id] }))}
                                                title={tr ? 'Ürün Şablonları' : 'Product Templates'}
                                                className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold transition-all ${expandedSubs[sub.id] ? 'bg-blue-500 text-white' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                                              >
                                                <Package className="h-2.5 w-2.5" />
                                                {(sub.product_templates || []).length}
                                              </button>
                                              {/* Up/Down for subcategories */}
                                              <Button size="sm" variant="ghost" onClick={() => handleMoveSubCat(cat.id, subIdx, -1)} disabled={subIdx === 0} className="text-amber-300 hover:text-amber-600 rounded-lg h-6 w-6 p-0 disabled:opacity-25"><ChevronUp className="h-3 w-3" /></Button>
                                              <Button size="sm" variant="ghost" onClick={() => handleMoveSubCat(cat.id, subIdx, 1)} disabled={subIdx === subs.length - 1} className="text-amber-300 hover:text-amber-600 rounded-lg h-6 w-6 p-0 disabled:opacity-25"><ChevronDown className="h-3 w-3" /></Button>
                                              <div className="w-px h-3.5 bg-amber-200 mx-0.5" />
                                              <Button size="sm" variant="ghost" onClick={() => setEditingSub({ id: sub.id, name_tr: sub.name_tr || sub.name || '', name_en: sub.name_en || sub.name || '', parent_id: cat.id })} className="text-blue-400 hover:bg-blue-50 rounded-lg h-7 w-7 p-0"><Pencil className="h-3 w-3" /></Button>
                                              <Button size="sm" variant="ghost" onClick={() => handleDeleteSubCategory(cat.id, sub.id)} className="text-red-400 hover:bg-red-50 rounded-lg h-7 w-7 p-0"><Trash2 className="h-3 w-3" /></Button>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                      {/* Product templates panel */}
                                      {expandedSubs[sub.id] && (
                                        <div className="ml-6 mt-1 mb-2 bg-blue-50 border border-blue-100 rounded-xl px-3 py-3 space-y-2">
                                          <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wide">
                                            {tr ? 'Ürün Şablonları' : 'Product Templates'}
                                            <span className="ml-1 text-blue-400 font-normal">(tedarikçi formunda dropdown olarak görünür)</span>
                                          </p>
                                          {(sub.product_templates || []).length === 0 && (
                                            <p className="text-xs text-blue-400 italic">{tr ? 'Henüz şablon eklenmedi.' : 'No templates yet.'}</p>
                                          )}
                                          {(sub.product_templates || []).map((tpl, tplIdx) => (
                                            <div key={tpl} className="bg-white rounded-lg px-3 py-2 border border-blue-100 space-y-1.5">
                                              {editingTpl?.subId === sub.id && editingTpl?.oldName === tpl ? (
                                                /* ── Düzenleme modu ── */
                                                <div className="flex gap-2">
                                                  <Input
                                                    value={editingTpl.newName}
                                                    onChange={e => setEditingTpl(p => ({ ...p, newName: e.target.value }))}
                                                    onKeyDown={e => { if (e.key === 'Enter') handleUpdateTemplate(); if (e.key === 'Escape') setEditingTpl(null); }}
                                                    className="h-7 rounded-lg text-xs flex-1"
                                                    autoFocus
                                                  />
                                                  <Button size="sm" onClick={handleUpdateTemplate} className="bg-purple-700 text-white rounded-lg h-7 px-2"><Check className="h-3.5 w-3.5" /></Button>
                                                  <Button size="sm" variant="ghost" onClick={() => setEditingTpl(null)} className="rounded-lg h-7 px-2"><X className="h-3.5 w-3.5" /></Button>
                                                </div>
                                              ) : (
                                                /* ── Normal görünüm ── */
                                                <div className="flex items-center justify-between">
                                                  <div className="flex items-center gap-1.5">
                                                    {(sub.template_codes || {})[tpl] && (
                                                      <span className="text-[10px] font-mono font-bold text-purple-600 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded shrink-0">{(sub.template_codes || {})[tpl]}</span>
                                                    )}
                                                    <span className="text-xs text-blue-800 font-medium">• {tpl}</span>
                                                  </div>
                                                  <div className="flex items-center gap-0.5">
                                                    {/* Yukarı / Aşağı */}
                                                    <Button size="sm" variant="ghost" onClick={() => handleMoveTemplate(cat.id, sub.id, tplIdx, -1)} disabled={tplIdx === 0} className="text-blue-300 hover:text-blue-600 rounded h-6 w-6 p-0 disabled:opacity-20"><ChevronUp className="h-3 w-3" /></Button>
                                                    <Button size="sm" variant="ghost" onClick={() => handleMoveTemplate(cat.id, sub.id, tplIdx, 1)} disabled={tplIdx === (sub.product_templates || []).length - 1} className="text-blue-300 hover:text-blue-600 rounded h-6 w-6 p-0 disabled:opacity-20"><ChevronDown className="h-3 w-3" /></Button>
                                                    {/* Düzenle / Sil */}
                                                    <button
                                                      onClick={() => setEditingTpl({ catId: cat.id, subId: sub.id, oldName: tpl, newName: tpl })}
                                                      className="text-blue-400 hover:text-blue-600 ml-1"
                                                      title={tr ? 'Düzenle' : 'Edit'}
                                                    ><Pencil className="h-3 w-3" /></button>
                                                    <button onClick={() => handleRemoveTemplate(cat.id, sub.id, tpl)} className="text-red-400 hover:text-red-600 ml-1"><X className="h-3 w-3" /></button>
                                                  </div>
                                                </div>
                                              )}
                                              {/* Profil Atama */}
                                              <div className="flex items-center gap-2">
                                                <select
                                                  value={(sub.template_profile_map || {})[tpl] || ''}
                                                  onChange={e => handleSetTemplateProfile(cat.id, sub.id, tpl, e.target.value)}
                                                  className="text-[11px] border border-blue-100 rounded-lg px-2 py-1 bg-white text-gray-600 flex-1"
                                                >
                                                  <option value="">— Profil Seç</option>
                                                  {productProfiles.map(pr => (
                                                    <option key={pr.id} value={pr.id}>{pr.name}</option>
                                                  ))}
                                                </select>
                                                {(sub.template_profile_map || {})[tpl] && (
                                                  <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-medium shrink-0">✓ Atandı</span>
                                                )}
                                              </div>
                                              {/* Renk/Desen Atama */}
                                              <div className="flex items-center gap-2 mt-1">
                                                <button
                                                  onClick={() => {
                                                    const currentIds = (sub.template_pattern_map || {})[tpl] || [];
                                                    setPatternPicker({ catId: cat.id, subId: sub.id, tplName: tpl });
                                                    setPickerSelected(new Set(currentIds));
                                                  }}
                                                  className="flex items-center gap-1.5 text-[11px] border border-purple-200 text-purple-600 rounded-lg px-2 py-1 hover:bg-purple-50 transition-colors"
                                                  title={tr ? 'Renk/Desen Ata' : 'Assign Patterns'}
                                                >
                                                  <Palette className="h-3 w-3" />
                                                  {tr ? 'Renk/Desen Ata' : 'Assign Colors'}
                                                </button>
                                                {((sub.template_pattern_map || {})[tpl] || []).length > 0 && (
                                                  <span className="text-[10px] font-medium bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                                    {((sub.template_pattern_map || {})[tpl] || []).length} {tr ? 'renk' : 'colors'}
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                          {tplErrors[sub.id] && <p className="text-red-500 text-xs">{tplErrors[sub.id]}</p>}
                                          <div className="flex gap-2 pt-1">
                                            <Input
                                              placeholder={tr ? 'Yeni ürün şablonu...' : 'New product template...'}
                                              value={newTplForms[sub.id] || ''}
                                              onChange={e => setNewTplForms(p => ({ ...p, [sub.id]: e.target.value }))}
                                              onKeyDown={e => e.key === 'Enter' && handleAddTemplate(cat.id, sub.id)}
                                              className="rounded-lg h-7 text-xs flex-1"
                                            />
                                            <Button size="sm" onClick={() => handleAddTemplate(cat.id, sub.id)} className="bg-blue-500 hover:bg-blue-600 text-white rounded-lg h-7 px-2 gap-1">
                                              <Plus className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                      </React.Fragment>
                                    ))}
                                    {subErrors[cat.id] && <p className="text-red-500 text-xs">{subErrors[cat.id]}</p>}
                                    {/* Add subcategory input */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-[10px] font-mono bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">TR</span>
                                        <Input
                                          placeholder={tr ? 'Türkçe alt kategori...' : 'Turkish name...'}
                                          value={(typeof newSubForms[cat.id] === 'object' ? newSubForms[cat.id]?.name_tr : '') || ''}
                                          onChange={e => {
                                            const cur = typeof newSubForms[cat.id] === 'object' ? newSubForms[cat.id] : { name_tr: '', name_en: '' };
                                            setSubForm(cat.id, { ...cur, name_tr: e.target.value });
                                            clearSubError(cat.id);
                                          }}
                                          onKeyDown={e => e.key === 'Enter' && handleAddSubCategory(cat.id)}
                                          className="rounded-lg h-8 text-sm flex-1"
                                        />
                                      </div>
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-[10px] font-mono bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">EN</span>
                                        <Input
                                          placeholder={tr ? 'İngilizce alt kategori...' : 'English name...'}
                                          value={(typeof newSubForms[cat.id] === 'object' ? newSubForms[cat.id]?.name_en : '') || ''}
                                          onChange={e => {
                                            const cur = typeof newSubForms[cat.id] === 'object' ? newSubForms[cat.id] : { name_tr: '', name_en: '' };
                                            setSubForm(cat.id, { ...cur, name_en: e.target.value });
                                            clearSubError(cat.id);
                                          }}
                                          onKeyDown={e => e.key === 'Enter' && handleAddSubCategory(cat.id)}
                                          className="rounded-lg h-8 text-sm flex-1"
                                        />
                                        <Button size="sm" onClick={() => handleAddSubCategory(cat.id)} className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg h-8 gap-1 px-3">
                                          <Plus className="h-3.5 w-3.5" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}</div>}
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>
            )}

                {/* ── Contractor Categories tab ── */}
                {catTab === 'contractor' && (
                  <>
                    <Card className="border-0 shadow-sm rounded-2xl">
                      <CardContent className="pt-5 pb-5 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                              <span className="text-[10px] font-mono bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">TR</span>
                              {tr ? 'Türkçe Adı' : 'Turkish name'}
                            </Label>
                            <Input data-testid="contractor-cat-name-tr-input" placeholder={tr ? 'Örn. Tasarım Bürosu' : 'e.g. Tasarım Bürosu'} value={contractorCatForm.name_tr}
                              onChange={e => { setContractorCatForm(p => ({ ...p, name_tr: e.target.value })); setContractorCatError(''); }}
                              onKeyDown={e => e.key === 'Enter' && handleAddContractorCategory()} className="rounded-xl" />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                              <span className="text-[10px] font-mono bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">EN</span>
                              {tr ? 'İngilizce Adı' : 'English name'}
                            </Label>
                            <Input data-testid="contractor-cat-name-en-input" placeholder={tr ? 'Örn. Design Studio' : 'e.g. Design Studio'} value={contractorCatForm.name_en}
                              onChange={e => { setContractorCatForm(p => ({ ...p, name_en: e.target.value })); setContractorCatError(''); }}
                              onKeyDown={e => e.key === 'Enter' && handleAddContractorCategory()} className="rounded-xl" />
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <Button data-testid="contractor-cat-add-btn" onClick={handleAddContractorCategory} className="bg-orange-600 hover:bg-orange-700 text-white rounded-xl gap-2">
                            <Plus className="h-4 w-4" />{tr ? 'Ekle' : 'Add'}
                          </Button>
                        </div>
                        {contractorCatError && <p className="text-red-500 text-xs">{contractorCatError}</p>}
                      </CardContent>
                    </Card>
                    <Card className="border-0 shadow-sm rounded-2xl">
                      <CardContent className="pt-4 pb-4">
                        {contractorCats.length === 0
                          ? <div className="text-center py-10 text-gray-400 text-sm">{tr ? 'Henüz kategori yok.' : 'No categories yet.'}</div>
                          : <div className="space-y-2">{contractorCats.map(cat => (
                              <div key={cat.id} className="flex items-center justify-between px-4 py-3 rounded-xl bg-orange-50 border border-orange-100">
                                {editingContractorCat?.id === cat.id ? (
                                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2 mr-2">
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[10px] font-mono bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">TR</span>
                                      <Input value={editingContractorCat.name_tr || ''}
                                        onChange={e => setEditingContractorCat({...editingContractorCat, name_tr: e.target.value})}
                                        className="h-8 rounded-lg text-sm flex-1" autoFocus
                                        onKeyDown={e => e.key === 'Enter' && handleUpdateContractorCategory(cat.id)} />
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[10px] font-mono bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">EN</span>
                                      <Input value={editingContractorCat.name_en || ''}
                                        onChange={e => setEditingContractorCat({...editingContractorCat, name_en: e.target.value})}
                                        className="h-8 rounded-lg text-sm flex-1"
                                        onKeyDown={e => e.key === 'Enter' && handleUpdateContractorCategory(cat.id)} />
                                      <Button size="sm" onClick={() => handleUpdateContractorCategory(cat.id)} className="bg-orange-600 text-white rounded-lg h-8"><Check className="h-4 w-4" /></Button>
                                      <Button size="sm" variant="ghost" onClick={() => setEditingContractorCat(null)} className="rounded-lg h-8"><X className="h-4 w-4" /></Button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <span className="font-medium text-orange-900 text-sm flex-1 flex items-center">
                                      {pickName(cat) || <span className="italic text-orange-300">—</span>}
                                      <MissingLangBadge item={cat} />
                                    </span>
                                    <div className="flex gap-1">
                                      <button onClick={() => setEditingContractorCat({ id: cat.id, name_tr: cat.name_tr || cat.name || '', name_en: cat.name_en || cat.name || '' })}
                                        className="text-orange-400 hover:text-orange-600 px-2"><Pencil className="h-4 w-4" /></button>
                                      <button onClick={() => handleDeleteContractorCategory(cat.id)}
                                        className="text-red-400 hover:text-red-600 px-2"><Trash2 className="h-4 w-4" /></button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}</div>
                        }
                      </CardContent>
                    </Card>
                  </>
                )}

                {/* ── Product Profiles tab ── */}
                {catTab === 'profiles' && (
                  <div className="space-y-6">
                    {/* Builtin + custom profiles list */}
                    <Card className="border-0 shadow-sm rounded-2xl">
                      <CardContent className="pt-5 pb-5 space-y-3">
                        <p className="text-xs text-gray-400">
                          {tr
                            ? 'Profiller, tedarikçi ürün formunda fiyatlama hesaplama yöntemini belirler. Alt kategorideki şablonlara profil atayabilirsiniz.'
                            : 'Profiles define the pricing calculation in the supplier product form. Assign profiles to subcategory templates.'}
                        </p>
                        {profilesLoading ? (
                          <div className="py-8 flex justify-center"><div className="animate-spin h-6 w-6 border-4 border-gray-900 border-t-transparent rounded-full" /></div>
                        ) : (
                          <div className="space-y-2">
                            {productProfiles.map(p => (
                              <div key={p.id} className={`flex items-start justify-between px-4 py-3 rounded-xl border ${p.builtin ? 'bg-gray-50 border-gray-100' : 'bg-blue-50 border-blue-100'}`}>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-sm text-gray-800">{p.name}</span>
                                    {p.builtin && <span className="text-[10px] bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-full font-medium">Yerleşik</span>}
                                    <span className="text-[10px] bg-white text-gray-500 px-1.5 py-0.5 rounded-full border font-mono">{p.id}</span>
                                  </div>
                                  <p className="text-xs text-blue-600 font-mono mt-0.5">= {p.formula}</p>
                                  <p className="text-[11px] text-gray-400 mt-0.5">
                                    Hesaplama: <span className="font-medium text-gray-600">{p.calculation_type}</span>
                                    {p.pricing_mode && <span className="ml-2">· Mod: <span className="font-medium text-gray-600">{p.pricing_mode}</span></span>}
                                  </p>
                                </div>
                                {!p.builtin && (
                                  <Button size="sm" variant="ghost" onClick={() => handleDeleteProfile(p.id)} className="text-red-400 hover:bg-red-50 rounded-lg h-7 w-7 p-0 shrink-0 ml-2">
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Add new custom profile */}
                    <Card className="border-0 shadow-sm rounded-2xl">
                      <CardContent className="pt-5 pb-5 space-y-3">
                        <p className="text-sm font-semibold text-gray-700">{tr ? 'Özel Profil Ekle' : 'Add Custom Profile'}</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1 col-span-2">
                            <label className="text-xs text-gray-500 font-semibold uppercase">Profil Adı *</label>
                            <Input placeholder="ör. Kişi × Gün × Fiyat" value={newProfileForm.name}
                              onChange={e => setNewProfileForm(f => ({ ...f, name: e.target.value }))}
                              className="rounded-xl" />
                          </div>
                          <div className="space-y-1 col-span-2">
                            <label className="text-xs text-gray-500 font-semibold uppercase">Formül Açıklaması</label>
                            <Input placeholder="ör. Kişi Sayısı × Gün Sayısı × Birim Fiyat" value={newProfileForm.formula}
                              onChange={e => setNewProfileForm(f => ({ ...f, formula: e.target.value }))}
                              className="rounded-xl" />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-gray-500 font-semibold uppercase">Hesaplama Tipi *</label>
                            <select value={newProfileForm.calculation_type}
                              onChange={e => setNewProfileForm(f => ({ ...f, calculation_type: e.target.value }))}
                              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm">
                              <option value="flat">flat — Adet bazlı</option>
                              <option value="daily_based">daily_based — Günlük</option>
                              <option value="hr_based">hr_based — Kişi + Gün</option>
                              <option value="hourly">hourly — Saatlik (Forklift)</option>
                              <option value="carton_based">carton_based — Koli</option>
                              <option value="area_based">area_based — m²</option>
                              <option value="person_based">person_based — Kişi başı</option>
                              <option value="quantity_and_days">quantity_and_days — Adet + Gün</option>
                            </select>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-gray-500 font-semibold uppercase">Fiyat Modu (opsiyonel)</label>
                            <select value={newProfileForm.pricing_mode}
                              onChange={e => setNewProfileForm(f => ({ ...f, pricing_mode: e.target.value }))}
                              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm">
                              <option value="">— Seçiniz</option>
                              <option value="daily">Günlük (daily)</option>
                              <option value="event">Etkinlik (event)</option>
                            </select>
                          </div>
                        </div>
                        {profileError && <p className="text-red-500 text-xs">{profileError}</p>}
                        <Button onClick={handleAddProfile} className="bg-gray-900 hover:bg-gray-800 text-white rounded-xl gap-2 w-full">
                          <Plus className="h-4 w-4" />{tr ? 'Profil Ekle' : 'Add Profile'}
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                )}



            {/* ── Security Center ── */}
            {activeSection === 'security' && (
              <div className="max-w-xl space-y-6">
                <div><h2 className="text-xl font-bold text-gray-900">Security Center</h2><p className="text-sm text-gray-400 mt-0.5">{tr ? 'Giriş şifresi ve ultra password yönetimi' : 'Login password and ultra password management'}</p></div>

                {/* Login Password Change */}
                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Lock className="h-4 w-4 text-blue-600" />{tr ? 'Giriş Şifresini Değiştir' : 'Change Login Password'}</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    {loginPwSuccess ? (
                      <div className="bg-green-50 rounded-xl p-4 flex items-center gap-3"><CheckCircle className="h-5 w-5 text-green-600" /><p className="text-sm text-green-700 font-semibold">{tr ? 'Giriş şifresi güncellendi!' : 'Login password updated!'}</p></div>
                    ) : (
                      <>
                        <div className="space-y-1">
                          <Label>{tr ? 'Yeni Şifre' : 'New Password'}</Label>
                          <div className="relative">
                            <Input type={showNew ? 'text' : 'password'} value={loginPwForm.newp} onChange={e => { setLoginPwForm(f => ({...f, newp: e.target.value})); setLoginPwError(''); }} className="pr-10 rounded-xl" placeholder="••••••••" />
                            <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-3 text-gray-400">{showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label>{tr ? 'Yeni Şifre (Tekrar)' : 'Confirm New Password'}</Label>
                          <div className="relative">
                            <Input type={loginPwShowConfirm ? 'text' : 'password'} value={loginPwForm.confirm} onChange={e => { setLoginPwForm(f => ({...f, confirm: e.target.value})); setLoginPwError(''); }} className={`rounded-xl pr-10 ${loginPwForm.confirm && loginPwForm.newp !== loginPwForm.confirm ? 'border-red-400' : ''}`} placeholder="••••••••" />
                            <button type="button" onClick={() => setLoginPwShowConfirm(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors">{loginPwShowConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                          </div>
                          {loginPwForm.confirm && loginPwForm.newp !== loginPwForm.confirm && <p className="text-red-500 text-xs">{tr ? 'Şifreler eşleşmiyor' : 'Passwords do not match'}</p>}
                          {loginPwForm.confirm && loginPwForm.newp === loginPwForm.confirm && loginPwForm.newp && <p className="text-purple-700 text-xs">✓ {tr ? 'Şifreler eşleşiyor' : 'Passwords match'}</p>}
                        </div>
                        {loginPwError && <p className="text-red-600 text-sm bg-red-50 rounded-xl px-3 py-2">⚠ {loginPwError}</p>}
                        <Button onClick={handleChangeLoginPassword} disabled={loginPwLoading || (loginPwForm.confirm && loginPwForm.newp !== loginPwForm.confirm)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-full gap-2">
                          {loginPwLoading ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Lock className="h-4 w-4" />}
                          {tr ? 'Şifreyi Güncelle' : 'Update Password'}
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Ultra Password */}
                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardHeader><CardTitle className="flex items-center gap-2 text-base"><KeyRound className="h-4 w-4 text-red-600" />Ultra Password</CardTitle></CardHeader>
                  <CardContent>
                    {upStep === 'done' ? (
                      <div className="bg-green-50 rounded-xl p-4 flex items-center gap-3"><CheckCircle className="h-5 w-5 text-green-600" /><p className="text-sm text-green-700 font-semibold">{tr ? 'Ultra Password güncellendi!' : 'Ultra Password updated!'}</p></div>
                    ) : upStep === 'idle' ? (
                      <div className="space-y-4">
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-3">
                          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">{tr ? 'Yetkili Ultra Admin' : 'Authorized Ultra Admin'}</p>
                          <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm">
                            <div className="h-9 w-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                              <KeyRound className="h-4 w-4 text-red-600" />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-slate-800">mbucak@gmail.com</p>
                              <p className="text-xs text-slate-400 mt-0.5">{tr ? 'Sadece bu hesap Ultra Password değiştirebilir.' : 'Only this account can change the Ultra Password.'}</p>
                            </div>
                            <span className="ml-auto text-xs bg-red-100 text-red-700 font-semibold px-2.5 py-1 rounded-full">Protected</span>
                          </div>
                        </div>
                        {upError && <p className="text-red-600 text-sm">{upError}</p>}
                        <Button data-testid="request-up-change-btn" onClick={async () => { setUpLoading(true); setUpError(''); try { await apiClient.post(`/ultra-admin/system-settings/request-change-code`, {}); setUpStep('requesting'); } catch (e) { setUpError(e.response?.data?.detail || 'Hata'); } finally { setUpLoading(false); } }} disabled={upLoading} className="bg-red-600 hover:bg-red-700 text-white rounded-full gap-2">
                          {upLoading ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <><KeyRound className="h-4 w-4" />{tr ? 'Ultra Password Değiştir' : 'Change Ultra Password'}</>}
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="bg-blue-50 rounded-xl p-4 flex items-start gap-3">
                          <Mail className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-blue-800">{tr ? 'Doğrulama kodu gönderildi' : 'Verification code sent'}</p>
                            <p className="text-xs text-blue-600 mt-0.5">
                              {tr ? 'Kod şu adrese gönderildi:' : 'Code was sent to:'}{' '}
                              <span className="font-bold">mbucak@gmail.com</span>
                            </p>
                          </div>
                        </div>
                        <Input data-testid="up-code-input" placeholder="000000" value={upCode} onChange={e => { setUpCode(e.target.value.replace(/\D/g,'').slice(0,6)); setUpError(''); }} className="rounded-xl font-mono text-center text-xl tracking-widest" maxLength={6} />
                        <div className="relative"><Input data-testid="up-new-pass-input" type={upShowPass?'text':'password'} placeholder={tr?'Yeni şifre':'New password'} value={upNewPass} onChange={e=>{setUpNewPass(e.target.value);setUpError('');}} className="pr-10 rounded-xl" /><button type="button" onClick={()=>setUpShowPass(!upShowPass)} className="absolute right-3 top-3 text-gray-400">{upShowPass?<EyeOff className="h-4 w-4"/>:<Eye className="h-4 w-4"/>}</button></div>
                        <Input data-testid="up-confirm-pass-input" type="password" placeholder={tr?'Tekrar girin':'Re-enter'} value={upConfirmPass} onChange={e=>{setUpConfirmPass(e.target.value);setUpError('');}} className={`rounded-xl ${upConfirmPass&&upNewPass!==upConfirmPass?'border-red-400':''}`} />
                        {upError && <p className="text-red-600 text-sm">{upError}</p>}
                        <div className="flex gap-3">
                          <Button variant="outline" className="rounded-full" onClick={()=>{setUpStep('idle');setUpCode('');setUpNewPass('');setUpConfirmPass('');setUpError('');}}>{tr?'İptal':'Cancel'}</Button>
                          <Button data-testid="confirm-up-change-btn" onClick={async()=>{if(!upCode||!upNewPass){setUpError(tr?'Tüm alanları doldurun':'Fill all fields');return;}if(upNewPass!==upConfirmPass){setUpError(tr?'Şifreler eşleşmiyor':'Passwords do not match');return;}setUpLoading(true);setUpError('');try{await apiClient.post(`/ultra-admin/system-settings/change-ultra-password`,{code:upCode,new_password:upNewPass});setUpStep('done');}catch(e){setUpError(e.response?.data?.detail||'Hata');}finally{setUpLoading(false);}}} disabled={upLoading||upNewPass!==upConfirmPass} className="bg-red-600 hover:bg-red-700 text-white rounded-full">
                            {upLoading?<div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"/>:(tr?'Güncelle':'Update')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Production Safety Lock (Primary Super Admin only) */}
                {isPrimary && (
                  <Card className="border-0 shadow-sm rounded-2xl">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <AlertTriangle className="h-4 w-4 text-orange-600" />
                        {tr ? 'Production Güvenlik Kilidi' : 'Production Safety Lock'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                        <p className="text-sm text-orange-800 font-medium mb-2">
                          {tr ? '⚠️ Tehlikeli İşlemler' : '⚠️ Dangerous Operations'}
                        </p>
                        <p className="text-xs text-orange-700 leading-relaxed">
                          {tr 
                            ? 'Bu kilit, production ortamında yanlışlıkla tüm test verilerinin silinmesini önler. Kilidi açtığınızda "Reset Test Data" butonu aktif hale gelir.'
                            : 'This lock prevents accidental deletion of all test data in production. When unlocked, the "Reset Test Data" button becomes active.'}
                        </p>
                      </div>

                      <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${allowResetTestData ? 'bg-red-100' : 'bg-green-100'}`}>
                            {allowResetTestData ? (
                              <AlertTriangle className="h-5 w-5 text-red-600" />
                            ) : (
                              <Shield className="h-5 w-5 text-green-600" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              {tr ? 'Test Verisi Sıfırlama' : 'Test Data Reset'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {allowResetTestData 
                                ? (tr ? '🔓 Kilitsiz (Tehlikeli!)' : '🔓 Unlocked (Dangerous!)')
                                : (tr ? '🔒 Kilitli (Güvenli)' : '🔒 Locked (Safe)')}
                            </p>
                          </div>
                        </div>
                        
                        <button
                          onClick={handleToggleResetLock}
                          disabled={resetLockLoading}
                          className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                            allowResetTestData 
                              ? 'bg-red-600 focus:ring-red-500' 
                              : 'bg-gray-300 focus:ring-gray-400'
                          } ${resetLockLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <span
                            className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                              allowResetTestData ? 'translate-x-7' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {allowResetTestData && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                          <p className="text-xs text-red-700 flex items-center gap-2">
                            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                            {tr 
                              ? 'Uyarı: Kilit açık! Reset butonu şu anda kullanılabilir durumda. İşleminiz bittiğinde kilidi tekrar kapatmayı unutmayın.'
                              : 'Warning: Lock is open! Reset button is now active. Don\'t forget to lock it again after your operation.'}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* ── Email Templates ── */}
            {activeSection === 'emails' && (
              <div className="max-w-2xl space-y-6">
                <div className="flex items-center justify-between">
                  <div><h2 className="text-xl font-bold text-gray-900">{tr ? 'E-posta Şablonları' : 'Email Templates'}</h2><p className="text-sm text-gray-400 mt-0.5">{tr ? 'Otomatik gönderilen şablonları yönetin' : 'Manage automated email templates'}</p></div>
                  <Button onClick={() => { setEditTmpl(null); setIsNewTmpl(true); setShowTmplModal(true); }} className="bg-gray-900 hover:bg-gray-800 text-white rounded-full gap-2"><Plus className="h-4 w-4" />{tr ? 'Yeni Şablon' : 'New Template'}</Button>
                </div>
                {tmplLoading ? <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-gray-900 border-t-transparent rounded-full" /></div>
                : <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="pt-4 pb-4 space-y-3">
                    {templates.map(tmpl => (
                      <div key={tmpl.id} className="flex items-center justify-between p-4 border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-sm text-gray-900">{tr ? tmpl.name_tr : tmpl.name_en}</p>
                              {tmpl.zone && (
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ZONE_BADGE[tmpl.zone] || 'bg-gray-100 text-gray-600'}`}>
                                  {zoneLabel(tmpl.zone, tr)}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 truncate">{tr ? tmpl.subject_tr : tmpl.subject_en}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Badge className={tmpl.status === 'active' ? 'bg-green-100 text-green-700 rounded-full text-xs' : 'bg-gray-100 text-gray-700 rounded-full text-xs'}>{tmpl.status === 'active' ? (tr ? 'Aktif' : 'Active') : (tr ? 'Taslak' : 'Draft')}</Badge>
                          <Button size="sm" variant="ghost" onClick={() => { setEditTmpl(tmpl); setIsNewTmpl(false); setShowTmplModal(true); }} className="text-blue-500 hover:bg-blue-50 rounded-full h-8 w-8 p-0"><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteTemplate(tmpl.id)} className="text-red-500 hover:bg-red-50 rounded-full h-8 w-8 p-0"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-gray-400 bg-blue-50 rounded-xl p-3">{tr ? 'SendGrid API entegrasyonu ile e-postalar otomatik gönderilir.' : 'Emails are sent automatically via SendGrid API.'}</p>
                  </CardContent></Card>}
              </div>
            )}

            {/* ── Visitor Tracking ── */}
            {activeSection === 'visitors' && (
              <div className="space-y-6 max-w-4xl">
                <div className="flex items-center justify-between">
                  <div><h2 className="text-xl font-bold text-gray-900">{tr ? 'Ziyaretçi Takip' : 'Visitor Tracking'}</h2><p className="text-sm text-gray-400 mt-0.5">{tr ? 'Gerçek zamanlı site ziyaretçi analizi' : 'Real-time site visitor analytics'}</p></div>
                  <div className="flex items-center gap-3">
                    <select value={statsDays} onChange={e => setStatsDays(Number(e.target.value))} className="text-sm border rounded-xl px-3 py-1.5">
                      <option value={1}>{tr ? '1 Gün' : '1 Day'}</option>
                      <option value={7}>{tr ? '7 Gün' : '7 Days'}</option>
                      <option value={30}>{tr ? '30 Gün' : '30 Days'}</option>
                      <option value={90}>{tr ? '90 Gün' : '90 Days'}</option>
                    </select>
                    <Button size="sm" variant="outline" className="rounded-xl gap-1.5" onClick={fetchVisitorStats}><RefreshCw className="h-3.5 w-3.5" />{tr ? 'Yenile' : 'Refresh'}</Button>
                  </div>
                </div>
                {visitorLoading ? <div className="flex justify-center py-16"><div className="animate-spin h-10 w-10 border-4 border-gray-900 border-t-transparent rounded-full" /></div>
                : visitorStats ? (
                  <>
                    {/* Stats cards */}
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        { label: tr ? 'Toplam Ziyaret' : 'Total Visits', value: visitorStats.total_visits, icon: BarChart2, color: 'text-blue-600', bg: 'bg-blue-50' },
                        { label: tr ? 'Tekil Ziyaretçi' : 'Unique Visitors', value: visitorStats.unique_visitors, icon: Users, color: 'text-purple-700', bg: 'bg-purple-50' },
                        { label: 'VPN', value: visitorStats.vpn_count, icon: WifiOff, color: 'text-orange-600', bg: 'bg-orange-50' },
                      ].map(({ label, value, icon: Icon, color, bg }) => (
                        <Card key={label} className="border-0 shadow-sm rounded-2xl">
                          <CardContent className="pt-5 pb-5 flex items-center gap-4">
                            <div className={`h-12 w-12 rounded-2xl ${bg} flex items-center justify-center flex-shrink-0`}><Icon className={`h-6 w-6 ${color}`} /></div>
                            <div><p className="text-2xl font-bold text-gray-900">{value}</p><p className="text-xs text-gray-400">{label}</p></div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      {/* Top Countries */}
                      <Card className="border-0 shadow-sm rounded-2xl">
                        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><MapPin className="h-4 w-4 text-blue-500" />{tr ? 'Ülkelere Göre' : 'By Country'}</CardTitle></CardHeader>
                        <CardContent className="pt-0 space-y-2">
                          {visitorStats.top_countries.length === 0 ? <p className="text-xs text-gray-400 text-center py-4">{tr ? 'Veri yok' : 'No data'}</p>
                          : visitorStats.top_countries.map(({ country, count }) => {
                            const pct = visitorStats.total_visits ? Math.round(count / visitorStats.total_visits * 100) : 0;
                            return (
                              <div key={country} className="flex items-center gap-3">
                                <span className="text-xs text-gray-600 w-28 truncate">{country}</span>
                                <div className="flex-1 bg-gray-100 rounded-full h-2"><div className="bg-blue-500 h-2 rounded-full" style={{ width: `${pct}%` }} /></div>
                                <span className="text-xs font-semibold text-gray-700 w-12 text-right">{count} <span className="text-gray-400">({pct}%)</span></span>
                              </div>
                            );
                          })}
                        </CardContent>
                      </Card>

                      {/* Top URLs */}
                      <Card className="border-0 shadow-sm rounded-2xl">
                        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><ExternalLink className="h-4 w-4 text-purple-600" />{tr ? 'Sayfalar' : 'Pages'}</CardTitle></CardHeader>
                        <CardContent className="pt-0 space-y-2">
                          {visitorStats.top_urls.length === 0 ? <p className="text-xs text-gray-400 text-center py-4">{tr ? 'Veri yok' : 'No data'}</p>
                          : visitorStats.top_urls.map(({ url, count }) => (
                            <div key={url} className="flex items-center gap-3">
                              <span className="text-xs font-mono text-gray-600 flex-1 truncate">{url}</span>
                              <Badge className="rounded-full text-xs bg-purple-100 text-purple-700">{count}</Badge>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    </div>

                    {/* Recent visits table */}
                    <Card className="border-0 shadow-sm rounded-2xl">
                      <CardHeader><CardTitle className="text-sm">{tr ? 'Son Ziyaretler' : 'Recent Visits'}</CardTitle></CardHeader>
                      <CardContent className="pt-0 overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-gray-100">
                              {[tr?'Tarih/Saat':'Date/Time', 'IP', tr?'Ülke':'Country', 'URL', tr?'Süre':'Duration', 'VPN', tr?'Referans':'Referrer'].map(h => (
                                <th key={h} className="text-left py-2 px-3 text-gray-400 font-medium">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {visitorStats.recent_visits.slice(0, 20).map(v => (
                              <tr key={v.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                                <td className="py-2 px-3 text-gray-600 whitespace-nowrap">{fmtDate(v.timestamp)}</td>
                                <td className="py-2 px-3 font-mono text-gray-600">{v.ip}</td>
                                <td className="py-2 px-3">
                                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-gray-400" />{v.country || '?'}</span>
                                </td>
                                <td className="py-2 px-3 font-mono max-w-[160px] truncate text-blue-600">{v.url}</td>
                                <td className="py-2 px-3 text-gray-600">{v.duration ? `${v.duration}s` : '—'}</td>
                                <td className="py-2 px-3">
                                  {v.is_vpn ? <Badge className="rounded-full text-xs bg-orange-100 text-orange-700">VPN</Badge> : <span className="text-gray-300">—</span>}
                                </td>
                                <td className="py-2 px-3 max-w-[120px] truncate text-gray-400">{v.referrer || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {visitorStats.recent_visits.length === 0 && <p className="text-center text-gray-400 text-xs py-8">{tr ? 'Henüz ziyaret yok. Siteye bir sayfa ziyareti yapın.' : 'No visits yet. Browse a page to start tracking.'}</p>}
                      </CardContent>
                    </Card>
                  </>
                ) : null}
              </div>
            )}

            {/* ── System Logs ── */}
            {activeSection === 'units' && (
              <div className="max-w-2xl space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{tr ? 'Birim Yönetimi' : 'Unit Management'}</h2>
                  <p className="text-sm text-gray-400 mt-0.5">{tr ? 'Tedarikçi ürün/hizmet birimlerini yönetin.' : 'Manage units for supplier products/services.'}</p>
                </div>
                <Card className="rounded-2xl">
                  <CardContent className="pt-5 space-y-4">
                    <div className="flex gap-2">
                      <Input value={unitForm.name} onChange={e => setUnitForm(f => ({ ...f, name: e.target.value }))} placeholder={tr ? 'Birim Adı (Adet)' : 'Unit Name (Piece)'} className="rounded-xl flex-1" />
                      <Input value={unitForm.code} onChange={e => setUnitForm(f => ({ ...f, code: e.target.value }))} placeholder={tr ? 'Kod (adet)' : 'Code (pc)'} className="rounded-xl w-28" />
                      <Button onClick={handleAddUnit} className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl shrink-0">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {unitError && <p className="text-xs text-red-500">{unitError}</p>}
                    {unitLoading ? (
                      <div className="flex justify-center py-6"><div className="animate-spin h-6 w-6 border-2 border-amber-500 border-t-transparent rounded-full" /></div>
                    ) : (
                      <div className="space-y-1.5">
                        {units.map(u => (
                          <div key={u.id} className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5">
                            {editingUnit?.id === u.id ? (
                              <>
                                <Input value={editingUnit.name} onChange={e => setEditingUnit(x => ({ ...x, name: e.target.value }))} className="rounded-lg h-8 flex-1 text-sm" />
                                <Input value={editingUnit.code} onChange={e => setEditingUnit(x => ({ ...x, code: e.target.value }))} className="rounded-lg h-8 w-24 text-sm" />
                                <button onClick={() => handleUpdateUnit(u.id)} className="text-purple-700 hover:text-purple-700"><Check className="h-4 w-4" /></button>
                                <button onClick={() => setEditingUnit(null)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                              </>
                            ) : (
                              <>
                                <span className="flex-1 text-sm font-medium text-gray-800">{u.name}</span>
                                <span className="text-xs font-mono bg-white border rounded px-1.5 py-0.5 text-gray-500">{u.code}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${u.is_active ? 'bg-purple-50 text-purple-700' : 'bg-gray-200 text-gray-500'}`}>
                                  {u.is_active ? (tr ? 'Aktif' : 'Active') : (tr ? 'Pasif' : 'Inactive')}
                                </span>
                                <button onClick={() => setEditingUnit({ ...u })} className="text-blue-500 hover:text-blue-700"><Pencil className="h-3.5 w-3.5" /></button>
                                <button onClick={() => handleToggleUnit(u)} className={`${u.is_active ? 'text-amber-500 hover:text-amber-700' : 'text-purple-600 hover:text-purple-700'}`}>
                                  {u.is_active ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                                </button>
                                <button onClick={() => handleDeleteUnit(u.id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
                              </>
                            )}
                          </div>
                        ))}
                        {units.length === 0 && <p className="text-sm text-gray-400 text-center py-4">{tr ? 'Henüz birim yok.' : 'No units yet.'}</p>}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {activeSection === 'shopping' && (
              <div className="max-w-2xl space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{tr ? 'Alışveriş Ayarları' : 'Shopping Settings'}</h2>
                  <p className="text-sm text-gray-400 mt-0.5">{tr ? 'Sepet rezervasyonu, süre sonu ve stok uyarılarını yönetin' : 'Manage cart reservation, expiration and stock alerts'}</p>
                </div>

                {shoppingLoading ? (
                  <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-gray-900 border-t-transparent rounded-full" /></div>
                ) : (
                  <>
                    {/* Cart Reservation */}
                    <Card className="border-0 shadow-sm rounded-2xl">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base">
                          <ShoppingCart className="h-4 w-4 text-blue-600" />
                          {tr ? 'Sepet Rezervasyonu' : 'Cart Reservation'}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-5">
                        <div className="space-y-1.5">
                          <Label className="text-sm font-medium text-gray-700">
                            {tr ? 'Rezervasyon Süresi (Saat)' : 'Reservation Timeout (Hours)'}
                          </Label>
                          <p className="text-xs text-gray-400">{tr ? 'Ürün sepete eklendiğinde kaç saat boyunca stoktan ayrılsın?' : 'How many hours should product be held from stock after adding to cart?'}</p>
                          <div className="flex items-center gap-3">
                            <Input
                              data-testid="cart-timeout-input"
                              type="number"
                              min={1}
                              max={168}
                              value={shoppingSettings.cart_reservation_timeout_hours}
                              onChange={e => setShoppingSettings(s => ({ ...s, cart_reservation_timeout_hours: e.target.value }))}
                              className="rounded-xl w-32"
                            />
                            <span className="text-sm text-gray-500">{tr ? 'saat' : 'hours'}</span>
                            <span className="text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg font-medium">
                              {tr
                                ? `${Math.floor((parseInt(shoppingSettings.cart_reservation_timeout_hours) || 24) / 24) > 0 ? Math.floor((parseInt(shoppingSettings.cart_reservation_timeout_hours) || 24) / 24) + ' gün ' : ''}${(parseInt(shoppingSettings.cart_reservation_timeout_hours) || 24) % 24 > 0 ? ((parseInt(shoppingSettings.cart_reservation_timeout_hours) || 24) % 24) + ' saat' : ''}`
                                : `${Math.floor((parseInt(shoppingSettings.cart_reservation_timeout_hours) || 24) / 24) > 0 ? Math.floor((parseInt(shoppingSettings.cart_reservation_timeout_hours) || 24) / 24) + 'd ' : ''}${(parseInt(shoppingSettings.cart_reservation_timeout_hours) || 24) % 24 > 0 ? ((parseInt(shoppingSettings.cart_reservation_timeout_hours) || 24) % 24) + 'h' : ''}`
                              }
                            </span>
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-sm font-medium text-gray-700">
                            {tr ? 'Uyarı Maili (Saat Önce)' : 'Warning Email (Hours Before)'}
                          </Label>
                          <p className="text-xs text-gray-400">{tr ? 'Sepet sona ermeden kaç saat önce müşteriye uyarı maili gönderilsin?' : 'How many hours before cart expiry should the customer receive a warning email?'}</p>
                          <div className="flex items-center gap-3">
                            <Input
                              data-testid="cart-warning-input"
                              type="number"
                              min={1}
                              max={Math.max(1, (parseInt(shoppingSettings.cart_reservation_timeout_hours) || 24) - 1)}
                              value={shoppingSettings.cart_expiry_warning_hours_before}
                              onChange={e => setShoppingSettings(s => ({ ...s, cart_expiry_warning_hours_before: e.target.value }))}
                              className="rounded-xl w-32"
                            />
                            <span className="text-sm text-gray-500">{tr ? 'saat önce' : 'hours before'}</span>
                          </div>
                        </div>

                        {/* Info box */}
                        <div className="bg-blue-50 rounded-xl p-4 flex gap-3">
                          <Clock className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                          <div className="text-xs text-blue-700 leading-relaxed">
                            <p className="font-semibold mb-1">{tr ? 'Nasıl Çalışır?' : 'How It Works?'}</p>
                            <p>{tr
                              ? `Müşteri sepete ürün eklediğinde, stok ${shoppingSettings.cart_reservation_timeout_hours || 24} saat boyunca rezerve edilir. Süre dolmadan ${shoppingSettings.cart_expiry_warning_hours_before || 1} saat önce müşteriye uyarı maili gider. Ödeme yapılmazsa stok otomatik serbest bırakılır.`
                              : `When a customer adds a product to cart, stock is reserved for ${shoppingSettings.cart_reservation_timeout_hours || 24} hours. A warning email is sent ${shoppingSettings.cart_expiry_warning_hours_before || 1} hour(s) before expiry. If no payment is made, stock is automatically released.`
                            }</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Stock Alert */}
                    <Card className="border-0 shadow-sm rounded-2xl">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base">
                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                          {tr ? 'Düşük Stok Uyarısı' : 'Low Stock Alert'}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-5">
                        <div className="space-y-1.5">
                          <Label className="text-sm font-medium text-gray-700">
                            {tr ? 'Düşük Stok Eşiği (%)' : 'Low Stock Threshold (%)'}
                          </Label>
                          <p className="text-xs text-gray-400">{tr ? 'Mevcut stok toplam stokun bu yüzdesinin altına düştüğünde tedarikçiye otomatik uyarı maili gönderilir.' : 'When available stock falls below this percentage of total stock, an automatic alert email is sent to the supplier.'}</p>
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <Input
                                data-testid="stock-threshold-input"
                                type="number"
                                min={1}
                                max={100}
                                value={shoppingSettings.stock_low_threshold_percent}
                                onChange={e => setShoppingSettings(s => ({ ...s, stock_low_threshold_percent: e.target.value }))}
                                className="rounded-xl w-32 pr-8"
                              />
                              <Percent className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                            </div>
                            <div className="flex items-center gap-1.5">
                              {[25, 50, 75].map(v => (
                                <button
                                  key={v}
                                  onClick={() => setShoppingSettings(s => ({ ...s, stock_low_threshold_percent: v }))}
                                  className={`px-3 py-1 text-xs rounded-lg font-medium transition-colors ${parseInt(shoppingSettings.stock_low_threshold_percent) === v ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-amber-50'}`}
                                >
                                  %{v}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Visual bar */}
                        <div className="space-y-2">
                          <p className="text-xs text-gray-500">{tr ? 'Uyarı tetiklenme bölgesi:' : 'Alert trigger zone:'}</p>
                          <div className="h-3 w-full rounded-full bg-gray-100 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-red-400 to-amber-400 transition-all duration-300"
                              style={{ width: `${Math.min(100, parseInt(shoppingSettings.stock_low_threshold_percent) || 50)}%` }}
                            />
                          </div>
                          <p className="text-xs text-amber-600">
                            {tr
                              ? `Mevcut stok, toplam stokun %${shoppingSettings.stock_low_threshold_percent || 50}'sinin altına düştüğünde tedarikçiye mail gider.`
                              : `Alert fires when available stock drops below ${shoppingSettings.stock_low_threshold_percent || 50}% of total stock.`
                            }
                          </p>
                        </div>

                        {/* Info box */}
                        <div className="bg-amber-50 rounded-xl p-4 flex gap-3">
                          <Package className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div className="text-xs text-amber-700 leading-relaxed">
                            <p className="font-semibold mb-1">{tr ? 'Stok Politikası Notu' : 'Stock Policy Note'}</p>
                            <p>{tr
                              ? '"Stok Takip Edilmiyor" (untracked) politikasına sahip ürünler için düşük stok uyarısı gönderilmez. Sadece "Kesin Stok" ve "Temin Edilebilir" politikaları izlenir.'
                              : 'Low stock alerts are NOT sent for products with "Untracked" stock policy. Only "Strict" and "Flexible Supply" policies are monitored.'
                            }</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Error */}
                    {shoppingError && (
                      <div className="bg-red-50 rounded-xl p-4 flex items-center gap-3">
                        <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-700">{shoppingError}</p>
                      </div>
                    )}

                    {/* Save button */}
                    <div className="flex justify-end">
                      <Button
                        data-testid="save-shopping-settings-btn"
                        onClick={handleSaveShoppingSettings}
                        className={`rounded-full px-8 gap-2 transition-all ${shoppingSaved ? 'bg-purple-700 hover:bg-purple-800' : 'bg-gray-900 hover:bg-gray-800'} text-white`}
                      >
                        {shoppingSaved
                          ? <><CheckCircle className="h-4 w-4" />{tr ? 'Kaydedildi!' : 'Saved!'}</>
                          : <><Save className="h-4 w-4" />{tr ? 'Ayarları Kaydet' : 'Save Settings'}</>
                        }
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── PHASE 86N — STAND CHANGE REQUEST QUOTA ───────────────────── */}
            {activeSection === 'stand-change-quota' && (
              <div className="max-w-2xl space-y-6" data-testid="stand-change-quota-section">
                <PageIntro {...getPageIntro('stand-change-quota', language)} />
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{tr ? 'Stand Değişiklik Limiti' : 'Stand Change Limit'}</h2>
                  <p className="text-sm text-gray-400 mt-0.5">
                    {tr
                      ? 'Bir müşteri etkinlik süresince kaç defa stand yer değişikliği talep edebilir?'
                      : 'How many stand-location change requests can a tenant submit during the event?'}
                  </p>
                </div>

                {scrLoading ? (
                  <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-gray-900 border-t-transparent rounded-full" /></div>
                ) : (
                  <Card className="border-0 shadow-sm rounded-2xl">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <SlidersHorizontal className="h-4 w-4 text-indigo-600" />
                        {tr ? 'Tenant Başına Maksimum Talep' : 'Max Requests Per Tenant'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-5">
                      <div className="space-y-1.5">
                        <Label className="text-sm font-medium text-gray-700">
                          {tr ? 'Maksimum Talep Sayısı' : 'Maximum Request Count'}
                        </Label>
                        <p className="text-xs text-gray-400">
                          {tr
                            ? 'Reddedilen ve iptal edilen talepler bu sayıma DAHIL DEĞİL. Onaylananlar veya bekleyenler hak tüketir. Varsayılan: 1.'
                            : 'Rejected & cancelled requests do NOT count. Approved/pending ones consume a slot. Default: 1.'}
                        </p>
                        <div className="flex items-center gap-3">
                          <Input
                            data-testid="stand-change-max-input"
                            type="number"
                            min={1}
                            max={20}
                            value={scrSettings.max_change_requests_per_tenant}
                            onChange={e => setScrSettings(s => ({ ...s, max_change_requests_per_tenant: e.target.value }))}
                            className="rounded-xl w-32"
                          />
                          <span className="text-sm text-gray-500">{tr ? 'talep / tenant' : 'requests / tenant'}</span>
                        </div>
                      </div>

                      <div className="bg-indigo-50 rounded-xl p-4 flex gap-3">
                        <SlidersHorizontal className="h-4 w-4 text-indigo-500 flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-indigo-700 leading-relaxed">
                          <p className="font-semibold mb-1">{tr ? 'Nasıl Çalışır?' : 'How It Works?'}</p>
                          <p>{tr
                            ? `Müşteri "+ Yeni Talep" butonuna basınca sistem ${scrSettings.max_change_requests_per_tenant} hakkı kontrol eder. Aşılırsa buton pasifleşir ve "Hak doldu" mesajı gösterilir. Reddedilen talepler hakka geri yazılır → müşteri tekrar deneyebilir.`
                            : `When a customer clicks "+ New Request", the system enforces ${scrSettings.max_change_requests_per_tenant} slot(s). Once exceeded the button is disabled and a "limit reached" message is shown. Rejected requests refund a slot — the customer can retry.`
                          }</p>
                        </div>
                      </div>

                      {scrError && (
                        <div className="text-sm text-red-600 bg-red-50 rounded-xl p-3" data-testid="scr-error">{scrError}</div>
                      )}
                      <div className="flex items-center gap-3">
                        <Button
                          data-testid="save-stand-change-quota-btn"
                          onClick={handleSaveScrSettings}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-6"
                        >
                          {tr ? 'Kaydet' : 'Save'}
                        </Button>
                        {scrSaved && (
                          <span className="text-sm text-emerald-600 font-medium" data-testid="scr-saved">
                            ✓ {tr ? 'Kaydedildi' : 'Saved'}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
            {activeSection === 'session' && (
              <div className="max-w-2xl space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{tr ? 'Oturum Yönetimi' : 'Session Management'}</h2>
                  <p className="text-sm text-gray-400 mt-0.5">
                    {tr
                      ? 'Her kullanıcı kategorisinin hangi süre sonunda otomatik olarak oturumunu kapatacağını belirleyin.'
                      : 'Set how long each user category stays logged in before automatic logout.'}
                  </p>
                </div>

                {sessionLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin h-8 w-8 border-4 border-gray-900 border-t-transparent rounded-full" />
                  </div>
                ) : (
                  <>
                    {/* Info banner */}
                    <div className="bg-blue-50 rounded-xl p-4 flex gap-3">
                      <Clock className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-blue-700 leading-relaxed">
                        <p className="font-semibold mb-1">{tr ? 'Nasıl Çalışır?' : 'How it works?'}</p>
                        <p>
                          {tr
                            ? 'Kullanıcı giriş yaptıktan sonra belirlenen süre dolunca otomatik olarak oturumu kapatılır ve giriş sayfasına yönlendirilir. Süre 0 dakika olursa oturum sınırsız devam eder.'
                            : 'After login, the session expires automatically after the configured duration. Setting 0 minutes disables auto-logout for that category.'}
                        </p>
                      </div>
                    </div>

                    {/* Role cards */}
                    {[
                      { key: 'blue_zone',   label: tr ? 'Blue Zone (Müşteri Admin)' : 'Blue Zone (Customer Admin)',   color: 'blue',   icon: '🔵', desc: tr ? 'COP31 katılımcıları — stand sahipleri ve delegeler' : 'COP31 participants — stand owners and delegates' },
                      { key: 'green_zone',  label: tr ? 'Green Zone (Ziyaretçi)'    : 'Green Zone (Visitor)',          color: 'green',  icon: '🟢', desc: tr ? 'Genel ziyaretçi hesapları' : 'General visitor accounts' },
                      { key: 'supplier',    label: tr ? 'Supplier Admin (Tedarikçi)' : 'Supplier Admin',               color: 'amber',  icon: '🟡', desc: tr ? 'Ürün ve hizmet sağlayıcı hesapları' : 'Product and service provider accounts' },
                      { key: 'ultra_admin', label: tr ? 'Ultra Admin'                : 'Ultra Admin',                  color: 'red',    icon: '🔴', desc: tr ? 'Platform yöneticileri — en yüksek yetki' : 'Platform administrators — highest privilege' },
                    ].map(role => {
                      const s = sessionSettings[role.key] || { timeout_minutes: 480, enabled: true };
                      const mins = parseInt(s.timeout_minutes) || 0;
                      const hours = Math.floor(mins / 60);
                      const remainMins = mins % 60;
                      const display = mins === 0
                        ? (tr ? 'Sınırsız' : 'Unlimited')
                        : hours > 0 && remainMins > 0
                          ? `${hours} ${tr ? 'saat' : 'hr'} ${remainMins} ${tr ? 'dk' : 'min'}`
                          : hours > 0
                            ? `${hours} ${tr ? 'saat' : (hours === 1 ? 'hour' : 'hours')}`
                            : `${remainMins} ${tr ? 'dakika' : 'min'}`;

                      const colorMap = {
                        blue:  { bg: 'bg-blue-50',  border: 'border-blue-200',  badge: 'bg-blue-100 text-blue-700',  ring: 'ring-blue-400'  },
                        green: { bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-100 text-green-700', ring: 'ring-green-400' },
                        amber: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', ring: 'ring-amber-400' },
                        red:   { bg: 'bg-red-50',   border: 'border-red-200',   badge: 'bg-red-100 text-red-700',    ring: 'ring-red-400'   },
                      };
                      const c = colorMap[role.color];

                      return (
                        <Card key={role.key} className={`border shadow-sm rounded-2xl ${s.enabled ? c.border : 'border-gray-200'}`}>
                          <CardContent className="p-5 space-y-4">
                            {/* Header row */}
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-3">
                                <span className="text-xl">{role.icon}</span>
                                <div>
                                  <p className="font-semibold text-gray-900 text-sm">{role.label}</p>
                                  <p className="text-xs text-gray-400 mt-0.5">{role.desc}</p>
                                </div>
                              </div>
                              {/* Toggle */}
                              <button
                                data-testid={`session-toggle-${role.key}`}
                                onClick={() => updateSessionRole(role.key, 'enabled', !s.enabled)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 focus:outline-none ${s.enabled ? 'bg-emerald-500' : 'bg-gray-200'}`}
                              >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${s.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                              </button>
                            </div>

                            {/* Timeout input */}
                            <div className={`space-y-3 transition-opacity ${s.enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                              <div className="flex items-center gap-4">
                                {/* Quick presets */}
                                <div className="flex flex-wrap gap-2">
                                  {[30, 60, 120, 240, 480].map(preset => (
                                    <button
                                      key={preset}
                                      data-testid={`session-preset-${role.key}-${preset}`}
                                      onClick={() => updateSessionRole(role.key, 'timeout_minutes', preset)}
                                      className={`px-3 py-1 text-xs rounded-full font-medium border transition-all ${mins === preset
                                        ? `${c.badge} border-current`
                                        : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}
                                    >
                                      {preset >= 60
                                        ? `${preset / 60}${tr ? ' saat' : 'h'}`
                                        : `${preset}${tr ? ' dk' : 'min'}`}
                                    </button>
                                  ))}
                                  <button
                                    onClick={() => updateSessionRole(role.key, 'timeout_minutes', 0)}
                                    className={`px-3 py-1 text-xs rounded-full font-medium border transition-all ${mins === 0
                                      ? 'bg-gray-800 text-white border-gray-800'
                                      : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}
                                  >
                                    {tr ? 'Sınırsız' : 'Unlimited'}
                                  </button>
                                </div>
                              </div>

                              {/* Custom minute input */}
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                  <Clock className="h-4 w-4 text-gray-400" />
                                  <Input
                                    data-testid={`session-minutes-${role.key}`}
                                    type="number"
                                    min={0}
                                    max={43200}
                                    value={s.timeout_minutes}
                                    onChange={e => updateSessionRole(role.key, 'timeout_minutes', Math.max(0, parseInt(e.target.value) || 0))}
                                    className="w-24 rounded-xl text-sm"
                                  />
                                  <span className="text-sm text-gray-500">{tr ? 'dakika' : 'minutes'}</span>
                                </div>
                                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${c.badge}`}>
                                  {display}
                                </span>
                              </div>
                            </div>

                            {/* Disabled state note */}
                            {!s.enabled && (
                              <p className="text-xs text-gray-400 italic">
                                {tr ? 'Bu kategori için otomatik oturum kapatma devre dışı.' : 'Auto-logout disabled for this category.'}
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}

                    {/* Error */}
                    {sessionError && (
                      <div className="bg-red-50 rounded-xl p-4 flex items-center gap-3">
                        <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-700">{sessionError}</p>
                      </div>
                    )}

                    {/* Save button */}
                    <div className="flex justify-end">
                      <Button
                        data-testid="save-session-settings-btn"
                        onClick={handleSaveSessionSettings}
                        className={`rounded-full px-8 gap-2 transition-all ${sessionSaved ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-900 hover:bg-gray-800'} text-white`}
                      >
                        {sessionSaved
                          ? <><CheckCircle className="h-4 w-4" />{tr ? 'Kaydedildi!' : 'Saved!'}</>
                          : <><Save className="h-4 w-4" />{tr ? 'Oturum Ayarlarını Kaydet' : 'Save Session Settings'}</>
                        }
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeSection === 'patterns' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{tr ? 'Desenler & Renkler' : 'Patterns & Colors'}</h2>
                    <p className="text-sm text-gray-400 mt-0.5">{tr ? 'Ürün desen ve renk profillerini yönetin' : 'Manage product pattern and color profiles'}</p>
                  </div>
                  <Button
                    onClick={() => { setShowScrapeModal(true); setScrapeError(''); setScrapeSuccess(''); setScrapeResults(null); setScrapeSelected(new Set()); setScrapeGroupName(''); setScrapeUrl(''); }}
                    className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl gap-2 px-4"
                  >
                    <Globe className="h-4 w-4" />
                    {tr ? 'URL\'den İçe Aktar' : 'Import from URL'}
                  </Button>
                </div>

                <div className="flex gap-6">
                  {/* ── LEFT: Group List ──────────────────────────────── */}
                  <div className="w-64 flex-shrink-0 space-y-3">
                    {/* Add group */}
                    <Card className="border-0 shadow-sm rounded-2xl">
                      <CardContent className="pt-4 pb-4 space-y-2">
                        <p className="text-xs font-semibold text-gray-500 uppercase">{tr ? 'Yeni Grup' : 'New Group'}</p>
                        <div className="flex gap-2">
                          <input
                            value={newGroupName}
                            onChange={e => { setNewGroupName(e.target.value); setPatternGroupError(''); }}
                            onKeyDown={e => e.key === 'Enter' && handleAddPatternGroup()}
                            placeholder={tr ? 'Örn: Rip Halı' : 'e.g. Rip Carpet'}
                            className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-200"
                          />
                          <button
                            onClick={handleAddPatternGroup}
                            className="h-9 w-9 rounded-xl bg-gray-900 text-white flex items-center justify-center hover:bg-gray-700 transition-colors flex-shrink-0"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                        {patternGroupError && <p className="text-xs text-red-500">{patternGroupError}</p>}
                      </CardContent>
                    </Card>

                    {/* Group list */}
                    {patternLoading ? (
                      <div className="flex items-center justify-center py-8"><div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-gray-800 rounded-full" /></div>
                    ) : (
                      <div className="space-y-1.5">
                        {patternGroups.map(group => (
                          <div
                            key={group.id}
                            onClick={() => { setSelectedGroupId(group.id); setEditingGroup(null); }}
                            className={`group flex items-center justify-between gap-2 px-3 py-3 rounded-xl cursor-pointer transition-all border ${
                              selectedGroupId === group.id
                                ? 'bg-purple-50 border-purple-200 shadow-sm'
                                : 'bg-white border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            {editingGroup?.id === group.id ? (
                              <input
                                autoFocus
                                value={editingGroup.name}
                                onChange={e => setEditingGroup(g => ({ ...g, name: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') handleUpdatePatternGroup(group.id); if (e.key === 'Escape') setEditingGroup(null); }}
                                onClick={e => e.stopPropagation()}
                                className="flex-1 text-sm border border-purple-300 rounded-lg px-2 py-1 focus:outline-none"
                              />
                            ) : (
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-semibold truncate ${selectedGroupId === group.id ? 'text-purple-800' : 'text-gray-700'}`}>{group.name}</p>
                                <div className="flex items-center gap-1 mt-0.5">
                                  <span className="text-[10px] font-mono bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded">{group.code}</span>
                                  <span className="text-[10px] text-gray-400">{group.items?.length || 0} öğe</span>
                                </div>
                              </div>
                            )}
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                              {editingGroup?.id === group.id ? (
                                <>
                                  <button onClick={() => handleUpdatePatternGroup(group.id)} className="h-6 w-6 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center hover:bg-emerald-200"><Check className="h-3 w-3" /></button>
                                  <button onClick={() => setEditingGroup(null)} className="h-6 w-6 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center hover:bg-gray-200"><X className="h-3 w-3" /></button>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => setEditingGroup({ id: group.id, name: group.name })} className="h-6 w-6 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center hover:bg-gray-200"><Pencil className="h-3 w-3" /></button>
                                  <button onClick={() => handleDeletePatternGroup(group.id)} className="h-6 w-6 rounded-lg bg-red-100 text-red-500 flex items-center justify-center hover:bg-red-200"><Trash2 className="h-3 w-3" /></button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                        {patternGroups.length === 0 && !patternLoading && (
                          <div className="text-center py-8 text-sm text-gray-400">
                            <Palette className="h-8 w-8 mx-auto mb-2 opacity-30" />
                            <p>{tr ? 'Henüz grup yok' : 'No groups yet'}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── RIGHT: Items Grid ──────────────────────────────── */}
                  <div className="flex-1">
                    {selectedGroupId ? (() => {
                      const group = patternGroups.find(g => g.id === selectedGroupId);
                      if (!group) return null;
                      return (
                        <Card className="border-0 shadow-sm rounded-2xl">
                          <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <CardTitle className="text-base flex items-center gap-2">
                                  <span className="text-xs font-mono bg-purple-100 text-purple-600 px-2 py-0.5 rounded">{group.code}</span>
                                  {group.name}
                                </CardTitle>
                                <p className="text-xs text-gray-400 mt-0.5">{group.items?.length || 0} {tr ? 'desen/renk' : 'pattern/color'}</p>
                              </div>
                              <button
                                data-testid="add-pattern-item-btn"
                                onClick={() => { setShowAddItemModal(true); setEditingItem(null); setPatternSlots(Array(12).fill(null)); setNewItemError(''); }}
                                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 transition-colors"
                              >
                                <UploadCloud className="h-4 w-4" />
                                {tr ? 'Toplu Ekle' : 'Bulk Add'}
                              </button>
                            </div>
                          </CardHeader>
                          <CardContent>
                            {group.items?.length === 0 ? (
                              <div className="text-center py-12 text-gray-400">
                                <ImageIcon className="h-12 w-12 mx-auto mb-3 opacity-20" />
                                <p className="text-sm">{tr ? 'Henüz desen/renk eklenmedi' : 'No patterns/colors added yet'}</p>
                                <p className="text-xs text-gray-300 mt-1">{tr ? 'Sağ üstteki "Desen/Renk Ekle" butonuna tıklayın' : 'Click "Add Pattern" to get started'}</p>
                              </div>
                            ) : (
                              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                                {group.items.map(item => (
                                  <div key={item.id} className="group relative">
                                    <div className="relative aspect-square rounded-xl overflow-hidden border-2 border-gray-100 bg-gray-50 hover:border-purple-300 transition-all">
                                      {item.image_url ? (
                                        <img
                                          src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`}
                                          alt={item.name}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                                          <ImageIcon className="h-6 w-6 text-gray-300" />
                                        </div>
                                      )}
                                      {/* Hover actions */}
                                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                                        <button
                                          onClick={() => { setEditingItem({ id: item.id, name: item.name, preview: item.image_url ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}` : null, newImage: null }); setShowAddItemModal(true); setNewItemError(''); }}
                                          className="h-7 w-7 rounded-lg bg-white/90 text-gray-700 flex items-center justify-center hover:bg-white"
                                        >
                                          <Pencil className="h-3 w-3" />
                                        </button>
                                        <button
                                          onClick={() => handleDeletePatternItem(item.id)}
                                          className="h-7 w-7 rounded-lg bg-red-500/90 text-white flex items-center justify-center hover:bg-red-600"
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="mt-1.5 text-center">
                                      <p className="text-xs font-semibold text-gray-700 truncate">{item.name}</p>
                                      <span className="text-[10px] font-mono text-purple-500">{item.code}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })() : (
                      <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
                        <div className="text-center">
                          <Palette className="h-10 w-10 mx-auto mb-3 opacity-20" />
                          <p>{tr ? 'Sol taraftan bir grup seçin veya yeni grup oluşturun' : 'Select a group from the left or create a new one'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Add/Edit Item Modal ──────────────────────────────── */}
                {showAddItemModal && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => { if (!bulkUploading) { setShowAddItemModal(false); setPatternSlots(Array(12).fill(null)); } }}>
                    {editingItem ? (
                      /* ── SINGLE EDIT MODE ── */
                      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-6 border-b border-gray-100">
                          <div>
                            <h2 className="text-base font-bold text-gray-900">{tr ? 'Öğeyi Düzenle' : 'Edit Item'}</h2>
                            <p className="text-xs text-gray-400 mt-0.5">{patternGroups.find(g => g.id === selectedGroupId)?.name}</p>
                          </div>
                          <button onClick={() => { setShowAddItemModal(false); setEditingItem(null); }} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="p-6 space-y-4">
                          <label className="cursor-pointer block">
                            <div className="relative rounded-2xl overflow-hidden border-2 border-dashed border-purple-200 hover:border-purple-400 transition-all" style={{ aspectRatio: '1/1', maxHeight: 160 }}>
                              {editingItem.preview ? (
                                <img src={editingItem.preview} alt="preview" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center text-gray-300 py-8">
                                  <UploadCloud className="h-10 w-10 mb-2" /><p className="text-xs">{tr ? 'Resim seç' : 'Choose image'}</p>
                                </div>
                              )}
                              {editingItem.preview && <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"><UploadCloud className="h-8 w-8 text-white" /></div>}
                            </div>
                            <input type="file" accept="image/*" className="sr-only" onChange={e => {
                              const file = e.target.files?.[0]; if (!file) return;
                              setEditingItem(i => ({ ...i, newImage: file, preview: URL.createObjectURL(file) }));
                            }} />
                          </label>
                          <input autoFocus value={editingItem.name} onChange={e => setEditingItem(i => ({ ...i, name: e.target.value }))}
                            placeholder={tr ? 'İsim...' : 'Name...'}
                            className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-purple-200" />
                          {newItemError && <p className="text-xs text-red-500">{newItemError}</p>}
                        </div>
                        <div className="flex gap-3 px-6 pb-6">
                          <button onClick={() => { setShowAddItemModal(false); setEditingItem(null); }} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">{tr ? 'İptal' : 'Cancel'}</button>
                          <button data-testid="save-pattern-item-btn" onClick={handleUpdatePatternItem} disabled={savingItem}
                            className="flex-1 px-4 py-2.5 rounded-xl bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-60 flex items-center justify-center gap-2">
                            {savingItem ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Save className="h-4 w-4" />}
                            {tr ? 'Kaydet' : 'Save'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* ── 3×4 BULK GRID MODE ── */
                      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col" style={{ maxHeight: '92vh' }} onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
                          <div>
                            <h2 className="text-base font-bold text-gray-900">{tr ? 'Toplu Desen/Renk Ekle' : 'Bulk Add Patterns'}</h2>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {patternGroups.find(g => g.id === selectedGroupId)?.name} · {tr ? 'Her kutuya sürükle veya "Browse" ile 12 dosya seç' : 'Drag to each box or browse 12 files at once'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Browse button — fills empty slots */}
                            <button
                              onClick={() => patternBrowseRef.current?.click()}
                              disabled={bulkUploading}
                              className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-purple-200 text-purple-700 text-sm font-semibold hover:bg-purple-50 transition-colors disabled:opacity-40"
                            >
                              <ImageIcon className="h-4 w-4" />
                              {tr ? 'Browse...' : 'Browse...'}
                            </button>
                            <input ref={patternBrowseRef} type="file" multiple accept="image/*" className="sr-only"
                              onChange={e => { fillSlotsFromFiles(e.target.files); e.target.value = ''; }} />
                            <button onClick={() => { setShowAddItemModal(false); setPatternSlots(Array(12).fill(null)); }} disabled={bulkUploading} className="text-gray-400 hover:text-gray-700 disabled:opacity-40 ml-1"><X className="h-5 w-5" /></button>
                          </div>
                        </div>

                        {/* 3×4 Grid — with drag-over support on whole grid */}
                        <div
                          className="flex-1 overflow-y-auto p-5"
                          onDragOver={handleGridDragOver}
                          onDrop={handleGridDrop}
                        >
                          <div className="grid grid-cols-3 gap-3">
                            {patternSlots.map((slot, idx) => (
                              <div key={idx}
                                onDragOver={e => { e.preventDefault(); e.stopPropagation(); setIsDraggingSlot(idx); }}
                                onDragLeave={() => setIsDraggingSlot(null)}
                                onDrop={e => handleSlotDrop(e, idx)}
                              >
                                {/* Image box */}
                                <label className="cursor-pointer block">
                                  <div className={`relative rounded-2xl overflow-hidden transition-all ${
                                    isDraggingSlot === idx
                                      ? 'ring-2 ring-purple-500 scale-[1.03] bg-purple-50'
                                      : slot
                                      ? 'ring-0'
                                      : 'border-2 border-dashed border-gray-200 hover:border-purple-300 bg-gray-50 hover:bg-purple-50/40'
                                  }`} style={{ aspectRatio: '1/1' }}>
                                    {slot ? (
                                      <>
                                        <img src={slot.preview} alt={slot.name} className="w-full h-full object-cover" />
                                        {/* Status overlays */}
                                        {slot.status === 'uploading' && (
                                          <div className="absolute inset-0 bg-purple-600/60 flex items-center justify-center">
                                            <div className="animate-spin h-7 w-7 border-3 border-white border-t-transparent rounded-full" style={{ borderWidth: 3 }} />
                                          </div>
                                        )}
                                        {slot.status === 'done' && (
                                          <div className="absolute inset-0 bg-purple-600/60 flex items-center justify-center">
                                            <Check className="h-8 w-8 text-white" strokeWidth={3} />
                                          </div>
                                        )}
                                        {slot.status === 'error' && (
                                          <div className="absolute inset-0 bg-red-500/60 flex items-center justify-center flex-col gap-1">
                                            <AlertTriangle className="h-6 w-6 text-white" />
                                            <span className="text-[10px] text-white font-medium px-1 text-center">{slot.error}</span>
                                          </div>
                                        )}
                                        {/* Remove/replace button */}
                                        {slot.status === 'pending' && !bulkUploading && (
                                          <button
                                            type="button"
                                            onClick={e => { e.preventDefault(); e.stopPropagation(); setPatternSlots(prev => { const n = [...prev]; n[idx] = null; return n; }); }}
                                            className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-red-500 transition-colors z-10"
                                          >
                                            <X className="h-3 w-3" />
                                          </button>
                                        )}
                                        {/* Slot number badge */}
                                        <span className="absolute bottom-1.5 left-1.5 text-[9px] font-mono bg-black/40 text-white px-1 rounded">{String(idx + 1).padStart(2, '0')}</span>
                                      </>
                                    ) : (
                                      /* Empty slot */
                                      <div className="w-full h-full flex flex-col items-center justify-center gap-1 py-2">
                                        {isDraggingSlot === idx ? (
                                          <UploadCloud className="h-7 w-7 text-purple-500" />
                                        ) : (
                                          <>
                                            <Plus className="h-6 w-6 text-gray-300" />
                                            <span className="text-[10px] text-gray-300 font-mono">{String(idx + 1).padStart(2, '0')}</span>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  {/* Click-to-replace hidden input (for filled pending slots) */}
                                  {(!slot || slot.status === 'pending') && !bulkUploading && (
                                    <input type="file" accept="image/*" className="sr-only"
                                      onChange={e => {
                                        const file = e.target.files?.[0]; if (!file) return;
                                        setPatternSlots(prev => { const n = [...prev]; n[idx] = { file, preview: URL.createObjectURL(file), name: file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' '), status: 'pending', error: null }; return n; });
                                        e.target.value = '';
                                      }} />
                                  )}
                                </label>
                                {/* Editable name */}
                                {slot && slot.status !== 'done' ? (
                                  <input
                                    value={slot.name}
                                    onChange={e => setPatternSlots(prev => { const n = [...prev]; if (n[idx]) n[idx] = { ...n[idx], name: e.target.value }; return n; })}
                                    disabled={bulkUploading}
                                    placeholder={tr ? 'İsim...' : 'Name...'}
                                    className="mt-1.5 w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-purple-400 bg-white disabled:bg-gray-50 text-center"
                                  />
                                ) : slot?.status === 'done' ? (
                                  <p className="mt-1.5 text-xs font-semibold text-purple-700 text-center truncate">{slot.name}</p>
                                ) : (
                                  <div className="mt-1.5 h-7" /> /* placeholder height */
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between gap-3 px-6 pb-5 pt-4 border-t border-gray-100 flex-shrink-0">
                          <div className="text-sm text-gray-400">
                            {(() => {
                              const filled = patternSlots.filter(Boolean).length;
                              const pending = patternSlots.filter(s => s?.status === 'pending').length;
                              const done = patternSlots.filter(s => s?.status === 'done').length;
                              return filled > 0
                                ? <span>{filled} {tr ? 'dolu' : 'filled'}{done > 0 ? ` · ${done} ✓` : ''}{pending > 0 ? ` · ${pending} ${tr ? 'bekliyor' : 'pending'}` : ''}</span>
                                : <span className="text-gray-300">{tr ? 'Henüz görsel yok' : 'No images yet'}</span>;
                            })()}
                          </div>
                          <div className="flex gap-3">
                            {!bulkUploading && patternSlots.filter(s => s?.status === 'pending').length > 0 && (
                              <button onClick={() => setPatternSlots(Array(12).fill(null))} className="px-3 py-2 rounded-xl text-xs text-gray-500 hover:text-red-500 transition-colors">
                                {tr ? 'Temizle' : 'Clear'}
                              </button>
                            )}
                            <button onClick={() => { if (!bulkUploading) { setShowAddItemModal(false); setPatternSlots(Array(12).fill(null)); } }} disabled={bulkUploading} className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40">
                              {tr ? 'İptal' : 'Cancel'}
                            </button>
                            <button
                              data-testid="save-pattern-item-btn"
                              onClick={handleBulkUpload}
                              disabled={patternSlots.filter(s => s?.status === 'pending').length === 0 || bulkUploading}
                              className="px-5 py-2.5 rounded-xl bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 transition-colors disabled:opacity-40 flex items-center gap-2 min-w-[140px] justify-center"
                            >
                              {bulkUploading ? (
                                <>
                                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                  {bulkProgress.done}/{bulkProgress.total}
                                </>
                              ) : (
                                <>
                                  <UploadCloud className="h-4 w-4" />
                                  {patternSlots.filter(s => s?.status === 'pending').length > 0
                                    ? `${patternSlots.filter(s => s?.status === 'pending').length} ${tr ? 'öğeyi yükle' : 'items upload'}`
                                    : (tr ? 'Tümü Tamam' : 'All Done')}
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Pattern Picker Modal (Template Pattern Assignment) ── */}
            {patternPicker && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setPatternPicker(null)}>
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-4 flex flex-col" style={{ maxHeight: '85vh' }} onClick={e => e.stopPropagation()}>
                  {/* Header */}
                  <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
                    <div>
                      <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                        <Palette className="h-4 w-4 text-purple-500" />
                        {tr ? 'Renk/Desen Ata' : 'Assign Colors/Patterns'}
                      </h2>
                      <p className="text-xs text-gray-400 mt-0.5 font-medium">{patternPicker.tplName}</p>
                    </div>
                    <button onClick={() => setPatternPicker(null)} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
                  </div>

                  {/* Pattern Groups — bireysel swatch seçimi */}
                  <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                    {patternGroups.length === 0 ? (
                      <div className="text-center py-10 text-gray-400">
                        <Palette className="h-10 w-10 mx-auto mb-3 opacity-20" />
                        <p className="text-sm">{tr ? 'Henüz pattern grubu yok' : 'No pattern groups yet'}</p>
                        <p className="text-xs mt-1">{tr ? 'Önce Ayarlar → Desenler & Renkler bölümünden grup ekleyin' : 'Add groups in Settings → Patterns & Colors first'}</p>
                      </div>
                    ) : (
                      <>
                        {/* Toplu aksiyon */}
                        <div className="flex items-center gap-2 pb-1">
                          <button
                            onClick={() => setPickerSelected(new Set(patternGroups.flatMap(g => g.items?.map(i => i.id) || [])))}
                            className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                          >{tr ? 'Tümünü Seç' : 'Select All'}</button>
                          <span className="text-gray-300">|</span>
                          <button onClick={() => setPickerSelected(new Set())} className="text-xs text-gray-500 hover:text-gray-700">{tr ? 'Temizle' : 'Clear'}</button>
                          <span className="ml-auto text-xs text-gray-400">{pickerSelected.size} {tr ? 'renk seçili' : 'colors selected'}</span>
                        </div>

                        {/* Grup kartları */}
                        {patternGroups.map(group => {
                          const groupItemIds = group.items?.map(i => i.id) || [];
                          const selectedInGroup = groupItemIds.filter(id => pickerSelected.has(id)).length;
                          const allSelected = selectedInGroup === groupItemIds.length && groupItemIds.length > 0;
                          const someSelected = selectedInGroup > 0 && !allSelected;

                          return (
                            <div key={group.id} className={`p-3 rounded-2xl border-2 transition-all ${
                              allSelected ? 'border-purple-400 bg-purple-50' :
                              someSelected ? 'border-purple-200 bg-purple-50/50' :
                              'border-gray-100 hover:border-gray-200'
                            }`}>
                              {/* Grup başlık satırı */}
                              <div className="flex items-center gap-2 mb-2">
                                {/* Grup checkbox — hepsini seç/kaldır */}
                                <button
                                  onClick={() => {
                                    setPickerSelected(prev => {
                                      const next = new Set(prev);
                                      if (allSelected) groupItemIds.forEach(id => next.delete(id));
                                      else groupItemIds.forEach(id => next.add(id));
                                      return next;
                                    });
                                  }}
                                  className={`h-5 w-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                    allSelected ? 'border-purple-500 bg-purple-500' :
                                    someSelected ? 'border-purple-400 bg-purple-200' :
                                    'border-gray-300 hover:border-purple-400'
                                  }`}
                                >
                                  {allSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                  {someSelected && <div className="w-2 h-0.5 bg-purple-600 rounded" />}
                                </button>
                                <span className="text-[10px] font-mono font-bold text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded">{group.code}</span>
                                <span className="text-sm font-semibold text-gray-800">{group.name}</span>
                                <span className="text-xs text-gray-400 ml-auto">
                                  {selectedInGroup > 0 ? `${selectedInGroup}/` : ''}{groupItemIds.length} {tr ? 'renk' : 'colors'}
                                </span>
                              </div>

                              {/* Bireysel swatch'lar — tıklanabilir */}
                              {group.items?.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                  {group.items.map(item => {
                                    const isItemSelected = pickerSelected.has(item.id);
                                    return (
                                      <button
                                        key={item.id}
                                        title={item.name}
                                        onClick={() => {
                                          setPickerSelected(prev => {
                                            const next = new Set(prev);
                                            isItemSelected ? next.delete(item.id) : next.add(item.id);
                                            return next;
                                          });
                                        }}
                                        className={`relative rounded-lg overflow-hidden transition-all focus:outline-none ${
                                          isItemSelected
                                            ? 'ring-2 ring-purple-500 ring-offset-1 scale-105'
                                            : 'ring-1 ring-gray-200 hover:ring-purple-300 hover:scale-105'
                                        }`}
                                        style={{ width: 36, height: 36 }}
                                      >
                                        {item.image_url ? (
                                          <img
                                            src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`}
                                            alt={item.name}
                                            className="w-full h-full object-cover"
                                          />
                                        ) : (
                                          <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200" />
                                        )}
                                        {/* Seçili overlay */}
                                        {isItemSelected && (
                                          <div className="absolute inset-0 bg-purple-500/30 flex items-center justify-center">
                                            <Check className="h-4 w-4 text-white drop-shadow" strokeWidth={3} />
                                          </div>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex gap-3 px-6 pb-5 pt-4 border-t border-gray-100 flex-shrink-0">
                    <button onClick={() => setPatternPicker(null)} className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                      {tr ? 'İptal' : 'Cancel'}
                    </button>
                    <button
                      onClick={handleSaveTemplatePatterns}
                      disabled={savingPatterns}
                      className="flex-1 px-4 py-2.5 rounded-xl bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {savingPatterns
                        ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        : <Save className="h-4 w-4" />}
                      {pickerSelected.size > 0
                        ? (tr ? `${pickerSelected.size} Rengi Ata` : `Assign ${pickerSelected.size} Color(s)`)
                        : (tr ? 'Atamayı Kaldır' : 'Remove Assignment')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'logs' && (
              <div className="max-w-2xl space-y-6">
                <div><h2 className="text-xl font-bold text-gray-900">{tr ? 'Sistem Günlükleri' : 'System Logs'}</h2><p className="text-sm text-gray-400 mt-0.5">{tr ? 'Sistem aktivite kayıtları' : 'System activity records'}</p></div>
                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardContent className="pt-4">
                    <div className="bg-gray-900 rounded-xl p-4 font-mono text-xs text-green-400 space-y-1.5 max-h-96 overflow-y-auto">
                      <p>[{new Date().toISOString()}] INFO System started</p>
                      <p>[{new Date(Date.now()-60000).toISOString()}] INFO User login: mbucak@gmail.com</p>
                      <p>[{new Date(Date.now()-120000).toISOString()}] INFO Registration approved: hello@tss-united.com</p>
                      <p>[{new Date(Date.now()-180000).toISOString()}] INFO New pending: info@quattrostand.com</p>
                      <p>[{new Date(Date.now()-240000).toISOString()}] INFO Platform initialized</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* ── Collections Viewer ─────────────────────────────────────── */}
            {activeSection === 'collections' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{tr ? 'Koleksiyonlar' : 'Collections'}</h2>
                    <p className="text-sm text-gray-400 mt-0.5">MongoDB · {colList.length} koleksiyon</p>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-xl gap-1.5" onClick={fetchCollections}>
                    <RefreshCw className="h-3.5 w-3.5" />{tr ? 'Yenile' : 'Refresh'}
                  </Button>
                </div>

                <div className="flex gap-4" style={{minHeight: '70vh'}}>
                  {/* Left — Collection list */}
                  <div className="w-64 flex-shrink-0 flex flex-col gap-2">
                    {/* Collection name search */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <input
                        type="text"
                        value={colListSearch}
                        onChange={e => setColListSearch(e.target.value)}
                        placeholder={tr ? 'Koleksiyon ara...' : 'Search collection...'}
                        className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white"
                      />
                    </div>
                    <div className="space-y-1 overflow-y-auto" style={{maxHeight: 'calc(70vh - 48px)'}}>
                    {colList.filter(col => col.name.toLowerCase().includes(colListSearch.toLowerCase())).map(col => (
                      <button
                        key={col.name}
                        onClick={() => handleSelectCol(col.name)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-colors text-left ${
                          selectedCol === col.name
                            ? 'bg-gray-900 text-white font-semibold'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center gap-2 overflow-hidden">
                          <Table2 className="h-4 w-4 flex-shrink-0 opacity-60" />
                          <span className="truncate">{col.name}</span>
                        </div>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-mono flex-shrink-0 ${
                          selectedCol === col.name ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                        }`}>{col.count}</span>
                      </button>
                    ))}
                    </div>
                  </div>

                  {/* Right — Documents */}
                  <div className="flex-1 min-w-0">
                    {!selectedCol ? (
                      <div className="flex flex-col items-center justify-center h-full text-gray-300 gap-3">
                        <Database className="h-16 w-16" />
                        <p className="text-sm">{tr ? 'Soldaki listeden bir koleksiyon seçin' : 'Select a collection from the left'}</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Header */}
                        <div className="flex items-center gap-3">
                          <button onClick={() => setSelectedCol(null)} className="text-gray-400 hover:text-gray-700">
                            <ChevronLeft className="h-5 w-5" />
                          </button>
                          <h3 className="font-bold text-gray-900 text-base">{selectedCol}</h3>
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{colTotal} kayıt</span>
                          <Button size="sm" variant="ghost" className="ml-auto rounded-xl" onClick={() => fetchColDocs(selectedCol, colPage, colSearch)}>
                            <RefreshCw className="h-3.5 w-3.5" />
                          </Button>
                        </div>

                        {/* Search */}
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            value={colSearch}
                            onChange={e => { setColSearch(e.target.value); fetchColDocs(selectedCol, 1, e.target.value); }}
                            placeholder={tr ? 'Ara (email, id, status...)' : 'Search (email, id, status...)'}
                            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                          />
                        </div>

                        {/* Documents */}
                        {colLoading ? (
                          <div className="flex items-center justify-center py-16">
                            <div className="animate-spin h-8 w-8 border-2 border-gray-900 border-t-transparent rounded-full" />
                          </div>
                        ) : colDocs.length === 0 ? (
                          <div className="text-center py-16 text-gray-400 text-sm">{tr ? 'Kayıt bulunamadı.' : 'No records found.'}</div>
                        ) : (
                          <div className="space-y-2">
                            {colDocs.map((doc, i) => {
                              const docId = doc.id || doc._id || String(i);
                              const isExpanded = expandedDoc === docId;
                              // Summary line: show first 3 string fields
                              const summary = Object.entries(doc)
                                .filter(([k, v]) => typeof v === 'string' || typeof v === 'number')
                                .slice(0, 4)
                                .map(([k, v]) => `${k}: ${String(v).slice(0, 40)}`)
                                .join(' · ');
                              return (
                                <div key={docId} className="border border-gray-100 rounded-xl overflow-hidden">
                                  <div
                                    className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors min-w-0 overflow-hidden"
                                    onClick={() => setExpandedDoc(isExpanded ? null : docId)}
                                  >
                                    <span className="text-xs font-mono text-gray-300 w-6 flex-shrink-0">{(colPage-1)*50 + i + 1}</span>
                                    <span className="text-xs text-gray-600 flex-1 min-w-0 truncate">{summary || JSON.stringify(doc).slice(0, 80)}</span>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      {doc.id && (
                                        <button
                                          onClick={e => { e.stopPropagation(); handleDeleteColDoc(doc.id); }}
                                          className="p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                                          title={tr ? 'Sil' : 'Delete'}
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                      )}
                                      {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                                    </div>
                                  </div>
                                  {isExpanded && (
                                    <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                                      <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all overflow-auto max-h-64 font-mono leading-relaxed">
                                        {JSON.stringify(doc, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Pagination */}
                        {colPages > 1 && (
                          <div className="flex items-center justify-center gap-2 pt-2">
                            <Button size="sm" variant="outline" disabled={colPage <= 1} onClick={() => { const p = colPage-1; setColPage(p); fetchColDocs(selectedCol, p, colSearch); }} className="rounded-xl">
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-sm text-gray-600">{colPage} / {colPages}</span>
                            <Button size="sm" variant="outline" disabled={colPage >= colPages} onClick={() => { const p = colPage+1; setColPage(p); fetchColDocs(selectedCol, p, colSearch); }} className="rounded-xl">
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Grid Plan Ayarları */}
            {activeSection === 'grid' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{tr ? 'Grid Plan Ayarları' : 'Grid Settings'}</h2>
                  <p className="text-sm text-gray-400 mt-0.5">{tr ? 'Hall editörü ızgara ve görünüm yapılandırması' : 'Hall editor grid and display configuration'}</p>
                </div>
                <div className="bg-purple-50 rounded-2xl p-6 text-center space-y-3">
                  <SlidersHorizontal className="h-10 w-10 text-purple-400 mx-auto" />
                  <p className="text-sm text-gray-600">
                    {tr ? 'Grid plan yapılandırması Hall Editörü sayfasından yönetilmektedir.' : 'Grid configuration is managed from the Hall Editor page.'}
                  </p>
                  <a href="/ultra-admin/grid-settings" className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 transition-colors">
                    <SlidersHorizontal className="h-4 w-4" />
                    {tr ? 'Grid Plan Ayarlarına Git' : 'Go to Grid Settings'}
                  </a>
                </div>
              </div>
            )}

            {/* Denetim Kayıtları */}
            {activeSection === 'audit' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{tr ? 'Denetim Kayıtları' : 'Audit Logs'}</h2>
                  <p className="text-sm text-gray-400 mt-0.5">{tr ? 'Platform üzerindeki tüm yönetici işlemlerinin kaydı' : 'Record of all admin actions on the platform'}</p>
                </div>
                <div className="bg-slate-50 rounded-2xl p-6 text-center space-y-3">
                  <Activity className="h-10 w-10 text-slate-400 mx-auto" />
                  <p className="text-sm text-gray-600">
                    {tr ? 'Denetim kayıtları Sistem Günlükleri ile entegre çalışmaktadır.' : 'Audit logs are integrated with System Logs.'}
                  </p>
                  <a href="/ultra-admin/audit-logs" className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors">
                    <Activity className="h-4 w-4" />
                    {tr ? 'Denetim Kayıtlarına Git' : 'Go to Audit Logs'}
                  </a>
                </div>
              </div>
            )}

            {/* E-posta Kütüphanesi */}
            {activeSection === 'email-library' && (
              <EmailLibrarySection tr={tr} />
            )}

            {/* ── Phase 80: Moved-from-sidebar shortcuts ─────────────────
                Each of these renders a clean "go to" card rather than
                embedding the full page. Keeps this file small AND lets the
                dedicated pages keep their own routing/permissions. */}
            {activeSection === 'delegate-map' && (
              <SettingsShortcut
                tr={tr}
                title={tr ? 'Delege Haritası' : 'Delegate Map'}
                desc={tr ? 'UNFCCC delege listesinin harita üzerinde görsel dağılımı.' : 'Geographic distribution of UNFCCC delegates.'}
                href="/ultra-admin/delegate-map"
                Icon={Globe2}
                color="cyan"
              />
            )}
            {activeSection === 'platform-rules' && (
              <SettingsShortcut
                tr={tr}
                title={tr ? 'Platform Kuralları' : 'Platform Rules'}
                desc={tr ? 'Onay akışları, rezervasyon süreleri ve iş kuralları yönetimi.' : 'Approval workflows, reservation windows, and business rules.'}
                href="/ultra-admin/platform-rules"
                Icon={BookOpen}
                color="amber"
              />
            )}
            {activeSection === 'data-health' && (
              <SettingsShortcut
                tr={tr}
                title={tr ? 'Veri Sağlığı' : 'Data Health'}
                desc={tr ? 'Orphan kayıtlar, tutarsızlık uyarıları ve drift tespiti.' : 'Orphan records, consistency alerts, and drift detection.'}
                href="/ultra-admin/data-health"
                Icon={Activity}
                color="rose"
              />
            )}
            {activeSection === 'media-health' && (
              <SettingsShortcut
                tr={tr}
                title={tr ? 'Medya Sağlığı' : 'Media Health'}
                desc={tr ? 'S3 resim bütünlüğü: yarı-orphan ve tam orphan tespiti + güvenli onarım.' : 'S3 image integrity: half-orphan / full-orphan detection and safe repair.'}
                href="/ultra-admin/media-health"
                Icon={ImageIcon}
                color="amber"
              />
            )}
            {activeSection === 'system-guide' && (
              <SettingsShortcut
                tr={tr}
                title={tr ? 'Sistem Rehberi' : 'System Guide'}
                desc={tr ? 'Platform kullanım dokümantasyonu ve özellik haritası.' : 'Platform usage documentation and feature map.'}
                href="/ultra-admin/system-guide"
                Icon={HelpCircle}
                color="sky"
              />
            )}
            {activeSection === 'super-admins' && (
              <SettingsShortcut
                tr={tr}
                title={tr ? 'Süper Adminler' : 'Super Admins'}
                desc={tr ? 'Birincil Süper Admin tarafından kullanıcı yetki yönetimi.' : 'User privilege management by the Primary Super Admin.'}
                href="/ultra-admin/super-admins"
                Icon={Shield}
                color="violet"
              />
            )}
            {/* Phase 82 — Dashboard sekmelerinden taşınanlar */}
            {activeSection === 'business-hours' && (
              <SettingsShortcut
                tr={tr}
                title={tr ? 'Çalışma Saatleri' : 'Business Hours'}
                desc={tr ? 'Destek mesai saatleri ve iş günü yapılandırması — iletişim pencerelerini belirler.' : 'Support working hours and business-day configuration — controls contact windows.'}
                href="/ultra-admin/dashboard/settings"
                Icon={Clock}
                color="cyan"
              />
            )}
            {activeSection === 'admin-activity' && (
              <SettingsShortcut
                tr={tr}
                title={tr ? 'Yönetici Aktiviteleri' : 'Admin Activity'}
                desc={tr ? 'Son 50 yönetici aksiyonu ve çakışma bildirimleri — operasyonel canlı akış.' : 'Last 50 admin actions with conflict pills — operational live feed.'}
                href="/ultra-admin/dashboard/activity"
                Icon={Activity}
                color="amber"
              />
            )}

            {/* Tehlikeli İşlemler */}
            {activeSection === 'order-maintenance' && (
              <OrderMaintenanceSection tr={tr} />
            )}

            {activeSection === 'danger-zone' && (
              <DangerZoneSection tr={tr} />
            )}

          </main>
        </div>
      </div>

      {/* ── URL Scraper Modal ──────────────────────────────────────────────── */}
      {showScrapeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-violet-100 flex items-center justify-center">
                  <Globe className="h-5 w-5 text-violet-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{tr ? 'Web Sitesinden İçe Aktar' : 'Import from Website'}</h3>
                  <p className="text-xs text-gray-400">{tr ? 'URL girin, görselleri seçin, gruba ekleyin' : 'Enter URL, pick images, add to group'}</p>
                </div>
              </div>
              <button onClick={() => setShowScrapeModal(false)} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
            </div>

            {/* URL Input */}
            <div className="px-6 py-4 border-b border-gray-50 flex-shrink-0">
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="url"
                    value={scrapeUrl}
                    onChange={e => setScrapeUrl(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleScrapeUrl()}
                    placeholder="https://www.kastamonuentegre.com/tr_tr/urunler/dekoratif-panel"
                    className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                  />
                </div>
                <Button
                  onClick={handleScrapeUrl}
                  disabled={scrapeLoading || !scrapeUrl.trim()}
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl px-5 flex-shrink-0"
                >
                  {scrapeLoading
                    ? <span className="flex items-center gap-2"><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />{tr ? 'Tarıyor...' : 'Scanning...'}</span>
                    : <span className="flex items-center gap-2"><Search className="h-4 w-4" />{tr ? 'Tara' : 'Scan'}</span>
                  }
                </Button>
              </div>
              {scrapeError && !scrapeSuccess && (
                <p className="mt-2 text-sm text-red-600 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 flex-shrink-0" />{scrapeError}</p>
              )}
            </div>

            {/* Results Grid */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {!scrapeResults && !scrapeLoading && (
                <div className="flex flex-col items-center justify-center h-48 text-gray-300 gap-3">
                  <Globe className="h-14 w-14" />
                  <p className="text-sm text-center">{tr ? 'Bir ürün katalog sayfasının URL\'sini girin ve Tara\'ya basın' : 'Enter a product catalog URL and press Scan'}</p>
                  <p className="text-xs text-gray-400">{tr ? 'Örn: kastamonuentegre.com, vitingo.com.tr, vb.' : 'e.g. kastamonuentegre.com, vitingo.com.tr, etc.'}</p>
                </div>
              )}
              {scrapeLoading && (
                <div className="flex flex-col items-center justify-center h-48 gap-4">
                  <div className="animate-spin h-10 w-10 border-3 border-violet-500 border-t-transparent rounded-full" />
                  <p className="text-sm text-gray-500">{tr ? 'Sayfa taranıyor, görseller toplanıyor...' : 'Scanning page, collecting images...'}</p>
                </div>
              )}
              {scrapeResults && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold text-violet-700">{scrapeResults.count}</span> {tr ? 'görsel bulundu' : 'images found'} ·{' '}
                      <span className="font-semibold text-gray-900">{scrapeSelected.size}</span> {tr ? 'seçili' : 'selected'}
                    </p>
                    <div className="flex gap-2">
                      <button onClick={() => setScrapeSelected(new Set(scrapeResults.images.map(i => i.url)))} className="text-xs text-violet-600 hover:underline">{tr ? 'Tümünü Seç' : 'Select All'}</button>
                      <span className="text-gray-300">|</span>
                      <button onClick={() => setScrapeSelected(new Set())} className="text-xs text-gray-400 hover:underline">{tr ? 'Temizle' : 'Clear'}</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
                    {scrapeResults.images.map((img, i) => {
                      const sel = scrapeSelected.has(img.url);
                      return (
                        <div
                          key={i}
                          onClick={() => {
                            const s = new Set(scrapeSelected);
                            sel ? s.delete(img.url) : s.add(img.url);
                            setScrapeSelected(s);
                          }}
                          className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all group ${sel ? 'border-violet-500 shadow-md shadow-violet-100' : 'border-gray-100 hover:border-violet-200'}`}
                        >
                          <div className="aspect-square bg-gray-50">
                            <img
                              src={img.url}
                              alt={img.name}
                              className="w-full h-full object-cover"
                              onError={e => { e.target.style.display='none'; e.target.parentNode.classList.add('bg-gray-100'); }}
                            />
                          </div>
                          {sel && (
                            <div className="absolute top-1.5 right-1.5 h-5 w-5 bg-violet-600 rounded-full flex items-center justify-center">
                              <Check className="h-3 w-3 text-white" />
                            </div>
                          )}
                          <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-1.5 py-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                            {img.name}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Footer — Group name + Import */}
            {scrapeResults && scrapeSelected.size > 0 && (
              <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50">
                {scrapeSuccess ? (
                  <div className="flex items-center gap-2 text-purple-700 font-semibold">
                    <CheckCircle className="h-5 w-5" />{scrapeSuccess}
                  </div>
                ) : (
                  <div className="flex gap-3 items-center">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={scrapeGroupName}
                        onChange={e => { setScrapeGroupName(e.target.value); setScrapeError(''); }}
                        placeholder={tr ? 'Grup adı (örn: Rip Halı, Laminat Parke...)' : 'Group name (e.g. Rip Carpet, Laminate...)'}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                      />
                      {scrapeError && <p className="mt-1 text-xs text-red-500">{scrapeError}</p>}
                    </div>
                    <Button
                      onClick={handleScrapeImport}
                      disabled={scrapeImporting || !scrapeGroupName.trim()}
                      className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl px-5 flex-shrink-0"
                    >
                      {scrapeImporting
                        ? <span className="flex items-center gap-2"><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />{tr ? 'İçe Aktarılıyor...' : 'Importing...'}</span>
                        : <span className="flex items-center gap-2"><UploadCloud className="h-4 w-4" />{scrapeSelected.size} {tr ? 'Görseli İçe Aktar' : 'Import Images'}</span>
                      }
                    </Button>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      )}

      {/* Email Template Modal */}
      {showTmplModal && (
        <TemplateModal
          template={isNewTmpl ? null : editTmpl}
          onClose={() => { setShowTmplModal(false); setEditTmpl(null); }}
          onSave={handleSaveTemplate}
          tr={tr}
        />
      )}
    </div>
  );
};

/**
 * SettingsShortcut — tiny presentational card used by the "moved-from-sidebar"
 * settings sections (Delegate Map, Platform Rules, Data Health, System Guide,
 * Super Admins). Each of these has its own dedicated route/page — we deep-link
 * there rather than re-mounting the page in an iframe so permissions,
 * breadcrumbs and state behave naturally.
 */
function SettingsShortcut({ tr, title, desc, href, Icon, color = 'purple' }) {
  const palette = {
    cyan:   { bg: 'bg-cyan-50',   iconBg: 'bg-cyan-100',   iconFg: 'text-cyan-600',   btn: 'bg-cyan-600 hover:bg-cyan-700' },
    amber:  { bg: 'bg-amber-50',  iconBg: 'bg-amber-100',  iconFg: 'text-amber-600',  btn: 'bg-amber-600 hover:bg-amber-700' },
    rose:   { bg: 'bg-rose-50',   iconBg: 'bg-rose-100',   iconFg: 'text-rose-600',   btn: 'bg-rose-600 hover:bg-rose-700' },
    sky:    { bg: 'bg-sky-50',    iconBg: 'bg-sky-100',    iconFg: 'text-sky-600',    btn: 'bg-sky-600 hover:bg-sky-700' },
    violet: { bg: 'bg-violet-50', iconBg: 'bg-violet-100', iconFg: 'text-violet-600', btn: 'bg-violet-600 hover:bg-violet-700' },
    purple: { bg: 'bg-purple-50', iconBg: 'bg-purple-100', iconFg: 'text-purple-600', btn: 'bg-purple-600 hover:bg-purple-700' },
  }[color] || {};
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        <p className="text-sm text-gray-400 mt-0.5">{desc}</p>
      </div>
      <div className={`${palette.bg} rounded-2xl p-8 flex flex-col items-center text-center gap-4`}>
        <div className={`h-14 w-14 rounded-xl ${palette.iconBg} ${palette.iconFg} flex items-center justify-center`}>
          <Icon className="h-7 w-7" />
        </div>
        <p className="text-sm text-gray-600 max-w-md">{desc}</p>
        <a
          href={href}
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl ${palette.btn} text-white text-sm font-semibold shadow-sm transition-colors`}
        >
          {tr ? 'Sayfayı Aç' : 'Open Page'}
          <ChevronRight className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
}

/* ─── Phase 86I — CloudFront CDN Configuration ─────────────────────────────
 * Manages the DB-backed CloudFront signed-URL config without redeploying.
 * Private key is stored encrypted (Fernet derived from MEDIA_HMAC_SECRET).
 * Primary super admin only — gated in menuItems.
 * ──────────────────────────────────────────────────────────────────────── */
function CdnConfigSection({ tr }) {
  const [cfg, setCfg]                         = useState(null);
  const [domain, setDomain]                   = useState('');
  const [keyPairId, setKeyPairId]             = useState('');
  const [privateKey, setPrivateKey]           = useState('');
  const [urlTtl, setUrlTtl]                   = useState(86400);
  const [enabled, setEnabled]                 = useState(true);
  const [showPriv, setShowPriv]               = useState(false);
  const [saving, setSaving]                   = useState(false);
  const [testing, setTesting]                 = useState(false);
  const [testUrl, setTestUrl]                 = useState('');

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get('/ultra-admin/cdn-config');
      setCfg(r.data);
      setDomain(r.data.domain || '');
      setKeyPairId(r.data.key_pair_id || '');
      setUrlTtl(r.data.url_ttl || 86400);
      setEnabled(!!r.data.enabled);
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'CDN ayarı okunamadı.' : 'Failed to load CDN config.'));
    }
  }, [tr]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!domain.trim() || !keyPairId.trim()) {
      toast.error(tr ? 'Domain ve Key Pair ID zorunlu.' : 'Domain and Key Pair ID are required.');
      return;
    }
    if (!cfg?.private_key_set && !privateKey.trim()) {
      toast.error(tr ? 'İlk kayıtta private key zorunlu.' : 'Private key is required on first save.');
      return;
    }
    setSaving(true);
    try {
      const payload = { domain: domain.trim(), key_pair_id: keyPairId.trim(), url_ttl: Number(urlTtl) || 86400, enabled };
      if (privateKey.trim()) payload.private_key = privateKey.trim();
      await apiClient.put('/ultra-admin/cdn-config', payload);
      toast.success(tr ? 'CDN ayarı kaydedildi.' : 'CDN config saved.');
      setPrivateKey(''); // wipe input after save
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Kaydetme başarısız.' : 'Save failed.'));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestUrl('');
    try {
      const r = await apiClient.post('/ultra-admin/cdn-config/test');
      if (r.data?.success && r.data?.test_url) {
        setTestUrl(r.data.test_url);
        toast.success(tr ? 'Test URL üretildi — aşağıdan açın.' : 'Test URL generated — open below.');
      } else {
        toast.error(r.data?.error || (tr ? 'Test başarısız.' : 'Test failed.'));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Test başarısız.' : 'Test failed.'));
    } finally {
      setTesting(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm(tr ? 'CDN devre dışı bırakılsın mı? Görseller S3 doğrudan servis edilir.' : 'Disable CDN? Images will be served directly from S3.')) return;
    try {
      await apiClient.delete('/ultra-admin/cdn-config');
      toast.success(tr ? 'CDN devre dışı.' : 'CDN disabled.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Devre dışı bırakma başarısız.' : 'Disable failed.'));
    }
  };

  if (!cfg) {
    return <div className="text-sm text-gray-500">{tr ? 'Yükleniyor…' : 'Loading…'}</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="cdn-config-section">
      <Card className="border border-gray-200 rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Globe className="h-5 w-5 text-indigo-600" />
            {tr ? 'CDN (CloudFront) Ayarları' : 'CDN (CloudFront) Settings'}
          </CardTitle>
          <p className="text-xs text-gray-500 mt-1">
            {tr
              ? 'CloudFront imzalı URL’leri için domain, public key ID ve RSA private key. Private key veritabanında şifreli saklanır (Fernet, MEDIA_HMAC_SECRET türetilmiş anahtar). Boş bırakılırsa mevcut anahtar korunur (rotation için kolaylık).'
              : 'CloudFront signed-URL credentials. Private key is stored encrypted at rest (Fernet, derived from MEDIA_HMAC_SECRET). Leave private key empty to keep the existing one (rotation-safe).'}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status banner */}
          <div className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2 border ${cfg.enabled && cfg.private_key_set ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="cdn-status-banner">
            <div className="flex items-center gap-2">
              {cfg.enabled && cfg.private_key_set
                ? <><CheckCircle className="h-4 w-4 text-emerald-600" /> <span className="text-sm font-semibold text-emerald-800">{tr ? 'CDN aktif' : 'CDN active'}</span></>
                : <><AlertTriangle className="h-4 w-4 text-amber-600" /> <span className="text-sm font-semibold text-amber-800">{tr ? 'CDN devre dışı (S3 direkt servis)' : 'CDN disabled (serving via S3 direct)'}</span></>
              }
            </div>
            {cfg.private_key_set && <span className="text-[11px] text-gray-500 font-mono">key: {cfg.private_key_fingerprint}</span>}
          </div>

          {/* Domain */}
          <div>
            <Label className="text-xs font-bold text-gray-700 uppercase tracking-wider">CloudFront Domain</Label>
            <Input
              value={domain}
              onChange={e => setDomain(e.target.value)}
              placeholder="d325uqzebrrqve.cloudfront.net"
              data-testid="cdn-domain-input"
              className="mt-1"
            />
          </div>

          {/* Key Pair ID */}
          <div>
            <Label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Key Pair ID</Label>
            <Input
              value={keyPairId}
              onChange={e => setKeyPairId(e.target.value)}
              placeholder="K2Q968X0UBFTWT"
              data-testid="cdn-keypair-input"
              className="mt-1 font-mono"
            />
          </div>

          {/* Private Key (multi-line, hidden by default) */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                {tr ? 'Private Key (PEM)' : 'Private Key (PEM)'}
              </Label>
              <button
                type="button"
                onClick={() => setShowPriv(s => !s)}
                className="text-[11px] text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                data-testid="cdn-toggle-priv-visibility"
              >
                {showPriv
                  ? <><EyeOff className="h-3 w-3" /> {tr ? 'Gizle' : 'Hide'}</>
                  : <><Eye className="h-3 w-3" /> {tr ? 'Göster' : 'Show'}</>
                }
              </button>
            </div>
            <textarea
              value={privateKey}
              onChange={e => setPrivateKey(e.target.value)}
              placeholder={cfg.private_key_set
                ? (tr ? '••• kayıtlı ••• (yenisi yapıştırılırsa değiştirilir, boş bırakılırsa mevcut korunur)' : '••• stored ••• (paste a new one to rotate, leave empty to keep current)')
                : '-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----'}
              data-testid="cdn-privkey-input"
              className={`w-full mt-1 px-3 py-2 border border-gray-300 rounded-xl text-xs font-mono leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-200 ${showPriv ? '' : 'tracking-[2px]'}`}
              rows={8}
              style={showPriv ? {} : { WebkitTextSecurity: 'disc' }}
            />
            <p className="text-[11px] text-gray-400 mt-1">
              {tr
                ? 'Multi-line PEM formatı kabul edilir. Backend Fernet ile şifreleyip DB\'ye yazar; salt-text görünmez.'
                : 'Multi-line PEM accepted. Backend Fernet-encrypts before storage; never persisted in plaintext.'}
            </p>
          </div>

          {/* TTL */}
          <div>
            <Label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
              {tr ? 'Signed URL TTL (saniye)' : 'Signed URL TTL (seconds)'}
            </Label>
            <Input
              type="number"
              min={60}
              max={86400}
              value={urlTtl}
              onChange={e => setUrlTtl(e.target.value)}
              data-testid="cdn-ttl-input"
              className="mt-1 max-w-[200px]"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              {tr ? '60–86400 (24 saat). Varsayılan: 86400.' : '60–86400 (24h). Default: 86400.'}
            </p>
          </div>

          {/* Enabled toggle */}
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={enabled}
              onChange={e => setEnabled(e.target.checked)}
              data-testid="cdn-enabled-toggle"
              className="h-4 w-4 accent-indigo-600"
            />
            <span className="text-sm font-semibold text-gray-700">
              {tr ? 'CDN Aktif (kapatınca S3 doğrudan servis eder, anahtar saklanır)' : 'CDN Enabled (when off, images serve directly from S3; key stays in DB)'}
            </span>
          </label>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="cdn-save-btn"
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-5"
            >
              <Save className="h-4 w-4 mr-1.5" />
              {saving ? (tr ? 'Kaydediliyor…' : 'Saving…') : (tr ? 'Kaydet' : 'Save')}
            </Button>
            <Button
              onClick={handleTest}
              disabled={testing || !cfg.private_key_set}
              variant="outline"
              data-testid="cdn-test-btn"
              className="rounded-full"
            >
              {testing ? (tr ? 'Test ediliyor…' : 'Testing…') : (tr ? 'Test URL Üret' : 'Generate Test URL')}
            </Button>
            {cfg.enabled && (
              <Button
                onClick={handleDisable}
                variant="outline"
                data-testid="cdn-disable-btn"
                className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
              >
                {tr ? 'CDN\'i Devre Dışı Bırak' : 'Disable CDN'}
              </Button>
            )}
          </div>

          {/* Test URL display */}
          {testUrl && (
            <div className="mt-3 p-3 rounded-xl bg-gray-50 border border-gray-200" data-testid="cdn-test-url-box">
              <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">{tr ? 'Test URL (5 dk geçerli)' : 'Test URL (valid 5 min)'}</p>
              <a href={testUrl} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:text-indigo-800 break-all underline">
                {testUrl}
              </a>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


export default UltraAdminSettings;
